import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { formatPhoneInput } from '@smup/format';
import AssistantChat from './AssistantChat';
import revLogo from './assets/revmeuppro_logo_dkbkg.png';
import smupLogo from './assets/smup_logo_dkbkg.png';
import {
  ArrowLeft,
  Calendar,
  CalendarPlus,
  Check,
  FileUp,
  Loader2,
  MapPin,
  Plus,
  Repeat,
} from 'lucide-react';
/*
 * ⚠️ THE SAME ARITHMETIC THE SERVER RUNS, imported rather than reimplemented. This page
 * shows a running total as the customer ticks boxes and the server recomputes it on submit;
 * if the two ever disagreed she would read one figure here and a different one in her email,
 * which is indistinguishable from being overcharged. See packages/availability/src/pricing.ts.
 */
import {
  estimate,
  estimateVisit,
  type VisitEstimate,
  formatCents,
  formatEstimate,
  composeYesExplain,
  parseYesExplain,
  isYesExplainComplete,
  type AddonUnit,
  type Estimate,
} from '@smup/availability';

/*
 * The customer's booking page — the four-step flow specced in issue #48.
 *
 * ⚠️ It wears the TENANT'S colours, not ours. This page belongs to the business the
 * customer is booking; nothing on it should look like StartMeUpPro. Colours arrive from
 * /api/public/site and are applied as inline styles rather than Tailwind classes, because
 * they are per-tenant data and a utility class cannot hold a value from the database.
 *
 * ⚠️ NO TIMEZONE SELECTOR. A landscaper serves one county, and asking a customer to confirm
 * her timezone before booking a lawn cut is a form built by someone who does not understand
 * the business (#48). Every time on this page is the operator's local time, and the server
 * sends the label already formatted so the two cannot disagree.
 *
 * ⚠️ THE MOBILE NUMBER NOW CARRIES AN SMS OPT-IN CHECKBOX (2026-09-17). This used to forbid
 * any: #48 ruled out app-sent texts. That was reversed, and texts carry the booking
 * confirmation, the invoice and the review request (Mike, 2026-09-12, #271). It began the same
 * day as a notice and became a box the customer ticks — see `SmsConsent` below for why.
 */

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

/**
 * OUR OWN booking page wears the brand the visitor arrived from (Mike, 2026-09-19).
 *
 * ⚠️ INTERNAL TENANT ONLY. `book.startmeuppro.com` is where BOTH marketing sites send someone
 * who wants a 15-minute call, and arriving under a logo you have never seen is the kind of
 * small wrongness that makes a stranger close the tab. `?brand=rev` comes from revmeuppro.com's
 * CTA, `?brand=smup` (or nothing) from startmeuppro.com's. A customer's own booking page never
 * reaches this code — every use is behind `site.business.isInternal`.
 *
 * ⚠️ THE PALETTE IS NOT PER-BRAND, and that is not an oversight: both brands share the band
 * green and the orange, and the tenant's branding row already carries them. Only the wordmark
 * and where "Back" goes change.
 */
const OUR_BRANDS = {
  smup: { logo: smupLogo, name: 'StartMeUpPro', home: 'https://startmeuppro.com' },
  rev: { logo: revLogo, name: 'RevMeUpPro', home: 'https://revmeuppro.com' },
} as const;

function ourBrand(): (typeof OUR_BRANDS)[keyof typeof OUR_BRANDS] {
  const p = new URLSearchParams(window.location.search).get('brand')?.toLowerCase();
  return p === 'rev' ? OUR_BRANDS.rev : OUR_BRANDS.smup;
}

/**
 * Which consent wording the box sits beside, stored with every booking that ticks it (0065).
 *
 * ⚠️ BUMP THIS WHENEVER THE SENTENCE IN `SmsConsent` CHANGES. The stored value is how anybody
 * later answers "what exactly did she agree to" — a version that no longer names the words she
 * saw makes every record written under it worthless as evidence.
 */
const SMS_CONSENT_VERSION = 'booking-2026-09-17';
/**
 * Our own tenant's wording (2026-09-18). A 15-minute intro call has no invoice and no review
 * request, and our campaign describes confirmations and changes to the time, so the tenant
 * sentence was a mismatch a reviewer could flag. Same rule: bump it when that sentence changes.
 */
const SMS_CONSENT_VERSION_INTERNAL = 'internal-call-2026-09-18';
/**
 * A business with the day-before reminder on (0096, Mike 2026-10-28): the box names it. The API
 * sends a reminder only to a booking ticked under this version (REMINDER_CONSENT_VERSIONS in
 * services/api/src/lib/appointment-reminders.ts). Same rule: bump it when that sentence changes.
 */
const SMS_CONSENT_VERSION_REMINDERS = 'booking-2026-10-28';

/** Local dev has no tenant host, so the slug can come from the query string. */
const TENANT_PARAM = new URLSearchParams(window.location.search).get('tenant');

/**
 * `?service=payment-setup-support` — arrive with one service already chosen.
 *
 * ⚠️ IT PRESELECTS, IT DOES NOT SKIP THE STEP, the same rule the landing-page "Book this"
 * card follows a few hundred lines down and for the same reason: she should see what was
 * picked for her, on the screen where it can be changed, rather than find herself on "when"
 * for something she never chose.
 *
 * ⚠️ MATCHED ON THE NAME, NOT THE ID, because the link that uses this is HARDCODED IN ANOTHER
 * APP (the CRM's Payments screen) and service ids differ between staging and production — a
 * uuid in that link would work in exactly one environment and 404 silently in the other. The
 * cost is that renaming a service breaks the link, which is why
 * apps/marketing/scripts/check-booking-url.sh asserts the CRM's slug still matches a service
 * the internal seeder defines.
 *
 * ⚠️ AN UNKNOWN SLUG IS IGNORED, NEVER AN ERROR. A stale or mistyped link should land somebody
 * on the ordinary picker, not on a broken page — the worst outcome here is one extra tap.
 *
 * ⚠️ AND IT IS INERT ON EVERY TENANT BUT OURS. The gate is in the effect that reads this, not
 * here — see `site.business.isInternal` in Flow. A customer's booking page ignores the
 * parameter entirely.
 */
const SERVICE_PARAM = new URLSearchParams(window.location.search).get('service');
/**
 * Progressive reveal: one question at a time, each section opening as the last is answered.
 * THE DEFAULT SINCE 2026-09-10. `?reveal=0` still renders the previous long-scroll layout.
 *
 * ⚠️ THE ESCAPE HATCH IS TEMPORARY AND SHOULD BE DELETED, not kept as a permanent choice.
 * Two layouts of the revenue path in one file is two things to keep working, and the branches
 * below are only tolerable while there is a reason to reach the old one — a customer reporting
 * something odd, and a way to see the previous behaviour without a deploy. Once a fortnight of
 * `booking_funnel_daily` says the new one is not losing bookings, delete this const and every
 * `REVEAL_MODE ?` beneath it; the classic path is then dead code pretending to be a fallback.
 *
 * ⚠️ IT IS A URL PARAM, NOT A TENANT SETTING, AND THAT IS DELIBERATE. A per-tenant flag would
 * make this a product decision each operator has to have an opinion about, which none of them
 * asked for and none of them can evaluate. The page either works better or it does not.
 */
const REVEAL_MODE = new URLSearchParams(window.location.search).get('reveal') !== '0';

/**
 * `?hold=<token>` — the link the booking assistant texts when a customer picks a time (#251).
 *
 * ⚠️ READ ONCE AND TAKEN OUT OF THE ADDRESS BAR, the same treatment the CRM gives an invite
 * token. It names a slot on her calendar for thirty minutes; left in the URL it ends up in
 * browser history, a screenshot sent to a partner, and the Referer of anything the page links
 * to. It lives in memory from here, and every other parameter is left exactly as it was.
 *
 * ⚠️ MODULE SCOPE, NOT A STATE INITIALISER, because it rewrites the URL — StrictMode runs an
 * initialiser twice in development, and the second run would find the token already gone.
 * Declared after every other parameter is read, for the same reason.
 *
 * ⚠️ NOT GATED TO OUR OWN TENANT, UNLIKE `?service=`. Every tenant with texting sends these
 * links, and a token that does not name a live hold on THIS tenant is simply ignored.
 */
function takeHoldToken(): string | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const token = url.searchParams.get('hold');
  if (!token) return null;
  url.searchParams.delete('hold');
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return token.slice(0, 200);
}
const HOLD_TOKEN = takeHoldToken();

/** What `POST /api/public/hold` answers for a live hold. */
interface HeldSlot {
  startsAt: string;
  expiresAt: string;
  /** 'YYYY-MM-DD' in the business's timezone. */
  date: string;
}

/**
 * When the customer last moved the page herself.
 *
 * ⚠️ IT HAS TO BE LISTENING BEFORE THE TAP, WHICH IS WHY IT IS NOT INSIDE A COMPONENT. A flick
 * that begins on the same frame as the tap happens before React has rendered the section it is
 * about to reveal — so a listener owned by that section is registered a frame too late and
 * misses exactly the person who most objects to being moved. Measured: with per-section
 * listeners the same-frame case scrolled anyway; with this it does not.
 */
let lastUserScrollAt = 0;
if (REVEAL_MODE && typeof window !== 'undefined') {
  const mark = () => {
    lastUserScrollAt = Date.now();
  };
  // Deliberately NOT 'scroll': the smooth scroll below fires that, and would cancel itself.
  window.addEventListener('wheel', mark, { passive: true });
  window.addEventListener('touchmove', mark, { passive: true });
  window.addEventListener('keydown', mark, { passive: true });
}

/**
 * Whether the visitor has tapped, clicked or pressed a key since the page loaded.
 *
 * ⚠️ A REVEAL ONLY SCROLLS IN ANSWER TO SOMETHING SHE DID. On load the booking form's first
 * section mounts open — a Fold always opens a frame after mounting — and its bring-into-view
 * fired 700ms later with nobody having touched anything. Embedded under a landing page that
 * dragged every visitor from the hero straight down to the form (Mike, 2026-10-26, on
 * showshearsgrooming.com). Pointer and key, not scroll: reading the page is not asking to be
 * moved, and a "Book this" tap or the pet lookup she typed into both count.
 */
let userHasActed = false;
if (REVEAL_MODE && typeof window !== 'undefined') {
  const acted = () => {
    userHasActed = true;
  };
  window.addEventListener('pointerdown', acted, { passive: true, capture: true });
  window.addEventListener('keydown', acted, { passive: true, capture: true });
}

/** What `/api/public/rebook/pet` answers with. Snapshots, deliberately — see the route. */
interface LastPackage {
  serviceId: string;
  serviceName: string;
  tierLabel: string | null;
  addonNames: string[];
}
interface PetLookup {
  tierLabel?: string | null;
  lastPackage?: LastPackage | null;
}

/**
 * ⚠️ `true` MEANS FIRST VISIT, which is the opposite of what "Yes" reads as here. The labels
 * and the flag live together so the two places that render them cannot disagree about which
 * button means what — an inversion that would silently skip a first-timer's questions and her
 * deposit, and would look completely correct on screen.
 */
const FIRST_VISIT_CHOICES: readonly (readonly [string, boolean])[] = [
  ['No, first time', true],
  ['Yes, I’m a regular', false],
] as const;

/** Lowercase, non-alphanumerics collapsed to single dashes. `’` included — names carry them. */
export const serviceSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
export const withTenant = (path: string) => {
  if (!TENANT_PARAM) return `${API_URL}${path}`;
  return `${API_URL}${path}${path.includes('?') ? '&' : '?'}tenant=${encodeURIComponent(TENANT_PARAM)}`;
};

/*
 * How far a visitor got (2026-10-12): the page reports a handful of fixed points to
 * POST /api/public/funnel, which only counts them per day. No id, no cookie, nothing about who.
 * Once per page load each, so paging the calendar or going back a step can't inflate a step,
 * and fire-and-forget, so a slow or failed count can never hold up a booking.
 */
const reportedFunnel = new Set<string>();
export function reportFunnel(event: string): void {
  if (reportedFunnel.has(event)) return;
  reportedFunnel.add(event);
  try {
    void fetch(withTenant('/api/public/funnel'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* counting is never worth an error on her page */
  }
}

export interface Tier {
  id: string;
  /** As the operator wrote it — "Medium (21–50 lbs)". Never parsed. */
  label: string;
  priceCents: number;
  priceMaxCents: number | null;
  durationMinutes: number | null;
}

export interface Addon {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  priceMaxCents: number | null;
  /** 'minute' add-ons are quoted as a rate and never enter the total. */
  priceUnit: AddonUnit;
  durationMinutes: number;
}

interface Service {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  pricingMode: 'fixed' | 'quote';
  priceCents: number | null;
  priceMaxCents: number | null;
  /** Every figure on this card is a floor, not a promise. Decided by the operator. */
  priceIsEstimate: boolean;
  /** Her sentence about why the figure can move. Shown wherever the figure is. */
  priceNote: string | null;
  tiered: boolean;
  tiers: Tier[];
  addons: Addon[];
  recurringCapable: boolean;
  /** Questions about THIS SERVICE. The dog's profile lives on `settings.intakeQuestions`. */
  intakeQuestions: IntakeQuestion[];
}

export type IntakeQuestionType =
  | 'short'
  | 'long'
  | 'choice'
  | 'yesno'
  /** Yes / no, with an explanation required only on yes. */
  | 'yesno_explain'
  | 'number';

export interface IntakeQuestion {
  id: string;
  label: string;
  type: IntakeQuestionType;
  required: boolean;
  /** `choice` only, in the operator's order. */
  options?: string[];
  /** `choice` only: several answers, joined with ", " in the operator's order. */
  multi?: boolean;
  help?: string | null;
  /** Hidden once she says she has booked before. Self-declared, never checked. */
  firstVisitOnly: boolean;
  /**
   * Marks the question whose answer names a pet (0056), so a returning customer typing
   * "Bella" gets Bella's usual size back. Absent for every tenant until she ticks it, and
   * absent means the flow behaves exactly as it did.
   */
  role?: 'pet-name';
}

export interface LandingPhoto {
  id: string;
  caption: string | null;
  width: number;
  height: number;
  /** API-relative and content-versioned; prefix with API_URL to use. */
  url: string;
  /**
   * The point that must survive an `object-fit: cover` crop, in percent (0037).
   * Optional so an older payload renders centred, exactly as it did before.
   */
  focalX?: number;
  focalY?: number;
  /**
   * What the operator cut the photograph down to, in pixels of the original (#292), or
   * absent/null for the whole photo — which is every photo taken before the crop dialog
   * existed, so this stays optional and the renderer must treat missing as uncropped.
   *
   * ⚠️ NOT A REPLACEMENT FOR THE FOCAL POINT ABOVE. The crop is one rectangle; the frames it
   * lands in are three different shapes. Photo() applies the crop first and then the point.
   */
  crop?: { x: number; y: number; w: number; h: number } | null;
}

export interface LandingReview {
  id: string;
  quote: string;
  author: string | null;
  /** Free text the operator wrote — "Google", "Regular since 2019". Not a verified source. */
  source: string | null;
  /** 1–5, or null when the operator asserted no score. Never defaulted. */
  rating: number | null;
}

export type SocialPlatform =
  | 'facebook' | 'instagram' | 'tiktok' | 'youtube' | 'x'
  | 'linkedin' | 'google' | 'yelp' | 'nextdoor';

export interface SocialLink {
  platform: SocialPlatform;
  url: string;
}

export interface Landing {
  template: 'hero' | 'split' | 'gallery';
  /**
   * The landing page's own background (0038). `null` means inherit the booking palette,
   * which is what every page did before it existed — so null must render identically.
   */
  backgroundColor?: string | null;
  /** Readable text for `backgroundColor`, derived server-side. Null when that is null. */
  textColor?: string | null;
  /** The landing page's own accent (0039). Null means inherit the booking one. */
  accentColor?: string | null;
  headline: string | null;
  tagline: string | null;
  about: string | null;
  photos: LandingPhoto[];
  reviews: LandingReview[];
  social: SocialLink[];
  /**
   * Her "write a review" link — Google, Facebook, Yelp — the same one the emailed review page
   * offers (0050). Null when she has not set one, and the page then draws no link at all.
   */
  reviewUrl?: string | null;
}

export interface Site {
  business: {
    name: string;
    slug: string;
    /** A seeded sales demo, not a real business (#143). Drives the banner AND noindex. */
    isDemo?: boolean;
    /**
     * StartMeUpPro's own tenant (0049). Drives noindex ONLY — deliberately not the demo
     * banner: we are a real business, and telling a prospect otherwise thirty seconds before
     * asking for their phone number would be a strange way to sell them software.
     */
    isInternal?: boolean;
    /** Her text box promises a reminder the day before (0096). */
    smsReminders?: boolean;
    /** Her booking line in E.164, or null until one is bought (#251). Published on /terms. */
    bookingLine?: string | null;
  };
  branding: {
    backgroundColor: string;
    accentColor: string;
    textColor: string;
    hasLogo: boolean;
    logoVersion: string | null;
    logoUrl: string | null;
    /** The tab icon, when the tenant has uploaded one. Falls back to the logo. */
    hasFavicon: boolean;
    faviconVersion: string | null;
    faviconUrl: string | null;
  };
  settings: {
    timezone: string;
    intro: string | null;
    acceptingBookings: boolean;
    /** "Travel is included in all prices." Page-level, shown once under the service list. */
    pricingNote: string | null;
    /** Her cancellation policy (0083). Non-null means a box she must tick before booking. */
    cancellationPolicy?: string | null;
    /** Which repeat chips to draw, in order, and what each is worth off. */
    recurringOptions: { recurrence: Recurrence; percentOff: number }[];
    /** The dog's profile — asked on every booking, whatever the service. */
    intakeQuestions: IntakeQuestion[];
    intakeIntro: string | null;
    /**
     * What a customer who says it is her first visit is told she will be asked for.
     *
     * ⚠️ NEVER CHARGED HERE, and the copy has to keep saying so — nothing on this platform
     * takes a payment. The operator collects it herself. Null means she asks for none.
     */
    firstVisitDepositCents: number | null;
    /** Percent off the visit total when it holds more than one animal. Null = not offered. */
    multiPetDiscountPercent: number | null;
    /**
     * Whether she has configured any service areas (#135).
     *
     * ⚠️ A BOOLEAN, NOT THE AREAS THEMSELVES. Publishing every ZIP she covers and on which
     * days is not ours to broadcast — the page only needs to know whether to ask. One ZIP is
     * resolved at a time, through /api/public/area.
     */
    hasAreas: boolean;
    /**
     * Where customers bring the dog (0091). Non-empty makes this a drop-off page: a "Where?"
     * step before the calendar, and no street address asked. Absent from an older API reads
     * as none. ⚠️ Never carries an address — that arrives with the booking.
     */
    locations?: SiteLocation[];
    /** Percent added once per visit for an extended area. Null = she charges none (#135). */
    travelUpchargePercent: number | null;
    /** Her switch for the chat box (0059). Absent from an older API reads as off. */
    brainEnabled?: boolean;
  };
  services: Service[];
  /** Null when the tenant has no landing page or has turned it off — go straight to booking. */
  landing: Landing | null;
}

interface Slot {
  startsAt: string;
  endsAt: string;
  label: string;
}
interface DayAvailability {
  date: string;
  slots: Slot[];
  closedReason?: string;
}

/**
 * The confirmation, as the SERVER recorded it.
 *
 * ⚠️ The figures here come back from the insert rather than from local state. If the two
 * ever disagree — a tier re-priced between page load and submit — the customer must be shown
 * the one the operator will actually act on, not the one this tab happened to be holding.
 */
interface SiteLocation {
  id: string;
  name: string;
  description: string | null;
  /** `Date.getDay()` indexes — 0 is Sunday. */
  days: number[];
}

interface Done {
  status: string;
  /** Where to bring the dog, sent only once booked (0091). Null for a mobile booking. */
  location?: { name: string; address: string | null } | null;
  tierLabel?: string | null;
  addons?: { name: string; priceCents: number; priceMaxCents: number | null; priceUnit: AddonUnit }[];
  priceIsEstimate?: boolean;
  estimateMinCents?: number | null;
  estimateMaxCents?: number | null;
  recurringDiscountPercent?: number | null;
  /**
   * The deposit as the SERVER decided it, not as this tab predicted. If the operator changed
   * her figure between page load and submit, the customer is shown the one now attached to
   * her booking — which is the one the operator will ask for.
   */
  depositCents?: number | null;
  ics?: string;
  emailSent?: boolean;
  /**
   * The right to attach a vaccination record to the booking just made.
   *
   * ⚠️ THE TOKEN EXISTS ONLY IN THIS OBJECT, in this tab. It is never emailed and cannot be
   * reissued — closing the page ends the opportunity, which is why the panel says so out
   * loud rather than implying she can come back to it.
   */
  upload?: {
    token: string;
    expiresAt: string;
    wanted: boolean;
    retentionDays: number;
    /** What the operator calls the document (0040). Null when she never named one. */
    label?: string | null;
  };
}

type Recurrence = 'once' | 'weekly' | 'biweekly' | 'every_4_weeks' | 'every_6_weeks' | 'monthly';

const RECURRENCE_LABELS: Record<Recurrence, string> = {
  once: 'Once',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  every_4_weeks: 'Every 4 weeks',
  every_6_weeks: 'Every 6 weeks',
  monthly: 'Monthly',
};

const money = formatCents;

/**
 * What the CARD says before anything is selected.
 *
 * A tiered service has no price of its own, so the card shows the span of its tiers —
 * "From $100" when they are open-ended, "$100–$190" when they are not. Showing nothing, or
 * showing the cheapest tier as though it were the price, are both worse: the first makes the
 * card unbookable-looking and the second is the bait price the fine print exists to prevent.
 */
/**
 * A service with nothing to charge for.
 *
 * ⚠️ "$0" IS NOT A PRICE, IT IS THE ABSENCE OF ONE, and rendering it puts a money figure on a
 * card that is not about money. `booking_services` allows `price_cents = 0` (0023) and the
 * internal tenant uses it deliberately for our own calls — quote mode would promise a prospect
 * we will come back with a number. So zero is legitimate data; it is only the DISPLAY of it
 * that is noise, which is why this is a rendering rule and not a schema one.
 *
 * ⚠️ NOT THE SAME AS `priceCents == null`. Null means "not priced here" and still shows
 * "Quote", because that is a real answer to "what does this cost". Zero means it is free.
 */
function isFree(s: Service): boolean {
  if (s.pricingMode === 'quote' || s.tiered) return false;
  return s.priceCents === 0 && (s.priceMaxCents == null || s.priceMaxCents === 0);
}

function cardPriceLabel(s: Service): string {
  if (s.pricingMode === 'quote') return 'Quote';
  if (s.tiered) {
    if (s.tiers.length === 0) return 'Quote';
    const floor = Math.min(...s.tiers.map((t) => t.priceCents));
    const tops = s.tiers.map((t) => t.priceMaxCents ?? t.priceCents);
    const ceiling = Math.max(...tops);
    // Any open-ended tier makes the whole span open-ended — a "$190+" line cannot be
    // summarised by a range that stops at $190.
    const openEnded = s.priceIsEstimate && s.tiers.some((t) => t.priceMaxCents == null);
    if (openEnded || ceiling === floor) return `From ${money(floor)}`;
    return `${money(floor)}–${money(ceiling)}`;
  }
  if (s.priceCents == null) return 'Quote';
  return formatEstimate({
    minCents: s.priceCents,
    maxCents: s.priceMaxCents,
    isEstimate: s.priceIsEstimate,
  });
}

const durationLabel = (m: number) => {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r === 0 ? `${h} hr` : `${h} hr ${r} min`;
};
/** 12-hour, because that is how an American customer reads an appointment time. */
const prettyTime = (hhmm: string) => {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${hour}${period}` : `${hour}:${String(m).padStart(2, '0')}${period}`;
};
/**
 * An instant as a time on HER clock — "2:35pm". The hold's times are instants, and the page's
 * visitors are not always in her timezone.
 */
const clockIn = (iso: string, timeZone: string) =>
  new Date(iso)
    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone })
    .replace(':00', '')
    .replace(' ', '')
    .toLowerCase();
const prettyDate = (iso: string) => {
  const [y = 0, mo = 1, d = 1] = iso.split('-').map(Number);
  // Constructed as UTC and read back as UTC — this is a calendar date, not an instant, so
  // it must not be shifted by the viewer's own zone.
  return new Date(Date.UTC(y, mo - 1, d)).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};
/**
 * What months the visible grid covers, e.g. "September 2026" or "September – October 2026".
 *
 * ⚠️ THE GRID IS THREE WEEKS ANCHORED TO A MONDAY, so it routinely straddles a month boundary
 * and regularly a year one. Naming only the first day's month would label a page whose second
 * half belongs to the next — and "the 3rd" is a different day depending on which.
 *
 * UTC throughout, like `prettyDate`: these are calendar dates, not instants, and must not be
 * shifted into a neighbouring month by the viewer's own zone.
 */
const monthSpanLabel = (fromISO: string, toISO: string) => {
  const at = (iso: string) => {
    const [y = 0, mo = 1, d = 1] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, mo - 1, d));
  };
  const a = at(fromISO);
  const b = at(toISO);
  const name = (dt: Date, withYear: boolean) =>
    dt.toLocaleDateString(undefined, {
      month: 'long',
      ...(withYear ? { year: 'numeric' as const } : {}),
      timeZone: 'UTC',
    });
  if (a.getUTCFullYear() !== b.getUTCFullYear()) return `${name(a, true)} – ${name(b, true)}`;
  if (a.getUTCMonth() !== b.getUTCMonth()) return `${name(a, false)} – ${name(b, true)}`;
  return name(a, true);
};

/** The short zone name the operator's times are in, e.g. "EDT". Shown, never chosen. */
const zoneLabel = (tz: string) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    return '';
  }
};

/**
 * One fetch of the public site payload, shared by the landing page and /book so a customer
 * arriving at either does not pay for two round trips to describe the same business.
 */
export type SiteFailure = 'missing' | 'error';

/**
 * ⚠️ 404 AND A SERVER FAILURE ARE NOT THE SAME ANSWER, AND COLLAPSING THEM COST CUSTOMERS
 * SOMEBODY ELSE'S BOOKINGS. This returned a single `failed` boolean, and both callers
 * rendered "This page isn't available" for it — final, apologetic, and no way forward.
 *
 * That is the RIGHT answer for a 404: the tenant does not exist, or has not published the
 * page, and there is nothing to wait for. It is the WRONG answer for a 5xx or a dropped
 * connection, where the page exists and is coming back — the customer is told the business
 * has no booking page, and leaves. A deploy restarts the API for a few seconds
 * (`deploy-prod.sh` recreates the container), so this is a real window, not a hypothetical.
 *
 * Anything that is not a 404 is treated as transient, including a network error with no
 * status at all — the safe direction is offering a retry to somebody whose page really is
 * gone, not turning a blip into a lost booking.
 */
export function useSite(): { site: Site | null; failure: SiteFailure | null } {
  const [site, setSite] = useState<Site | null>(null);
  const [failure, setFailure] = useState<SiteFailure | null>(null);
  useEffect(() => {
    fetch(withTenant('/api/public/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(setSite)
      .catch((err: Error) => setFailure(err.message === '404' ? 'missing' : 'error'));
  }, []);

  useTabIdentity(site);

  return { site, failure };
}

/**
 * What a customer sees when the page cannot load.
 *
 * ⚠️ IT CANNOT NAME THE BUSINESS OR ITS PHONE NUMBER, and that is not an oversight to fix
 * later — the request that would have told us who this tenant is, is the one that just
 * failed. So the transient case offers the only two things available without tenant data: try
 * again, and go back to whoever sent you here.
 */
export function SiteUnavailable({ failure }: { failure: SiteFailure }) {
  if (failure === 'missing') {
    return (
      <Centered>
        <p className="text-slate-600">This booking page isn’t available.</p>
      </Centered>
    );
  }
  return (
    <Centered>
      <div className="max-w-sm text-center">
        <p className="text-slate-700">We couldn’t load this page just now.</p>
        <p className="mt-2 text-sm text-slate-500">
          It’s us, not you — this is usually over in a moment.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-5 inline-flex items-center rounded-lg bg-slate-800 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-slate-700"
        >
          Try again
        </button>
        <p className="mt-5 text-sm text-slate-500">
          In a hurry? Call the business directly — they can book you in themselves.
        </p>
      </div>
    </Centered>
  );
}

/**
 * The browser tab belongs to the tenant: their name, their logo.
 *
 * ⚠️ THIS IS THE SAME RULE AS THE COLOURS, and it was the last place breaking it. The page
 * wears the tenant's palette because it belongs to the business the customer is booking —
 * and then the tab said `Pawsome Mobile Grooming` for every tenant on the platform, because
 * index.html hardcoded it and nothing ever set `document.title`. Another customer's business
 * name, in the tab, the bookmark, the history entry and the label on any shared link, shown
 * to a tenant's own customers. Verified live on the staging demo tenant, 2026-08-12.
 *
 * ⚠️ IT LIVES IN `useSite`, NOT IN A COMPONENT, deliberately. There are two entry points
 * already — `/` and `/book` — and `/` falls through to the booking page when a tenant has no
 * landing page, so the paths are not as separate as they look. Anything that fetches the
 * site gets the right tab for free, and a third route cannot forget to ask.
 *
 * ⚠️ NEVER FALLS BACK TO OUR MARK. No logo means no icon at all — the browser's default is
 * neutral, and StartMeUpPro's teal square in a groomer's tab is precisely the "you inherit
 * our identity" mistake this app avoids everywhere else. See the note at the top of this
 * file and the one in apps/admin/index.html.
 */
function useTabIdentity(site: Site | null): void {
  useEffect(() => {
    if (!site) return;

    document.title = site.business.name;

    /*
     * ⚠️ A DEMO TENANT'S PAGE MUST NOT BE INDEXED (#143), and this is the only place that can
     * do it. These pages are served on real public hostnames with a real-looking business on
     * them — a fake landscaping company ranking in Greenville is a problem, because somebody
     * would ring the number. Caddy serves one static index.html for every tenant and cannot
     * vary a header per tenant, and the API's X-Robots-Tag only covers its own JSON, so the
     * page has to mark itself.
     *
     * ⚠️ IT LIVES HERE FOR THE SAME REASON THE TITLE DOES: `/` and `/book` both come through
     * `useSite`, `/` falls through to booking when a tenant has no landing page, and a fourth
     * entry point must not be able to forget. Google executes page JS and honours a robots
     * meta injected this way.
     *
     * REMOVED, not just skipped, for a real tenant — a client-side route change from a demo
     * to a real page would otherwise leave the tag behind and quietly deindex a customer.
     */
    const robots =
      document.querySelector<HTMLMetaElement>('meta[name="robots"]') ??
      document.head.appendChild(
        Object.assign(document.createElement('meta'), { name: 'robots' }),
      );
    if (site.business.isDemo || site.business.isInternal)
      robots.content = 'noindex, nofollow, noarchive';
    else robots.remove();

    /*
     * ⚠️ FAVICON FIRST, LOGO SECOND, NOTHING THIRD. A logo is usually a wide wordmark and a
     * wide wordmark squashed into 32x32 is a grey smear, so a tenant who has uploaded a
     * real square icon gets that. The logo stays as the fallback because it is still theirs
     * and still better than the alternative — and the alternative is never our mark.
     */
    const { logoUrl, logoVersion, faviconUrl, faviconVersion } = site.branding;
    const [href, version] = faviconUrl ? [faviconUrl, faviconVersion] : [logoUrl, logoVersion];
    const link =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]') ??
      document.head.appendChild(Object.assign(document.createElement('link'), { rel: 'icon' }));

    /*
     * The logo is whatever shape the tenant uploaded — a wide wordmark squashes into a
     * square favicon. Used anyway: it is their asset and their decision, and a slightly
     * awkward version of their own mark beats a perfect version of somebody else's.
     *
     * `logoVersion` is the same cache-buster the header image uses, so a re-uploaded logo
     * changes the tab without waiting out a cached icon.
     */
    if (href) link.href = `${API_URL}${href}?v=${version ?? ''}`;
    else link.remove();
  }, [site]);
}

export { API_URL, Centered };

export default function BookingPage() {
  const { site, failure } = useSite();

  if (failure) return <SiteUnavailable failure={failure} />;
  if (!site) {
    return (
      <Centered>
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </Centered>
    );
  }
  return (
    <>
      <DemoBanner site={site} />
      <Flow site={site} />
    </>
  );
}

/**
 * Says on the page that this business is not real (#143).
 *
 * ⚠️ RENDERED BY BOTH ENTRY POINTS, AND IT HAS TO BE. `/` and `/book` are chosen without a
 * router (see App.tsx), so there is no shared layout to hang this on — the first version put
 * it in App's landing branch only, and `/book` came up clean. That is exactly backwards:
 * `/book` is the page a confirmation email points at and the one a prospect is most likely to
 * forward, so it is the page most likely to be seen by someone who has no idea what they are
 * looking at. Exported and used in both places rather than duplicated.
 *
 * ⚠️ DELIBERATELY OUTSIDE THE TENANT'S PALETTE. The statement belongs to us, not to the
 * business being portrayed, and a banner styled to blend in is one nobody reads.
 */
export function DemoBanner({ site }: { site: Site }) {
  if (!site.business.isDemo) return null;
  return (
    <div
      className="w-full bg-amber-400 px-5 py-2 text-center text-sm font-semibold text-amber-950"
      role="status"
    >
      Demonstration site — this business is not real, and nothing booked here is a real
      appointment.
    </div>
  );
}

/**
 * ⚠️ IT PAINTS ITS OWN WHITE, and it is the only place in this app that has to. Every other
 * screen sits on the tenant's `backgroundColor`, so these pre-branding screens — loading, 404,
 * and the transient failure — were the only ones inheriting whatever the browser supplies.
 * In a dark-mode browser that rendered slate-600 text on near-black: legible enough to
 * screenshot, not legible enough to read, on exactly the screens a customer sees when
 * something has already gone wrong.
 */
function Centered({ children }: { children: ReactNode }) {
  return <div className="grid min-h-screen place-items-center bg-white px-6">{children}</div>;
}

/**
 * The booking flow. `embedded` renders it as a section inside the tenant's landing page
 * rather than as the whole page: no min-h-screen, no logo header, no footer — the page
 * around it already carries all three, and repeating them reads as two stacked pages.
 */
/**
 * One dog's selections, as the flow holds them (#134).
 *
 * ⚠️ THE SERVICE IS THE WHOLE OBJECT, NOT AN ID. Every figure the page shows — duration,
 * tier prices, which add-ons are even offered — is read off it, and a visit can mix services,
 * so a second dog cannot borrow the first one's.
 */
/** What the server says about one postcode (#135). */
export interface AreaAnswer {
  served: boolean;
  /** False when she has no areas at all — then everywhere is served and nothing filters. */
  filtered: boolean;
  name?: string | null;
  tier?: 'home' | 'extended';
  /** `Date.getDay()` indexes she covers this area. Null when nothing filters. */
  days?: number[] | null;
}

export interface PetSelection {
  service: Service;
  tier: Tier | null;
  addonIds: string[];
  answers: Record<string, string>;
}

/** The add-on objects a pet has ticked, resolved from its own service. */
const addonsFor = (p: PetSelection, _site: Site) =>
  (p.service.addons ?? []).filter((a) => p.addonIds.includes(a.id));

/**
 * The questions THIS dog was actually shown — the tenant's profile plus its own service's,
 * minus the first-visit-only ones a returning customer never saw.
 *
 * ⚠️ PER DOG, because a visit can mix services and each service carries its own questions.
 * Asking the server to accept answers the page never displayed is how a form becomes
 * unpassable for exactly the customers it was built to spare.
 */
const questionsFor = (p: PetSelection, site: Site, firstVisit: boolean | null) =>
  [...site.settings.intakeQuestions, ...(p.service.intakeQuestions ?? [])].filter(
    (q) => !(q.firstVisitOnly && firstVisit === false),
  );

export function Flow({
  site,
  embedded = false,
  pick,
}: {
  site: Site;
  embedded?: boolean;
  /**
   * A service chosen from OUTSIDE the flow — the "Book this" button on a landing-page card.
   *
   * ⚠️ IT CARRIES A NONCE, AND THAT IS NOT DECORATION. The flow is mounted once and stays
   * mounted, so a bare `serviceId` could not express "she pressed the same card again": the
   * prop would be unchanged and nothing would happen, which reads as a dead button. The
   * nonce makes every press a distinct event.
   */
  pick?: { serviceId: string; nonce: number } | null;
}) {
  const { backgroundColor, accentColor, textColor } = site.branding;
  const [stepIndex, setStepIndex] = useState(0);
  /** The top of the flow itself — what a step change should bring into view when embedded. */
  const flowRef = useRef<HTMLDivElement>(null);
  /** The step the scroll effect last saw — so mounting, which is not a step change, scrolls nothing. */
  const lastScrolledStep = useRef(stepIndex);
  /**
   * Dogs already configured and set aside — everything except the one on screen (#134).
   *
   * ⚠️ THE DOG BEING CONFIGURED STAYS IN THE EXISTING `service`/`tier`/`addonIds`/`answers`
   * STATE, and that is deliberate. Every step below already reads those; turning them into
   * `pets[i].service` would rewrite six screens to add a second dog. Instead this array holds
   * the FINISHED ones, the working state holds the current one, and `allPets` below is the
   * two put together. One dog is the empty array plus the working state — the same code path.
   */
  const [pets, setPets] = useState<PetSelection[]>([]);
  const [service, setService] = useState<Service | null>(null);
  const [firstVisit, setFirstVisit] = useState<boolean | null>(null);
  /**
   * A returning customer we recognised from her phone number (0056).
   *
   * ⚠️ THE TOKEN IS ALL WE GET, AND THAT IS THE DESIGN. The server never sends her name,
   * address or anything else back to this page — for a mobile business the stored address is
   * her home, and returning it would mean anyone holding her mobile number could read it. The
   * details are copied onto the booking server-side when this token goes back with it.
   */
  const [rebookToken, setRebookToken] = useState<string | null>(null);
  /**
   * Who this visit is for, and the size that pet usually is (0056).
   *
   * ⚠️ THE NAME IS THE SECOND FACT THAT UNLOCKS THE SIZE. A phone number alone returns
   * nothing about a pet; the server wants the token AND the name, so a stranger holding a
   * number learns no more than they already did. See the route.
   *
   * ⚠️ A LABEL, NOT A TIER ID. `bookings.tier_label` is a deliberate snapshot, so this is
   * matched against the CURRENT tiers by text — and simply finds nothing if the operator has
   * since renamed them, which leaves her picking a size exactly as she does today.
   */
  const [petName, setPetName] = useState('');
  const [recalledTier, setRecalledTier] = useState<string | null>(null);
  /**
   * What this pet was last booked in for, as snapshots (0056 + 2026-09-08).
   *
   * ⚠️ MATCHED ONTO THE LIVE CATALOGUE HERE, NOT ON THE SERVER, because the catalogue is
   * already on this page and the booking stores what was SOLD rather than ids. A service she
   * has renamed, a size she has retired or an add-on she has dropped simply does not match and
   * quietly leaves the offer — which is the same rule the size recall has always followed.
   */
  const [lastPackage, setLastPackage] = useState<LastPackage | null>(null);
  /** Whether the lookup for the CURRENT name has returned. Every keystroke resets it. */
  const [petLookupSettled, setPetLookupSettled] = useState(false);
  const petLookup = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Null unless the operator has marked one of her questions as the pet's name (0056). */
  const petNameQuestionId = useMemo(
    () => site.settings.intakeQuestions.find((q) => q.role === 'pet-name')?.id ?? null,
    [site.settings.intakeQuestions],
  );
  /** Yes → an open field, so a mover can correct it without being shown the old one. */
  const [addressChanged, setAddressChanged] = useState(false);
  const [tier, setTier] = useState<Tier | null>(null);
  const [addonIds, setAddonIds] = useState<string[]>([]);
  const [recurrence, setRecurrence] = useState<Recurrence>('once');
  /**
   * Where the job is, as a postcode (#135). Asked before the calendar because it decides
   * which days are offered.
   */
  const [zip, setZip] = useState('');
  /** The server's answer for that ZIP. Null until asked. */
  const [area, setArea] = useState<AreaAnswer | null>(null);
  /*
   * Where she is bringing the dog (0091). A drop-off business with one location needs no
   * question — it is preselected and the step is skipped; with two or more she picks.
   */
  const locations = site.settings.locations ?? [];
  const dropOff = locations.length > 0;
  const [locationId, setLocationId] = useState<string | null>(() =>
    locations.length === 1 ? locations[0]!.id : null,
  );
  const location = locations.find((l) => l.id === locationId) ?? null;
  const [date, setDate] = useState<string | null>(null);
  const [slot, setSlot] = useState<Slot | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', address: '', notes: '' });
  /**
   * ⚠️ FALSE UNTIL SHE TICKS IT. A carrier accepts an opt-in the customer actively selected —
   * a box waiting for her already ticked is the thing their reviewers reject.
   * `check-legal-disclosures.mjs` fails the build if this ever starts life as `true`.
   */
  const [smsConsent, setSmsConsent] = useState(false);
  /*
   * Her cancellation policy (0083) and the customer's tick. The text is state rather than read
   * off `site` because the server answers a stale one with the current words (409
   * `policy_changed`), and the page has to show THOSE before asking again. Unticked to start:
   * an agreement nobody made isn't one.
   */
  const [policyText, setPolicyText] = useState<string | null>(site.settings.cancellationPolicy ?? null);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);

  /*
   * ⚠️ THE STEPS ARE A LIST, NOT A COUNTER, and that is the whole reason the profile step
   * can exist. Most tenants ask nothing — a landscaper has no dog to describe — and a page
   * that said "Step 3 of 5" and then jumped from 3 to 5 would look broken. The profile is
   * simply absent from the list when there is nothing to ask, so "of N" is always true.
   */
  const profileQuestions = useMemo(
    () => [...site.settings.intakeQuestions, ...(service?.intakeQuestions ?? [])],
    [site.settings.intakeQuestions, service],
  );
  /*
   * ⚠️ A DEPOSIT IS ITSELF A REASON TO ASK. Found on staging: a tenant had a deposit
   * configured and no first-visit-only questions, so nothing ever put the question to a
   * customer, `firstVisit` stayed null, and the deposit could never apply to anybody. The
   * operator would have waited for deposits that were never going to be requested, with
   * nothing anywhere saying why.
   */
  const asksFirstVisit =
    profileQuestions.some((q) => q.firstVisitOnly) ||
    site.settings.firstVisitDepositCents != null;

  /*
   * ⚠️ THE PROFILE MOVED IN FRONT OF THE CALENDAR (#134), and it had to. Everything about
   * the dogs has to be settled before a day can be offered, because the slot grid reserves
   * the SUM of their durations — two Full Grooms is four hours in the driveway. "Add another
   * dog" lives at the end of the profile step, so the loop closes there and the calendar is
   * asked exactly once, for the whole visit.
   *
   * The order for a one-dog customer changes as a side effect: she now answers about her dog
   * before picking a time rather than after. That reads at least as naturally — everything
   * about the dog, then when, then who you are — and it is the price of one flow instead of
   * two.
   *
   * ⚠️ REVERSED 2026-10-12: the questions now come AFTER the calendar (see `steps` below). The
   * reasoning above about durations still holds; it never needed the questions, only the
   * services, and those stay on step one. "Add another" stays on the profile step and sends
   * her back through the calendar, so the calendar is still asked for the whole visit.
   */
  /*
   * ⚠️ THE ZIP STEP ONLY EXISTS IF SHE HAS AREAS (#135). Every tenant today has none, and a
   * postcode question in front of a customer that changes nothing is a step that costs
   * bookings for no reason. `site.settings.hasAreas` is the server's answer to "does this
   * filter anything"; when it is false the flow is exactly what it was.
   *
   * ⚠️ AND IT SITS BEFORE THE CALENDAR, which is the whole point: the days offered depend on
   * where she lives, so asking afterwards would mean showing a grid we then have to take
   * away.
   */
  const steps = useMemo(() => {
    const askProfile = profileQuestions.length > 0 || asksFirstVisit;
    /*
     * ⚠️ TIMES BEFORE THE QUESTIONS (Mike, 2026-10-12). Fluffy Paw Spa asked a new customer
     * nine questions about the dog before showing a single time, and 18 of 384 site visits
     * ever reached the calendar. Nothing in the profile changes what the calendar offers (the
     * services, sizes and add-ons do, and they are all settled on step one), so the questions
     * now follow the time. "Add another" still lives on the profile step; it releases the time
     * and sends her back through the calendar for the whole visit, which is correct, because
     * a second dog changes how long the visit is. That re-pick is the price, paid only by the
     * few who add a dog after choosing a time.
     */
    return [
      'services' as const,
      /*
       * ⚠️ A DROP-OFF BUSINESS NEVER ASKS FOR A ZIP (0091). Her areas are where she drives;
       * a customer bringing the dog to her has no use for them, and the location decides the
       * days instead.
       */
      ...(site.settings.hasAreas && !dropOff ? (['zip'] as const) : []),
      ...(locations.length > 1 ? (['where'] as const) : []),
      'when' as const,
      ...(askProfile ? (['profile'] as const) : []),
      /*
       * ⚠️ SKIPPED FOR A RECOGNISED RETURNING CUSTOMER (0056) — this step is the name, phone,
       * email and address, and the server already holds all four.
       *
       * ⚠️ AND IT NO LONGER COMES BACK WHEN THE ADDRESS CHANGES (Mike, 2026-09-08). It used
       * to, which meant saying "yes, it has changed" did nothing visible: the acknowledgement
       * was a step counter going from 5 to 6, and the field itself was two screens away, in
       * the company of a name, a phone and an email she had just been told she could skip.
       * The address is now asked inline, immediately under the question about it — which is
       * what the note on `addressChanged` has always claimed happened.
       */
      ...(rebookToken ? [] : (['details'] as const)),
      'confirm' as const,
    ];
  }, [
    profileQuestions.length,
    asksFirstVisit,
    site.settings.hasAreas,
    dropOff,
    locations.length,
    rebookToken,
    addressChanged,
  ]);
  // Clamped rather than reset: switching to a service with no questions on the confirm step
  // must not throw her back to the start.
  const step = steps[Math.min(stepIndex, steps.length - 1)]!;
  const goTo = (name: (typeof steps)[number]) => setStepIndex(steps.indexOf(name));
  // Each step after the first, the first time this visitor reaches it.
  useEffect(() => {
    if (step !== 'services') reportFunnel(`step:${step}`);
  }, [step]);

  /**
   * Choosing a service, from wherever.
   *
   * ⚠️ ONE FUNCTION, because there are now TWO ways in — the step-one grid and a landing-page
   * card — and the reset below is the part that must not diverge. A tier and an add-on belong
   * to the card they were shown on; carrying either across sends the server an id it will
   * refuse, or worse, one that resolves on the new service and quietly prices something she
   * did not choose.
   */
  const chooseService = useCallback(
    (s: Service) => {
      reportFunnel('flow_start');
      setService(s);
      /*
       * ⚠️ THE RECALLED SIZE IS APPLIED HERE, WHERE THE TIER IS ALREADY BEING RESET, so there
       * is exactly one place that decides what tier a freshly chosen service has. Matching is
       * on the LABEL because that is what a booking stores, and a miss leaves it null — the
       * behaviour every customer had before this existed.
       *
       * ⚠️ AND IT IS ONLY EVER A PRESELECTION. She sees it, and the size buttons work exactly
       * as they always did: a dog that has grown is one tap away from being right.
       */
      const remembered = recalledTier
        ? (s.tiers.find((t) => t.label.toLowerCase() === recalledTier.toLowerCase()) ?? null)
        : null;
      setTier(remembered);
      setAddonIds([]);
      setRecurrence('once');
      setSlot(null);
      setDate(null);
    },
    [recalledTier],
  );

  /*
   * ⚠️ THE SIZE CAN ARRIVE AFTER THE SERVICE IS ALREADY PICKED, AND chooseService NEVER RUNS
   * AGAIN. That is the whole bug (Mike, 2026-09-08): the pet lookup is debounced, so tapping
   * "Full Groom" while it is still in flight ran the preselection with nothing to preselect.
   * The page then said "we'll use Tate's usual size: Small" over a list with nothing selected
   * and a button that refused to continue — telling somebody we remembered their dog and then
   * asking them anyway, which is worse than never having claimed it.
   *
   * ⚠️ ONLY INTO AN EMPTY TIER. If she has tapped a size — a dog that grew — that is a newer
   * fact than the one on file and this must not overwrite it. `tier === null` is the whole
   * guard: there is no way to un-pick a size, so an empty tier means untouched.
   *
   * Nothing happens when the recalled size clears (`+ Add another`, or a name that matched no
   * pet): a null here is the absence of a fact, not an instruction to unpick one.
   */
  useEffect(() => {
    if (!recalledTier || !service || tier) return;
    const remembered = service.tiers.find(
      (t) => t.label.toLowerCase() === recalledTier.toLowerCase(),
    );
    if (remembered) setTier(remembered);
  }, [recalledTier, service, tier]);

  /*
   * `?service=` — preselect on arrival. See SERVICE_PARAM at the top of this file.
   *
   * ⚠️ ONCE, GUARDED BY A REF, AND NOT "WHEN NOTHING IS SELECTED". The obvious condition —
   * run while `service === null` — reselects the deep-linked service the moment she presses
   * Back from step two to change her mind, which is a picker that refuses to be un-picked.
   * The ref makes this an arrival event rather than a standing rule.
   */
  const deepLinked = useRef(false);
  /**
   * The service a `?service=` link named, if it matched one.
   *
   * ⚠️ IT HIDES THE OTHERS, NOT JUST PRESELECTS ITSELF (Mike, 2026-09-04). Preselecting alone
   * still showed an owner arriving for payment-setup help a "15-minute intro call" — a sales
   * demo of the product she already pays for — as the first thing on the page. A deep link is
   * a statement about WHY she is here, so the page answers that question and nothing else.
   *
   * ⚠️ THE OTHER SERVICES ARE STILL ONE EDIT OF THE URL AWAY. This narrows what is shown; it
   * is not access control and must never be relied on as any. The API prices and books
   * whatever service id it is given, exactly as before.
   */
  const [pinnedServiceId, setPinnedServiceId] = useState<string | null>(null);
  useEffect(() => {
    if (deepLinked.current || !SERVICE_PARAM) return;
    /*
     * ⚠️ OUR OWN TENANT ONLY (Mike, 2026-09-04). This file is the booking page EVERY tenant
     * renders — a live groomer's customers included — and `?service=` was built for one link
     * on one internal page. Ungated, it changed what a real business's customers could be
     * shown: `herdomain.com/book?service=nail-trim` would list Nail Trim and hide the other
     * three, on a page she never asked us to change. Nothing she publishes produces such a
     * link, which is exactly why it would have gone unnoticed.
     *
     * ⚠️ THE GATE IS THE WHOLE FEATURE, NOT JUST THE HIDING. Preselecting is also a behaviour
     * she did not ask for, and "harmless" is not the test — her page should do what it did
     * before we needed something for ours.
     *
     * If a tenant ever wants deep links of their own, that is a setting on their booking page,
     * not the removal of this check.
     */
    if (!site.business.isInternal) return;
    deepLinked.current = true;
    const match = site.services.find((s) => serviceSlug(s.name) === SERVICE_PARAM.toLowerCase());
    if (match) {
      chooseService(match);
      setPinnedServiceId(match.id);
    }
  }, [site.business.isInternal, site.services, chooseService]);

  /*
   * `?hold=` — the time the booking assistant held for them, and what it was for.
   *
   * ⚠️ IT PRESELECTS EVERYTHING AND SKIPS NOTHING, the rule `?service=` follows. They told the
   * assistant what they wanted; the page shows it back, on the screens where it can be changed,
   * and Continue is theirs to press. The intake questions still have to be answered — the
   * assistant never asks them.
   *
   * ⚠️ `chooseService` FIRST, THEN THE REST. It resets the size, the extras and the time by
   * design, so the hold's values are set after it; React applies the updates in order.
   */
  const [held, setHeld] = useState<HeldSlot | null>(null);
  const [holdLapsed, setHoldLapsed] = useState(false);
  const holdApplied = useRef(false);
  useEffect(() => {
    if (!HOLD_TOKEN || holdApplied.current) return;
    holdApplied.current = true;
    void fetch(withTenant('/api/public/hold'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: HOLD_TOKEN }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (h: ({ held: true; serviceId: string | null; tierId: string | null; addonIds: string[]; zip: string | null } & HeldSlot) | { held: false } | null) => {
          if (!h || !h.held) {
            setHoldLapsed(true);
            return;
          }
          const s = site.services.find((x) => x.id === h.serviceId);
          if (!s) {
            // The service was switched off since. The time may still be free; they pick again.
            setHoldLapsed(true);
            return;
          }
          chooseService(s);
          setTier(s.tiers.find((t) => t.id === h.tierId) ?? null);
          setAddonIds(h.addonIds.filter((id) => s.addons.some((a) => a.id === id)));
          if (h.zip && site.settings.hasAreas) setZip(h.zip);
          setDate(h.date);
          setHeld({ startsAt: h.startsAt, expiresAt: h.expiresAt, date: h.date });
        },
      )
      .catch(() => setHoldLapsed(true));
  }, [site.services, site.settings.hasAreas, chooseService]);

  /*
   * A clock for the note, so "held until 2:35" becomes "no longer held" without a reload. A
   * minute is plenty: the booking route re-checks the slot either way.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!held) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [held]);
  const holdLive = held !== null && new Date(held.expiresAt).getTime() > now;

  /**
   * What step one lists.
   *
   * ⚠️ FALLS BACK TO EVERYTHING IF THE PINNED SERVICE VANISHES. If the id no longer resolves —
   * a service deactivated between the link being sent and followed — showing an empty list
   * would read as "nothing is bookable". One stale link should cost a tap, not the booking.
   */
  const visibleServices = useMemo(() => {
    if (!pinnedServiceId) return site.services;
    const pinned = site.services.filter((s) => s.id === pinnedServiceId);
    return pinned.length > 0 ? pinned : site.services;
  }, [site.services, pinnedServiceId]);

  /*
   * "Book this" on a landing-page service card.
   *
   * ⚠️ KEYED ON THE NONCE, NOT THE ID, so pressing the same card twice still counts. And it
   * deliberately resets whatever she had picked before: she has just told us she wants a
   * different service, and silently keeping her old tier would be the exact bug `chooseService`
   * exists to prevent.
   *
   * ⚠️ IT DOES NOT SKIP THE SERVICES STEP. Landing her on "when" with the service chosen
   * off-screen would hide the one decision the price depends on — she would be picking a time
   * for something she can no longer see, and on a tiered service the size question lives on
   * that step too. It selects and shows her the selection; Continue is hers to press.
   */
  useEffect(() => {
    if (!pick) return;
    const s = site.services.find((x) => x.id === pick.serviceId);
    if (!s) return;
    chooseService(s);
    setStepIndex(0);
    // The card is far up the page, so bring the form to her regardless of whether the step
    // changed — the scroll effect below only fires on a step CHANGE, and usually there is none.
    flowRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }, [pick?.nonce]);

  /*
   * What she is actually shown. A returning customer skips the first-visit-only questions,
   * and the SERVER applies the identical rule when it checks the required ones — a page that
   * hid a question the API then insisted on would be unpassable.
   */
  const visibleQuestions = useMemo(
    () => profileQuestions.filter((q) => !(q.firstVisitOnly && firstVisit === false)),
    [profileQuestions, firstVisit],
  );

  const discountPercent =
    site.settings.recurringOptions.find((o) => o.recurrence === recurrence)?.percentOff ?? 0;

  /**
   * Every dog on this visit: the ones set aside, plus the one being configured.
   *
   * ⚠️ THE WORKING DOG IS INCLUDED ONLY ONCE IT HAS A SERVICE. Before that it is not a dog,
   * it is an empty form — counting it would make the calendar reserve a duration for an
   * animal nobody has chosen a groom for.
   */
  const allPets = useMemo<PetSelection[]>(
    () => (service ? [...pets, { service, tier, addonIds, answers }] : pets),
    [pets, service, tier, addonIds, answers],
  );

  /** What each dog costs on its own, before the visit's discounts. */
  const petLines = useMemo(
    () =>
      allPets.map((p) =>
        estimate({
          service: {
            durationMinutes: p.service.durationMinutes,
            pricingMode: p.service.pricingMode,
            priceCents: p.service.priceCents,
            priceMaxCents: p.service.priceMaxCents,
            priceIsEstimate: p.service.priceIsEstimate,
            tiered: p.service.tiered,
          },
          tier: p.tier,
          addons: addonsFor(p, site),
        }),
      ),
    [allPets, site],
  );

  /**
   * What the whole VISIT costs — the figure the customer actually agrees to (#134).
   *
   * ⚠️ THE DISCOUNTS LIVE HERE, NOT ON THE LINES. `petLines` above are gross on purpose: the
   * repeat rate and the multi-dog rate both apply to the visit total, and applying either per
   * dog would discount a line and then discount the sum again. Same function the server runs.
   */
  /*
   * ⚠️ THE SAME ARITHMETIC THE SERVER RUNS, including travel. The page shows the total she is
   * agreeing to, and "you'll see it before you book" is the sentence in her own approved
   * wording — a figure that appeared only after submitting would break the promise the copy
   * makes. The server still recomputes it from her areas; this is a courtesy, never an input.
   */
  const travelPercent =
    area?.tier === 'extended' ? (site.settings.travelUpchargePercent ?? null) : null;
  const visitQuote = useMemo(
    () =>
      estimateVisit({
        lines: petLines,
        recurringPercent: discountPercent,
        multiPetPercent: site.settings.multiPetDiscountPercent ?? null,
        travelUpchargePercent: travelPercent,
      }),
    [petLines, discountPercent, site.settings.multiPetDiscountPercent, travelPercent],
  );

  /*
   * Each step starts at the top; on a phone the flow is taller than the screen and landing
   * mid-page reads as nothing having happened.
   *
   * ⚠️ THE BRACES ARE LOAD-BEARING. This was `useEffect(() => window.scrollTo({…}), [step])`
   * — a concise arrow body, so the effect RETURNED whatever `window.scrollTo` returned, and
   * React treats any non-undefined return as the cleanup function. On the next step change
   * it calls it: `TypeError: n is not a function`, thrown from inside react-dom, taking the
   * whole page white.
   *
   * The spec says `scrollTo` returns undefined, which is why this survived every browser it
   * was ever tested in — including four attempts to reproduce it here. It only bites where
   * something has wrapped `scrollTo` and returns a value: a smooth-scroll polyfill, an
   * accessibility or scrolling extension, an in-app browser. Those are ordinary things for a
   * customer to have, and she would have seen a blank page at step two of booking.
   *
   * Found 2026-08-12 from a stack that pointed at React's effect-destroy loop. The rule is
   * simply: an effect body is a statement block, never an expression — what it returns is
   * API, not a convenience.
   */
  useEffect(() => {
    /*
     * ⚠️ NOT ON FIRST RENDER. Mounting is not a step change, and on the landing page the
     * customer arrives here by tapping "Book an appointment", which scrolls to this section
     * — scrolling again on mount would fight that and drag her somewhere she did not ask
     * for.
     *
     * ⚠️ COMPARED, NOT COUNTED. This was a `firstRender` flag cleared on the first run, and
     * React's development StrictMode runs every effect twice on mount — so the second run
     * found the flag already cleared and scrolled. Comparing the step is true however many
     * times the effect runs.
     */
    if (lastScrolledStep.current === stepIndex) return;
    lastScrolledStep.current = stepIndex;
    /*
     * ⚠️ EMBEDDED SCROLLS TO THE FORM, NOT TO THE DOCUMENT TOP, and that distinction is the
     * whole bug. On `/book` the form IS the page, so `top: 0` is right. Embedded in the
     * landing page it is not: the form sits below the hero, the photos and the reviews, so
     * `top: 0` threw the customer back up to the business name every time she pressed
     * Continue — past everything, with the form she was filling in now off-screen below.
     */
    if (embedded) flowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [stepIndex, embedded]);

  const onAccent = useMemo(() => pickReadable(accentColor), [accentColor]);
  const zone = useMemo(() => zoneLabel(site.settings.timezone), [site.settings.timezone]);

  const addons = useMemo(
    () => (service?.addons ?? []).filter((a) => addonIds.includes(a.id)),
    [service, addonIds],
  );

  /*
   * The running total, from the shared engine. Recomputed on every tick rather than
   * accumulated, so there is no path by which the displayed figure drifts from what the
   * server will independently arrive at from the same ids.
   */
  const quote = useMemo<Estimate | null>(
    () =>
      service
        ? estimate({
            service: {
              durationMinutes: service.durationMinutes,
              pricingMode: service.pricingMode,
              priceCents: service.priceCents,
              priceMaxCents: service.priceMaxCents,
              priceIsEstimate: service.priceIsEstimate,
              tiered: service.tiered,
            },
            tier,
            addons,
            discountPercent,
          })
        : null,
    [service, tier, addons, discountPercent],
  );

  async function submit() {
    if (!service || !slot) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(withTenant('/api/public/bookings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          /*
           * ⚠️ IDS, NOT PRICES. The server looks up every figure again from these — see the
           * note on `addonIds` in routes/public-booking.ts. The total shown on the previous
           * screen is a courtesy to the customer, never an input.
           *
           * ⚠️ ONE ENTRY PER DOG, EACH WITH ITS OWN ANSWERS (#134). `intake_answers` holds one
           * dog's profile, so "what is your dog's name" has a different answer per booking.
           */
          pets: allPets.map((p) => ({
            serviceId: p.service.id,
            tierId: p.tier?.id,
            addonIds: p.addonIds,
            /*
             * Only the answers to questions this dog was SHOWN. A returning customer's blanks
             * for first-visit-only questions are not sent at all — the server drops empties
             * anyway, but sending them would mean the request disagreed with the form.
             */
            intakeAnswers: Object.fromEntries(
              questionsFor(p, site, firstVisit)
                .map((q) => [q.id, (p.answers[q.id] ?? '').trim()] as const)
                .filter(([, v]) => v.length > 0),
            ),
          })),
          startsAt: slot.startsAt,
          /*
           * The assistant's hold, so the slot it kept for them is not counted against them.
           * Sent even after it lapsed: the server ignores a dead token and books the time if it
           * is still free, which is the outcome they want.
           */
          ...(HOLD_TOKEN && held ? { holdToken: HOLD_TOKEN } : {}),
          recurrence,
          // Sent so the server can resolve the area itself — it never trusts the page's
          // answer about which tier applies (#135).
          ...(zip ? { zip } : {}),
          // Checked again server-side, including that this is a day she is there (0091).
          ...(locationId ? { locationId } : {}),
          /*
           * ⚠️ OMITTED ENTIRELY FOR A RECOGNISED CUSTOMER (0056), not sent as empty strings.
           * The server fills a blank from the matched booking and lets anything supplied win,
           * so sending "" here would overwrite her real name with nothing. The one exception
           * is an address she has told us changed — that is the whole point of asking.
           */
          ...(rebookToken
            ? {
                rebookToken,
                ...(addressChanged && form.address ? { serviceAddress: form.address } : {}),
              }
            : {
                customerName: form.name,
                customerPhone: form.phone || undefined,
                customerEmail: form.email || undefined,
                serviceAddress: form.address || undefined,
              }),
          customerNotes: form.notes || undefined,
          firstVisit: asksFirstVisit && firstVisit !== null ? firstVisit : undefined,
          // Sent either way; the server records it only when the booking also has a number.
          smsConsent,
          smsConsentVersion: site.business.isInternal
            ? SMS_CONSENT_VERSION_INTERNAL
            : site.business.smsReminders
              ? SMS_CONSENT_VERSION_REMINDERS
              : SMS_CONSENT_VERSION,
          // The words she ticked, so the server can refuse if they've changed since (0083).
          ...(policyText
            ? { cancellationPolicyAccepted: policyAccepted, cancellationPolicyShown: policyText }
            : {}),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The one failure a real customer will actually hit: somebody else took the slot
        // while she was filling the form. Send her back to the calendar, not to a dead end.
        if (res.status === 400 && body.error === 'location_required') {
          setError(body.message ?? 'Please pick where you are bringing your pet.');
          setSlot(null);
          if (steps.includes('where')) goTo('where');
          return;
        }
        if (res.status === 409 && body.error === 'slot_unavailable') {
          setError(body.message ?? 'That time is no longer available.');
          setSlot(null);
          goTo('when');
          return;
        }
        /*
         * The server checks the required questions too, and disagreeing with it here would
         * strand her on a screen whose own validation says she is finished. Send her back to
         * the questions with the server's own wording.
         */
        if (res.status === 400 && body.error === 'intake_incomplete') {
          setError(body.issues?.[0]?.message ?? 'Some answers are missing.');
          goTo('profile');
          return;
        }
        /*
         * She rewrote her policy while this page was open (0083). Show the new words, untick
         * the box, and ask again — never book against words the customer didn't see.
         */
        if (res.status === 409 && body.error === 'policy_changed') {
          setPolicyText(body.policy ?? null);
          setPolicyAccepted(false);
          setError(`${site.business.name} just updated their cancellation policy. Please read it and check the box again.`);
          return;
        }
        if (res.status === 400 && body.error === 'policy_not_accepted') {
          setError('Please check the box to agree to the cancellation policy.');
          return;
        }
        setError('Something went wrong. Please try again.');
        return;
      }
      setDone({
        ...body.booking,
        location: body.location ?? null,
        ics: body.ics,
        emailSent: body.emailSent,
        upload: body.upload,
      });
    } catch {
      setError('We could not reach the booking system. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (!site.settings.acceptingBookings) {
    return (
      <Shell site={site} embedded={embedded}>
        <Card bg={backgroundColor} text={textColor}>
          <h2 className="text-lg font-semibold">Not taking bookings right now</h2>
          <p className="mt-1 opacity-75">
            {site.business.name} isn’t accepting online bookings at the moment. Please check back
            soon.
          </p>
        </Card>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell site={site} embedded={embedded}>
        <Card bg={backgroundColor} text={textColor}>
          <div
            className="mb-3 flex h-10 w-10 items-center justify-center rounded-full"
            style={{ backgroundColor: accentColor, color: onAccent }}
          >
            <Check className="h-5 w-5" />
          </div>
          {/*
            ⚠️ BOOKED EITHER WAY NOW (#132). Quote services used to arrive as a request the
            operator had to accept; they hold the slot outright since 2026-08-14. So the
            heading is the same for both, and what differs is the PRICE — she is coming, and
            the figure follows the visit.

            Saying only "you're booked" to a quote customer is the failure mode this branch
            exists to prevent: with no figure anywhere on the screen, silence reads as a
            price agreed.
          */}
          <h2 className="text-xl font-semibold">You’re booked</h2>
          <p className="mt-1 opacity-75">
            {done.estimateMinCents == null
              ? `${site.business.name} has your appointment. She will give you a price when she sees the job — nothing is agreed yet.`
              : `${site.business.name} has your appointment.`}
          </p>
          <dl className="mt-4 space-y-1 border-t pt-4 text-sm" style={{ borderColor: hairline(textColor) }}>
            <Row label="Service" value={service?.name ?? ''} />
            {done.tierLabel && <Row label="Size" value={done.tierLabel} />}
            {(done.addons ?? []).length > 0 && (
              <Row label="Extras" value={(done.addons ?? []).map((a) => a.name).join(', ')} />
            )}
            <Row label="When" value={`${date ? prettyDate(date) : ''}, ${slot ? prettyTime(slot.label) : ''}${zone ? ` ${zone}` : ''}`} />
            {recurrence !== 'once' && (
              <Row
                label="Repeats"
                value={
                  RECURRENCE_LABELS[recurrence] +
                  (done.recurringDiscountPercent ? ` · ${done.recurringDiscountPercent}% off` : '')
                }
              />
            )}
            {done.location ? (
              <Row
                label="Where"
                value={[done.location.name, done.location.address].filter(Boolean).join(', ')}
              />
            ) : (
              form.address && <Row label="Where" value={form.address} />
            )}
            {done.depositCents != null && (
              <Row label="Deposit on your first visit" value={money(done.depositCents)} />
            )}
            {done.estimateMinCents != null && (
              <div
                className="mt-2 flex justify-between gap-4 border-t pt-2"
                style={{ borderColor: hairline(textColor) }}
              >
                <dt className="font-medium">{done.priceIsEstimate ? 'Estimated' : 'Price'}</dt>
                <dd className="text-right text-lg font-bold">
                  {formatEstimate({
                    minCents: done.estimateMinCents,
                    maxCents: done.estimateMaxCents ?? null,
                    isEstimate: done.priceIsEstimate ?? false,
                  })}
                </dd>
              </div>
            )}
          </dl>

          {/*
            The reason the figure can move, repeated HERE as well as on the previous screen.
            This is the page she will still be looking at when she books, and a starting-at
            price whose explanation was two taps ago is how somebody expects $100 and is
            asked for $140.
          */}
          {done.priceIsEstimate && service?.priceNote && (
            <p className="mt-3 text-sm opacity-70">{service.priceNote}</p>
          )}
          {/*
            ⚠️ SAID AGAIN HERE, because this is the screen she keeps. A deposit line with no
            explanation next to a completed booking reads as something already taken, and the
            confirmation is exactly where somebody checks whether they have been charged.
          */}
          {done.depositCents != null && (
            <p className="mt-3 text-sm opacity-70">
              Nothing has been charged. {site.business.name} will arrange the{' '}
              {money(done.depositCents)} deposit with you directly.
            </p>
          )}
          <UnpricedAddonNote addons={(done.addons ?? []).filter((a) => a.priceUnit === 'minute')} />
          {/*
            The calendar file is the product's ENTIRE reminder mechanism (#48 chose it over
            SMS), so it gets a real button rather than a line of text — and it is offered
            whether or not an email went, because the email address is optional and a
            customer who gave only a phone number would otherwise leave with nothing.
          */}
          {done.ics && (
            <button
              onClick={() => downloadIcs(done.ics!, service?.name ?? 'appointment')}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold"
              style={{ backgroundColor: accentColor, color: onAccent }}
            >
              <CalendarPlus className="h-5 w-5" /> Add to my calendar
            </button>
          )}

          {/*
            Says only what is true. Promising an email that was never sent — because she
            gave no address, or because the provider is down — is worse than saying nothing,
            since she would wait for it instead of noting the time.
          */}
          <p className="mt-4 text-sm opacity-60">
            {done.emailSent
              ? 'We’ve emailed you a copy with the calendar invite attached.'
              : 'Add it to your calendar above so you don’t forget — we haven’t sent an email.'}
          </p>
        </Card>

        {/*
          The vaccination record, offered only after the booking is safely made. Below the
          card rather than inside it: the appointment is done and nothing here can undo it,
          which is exactly what the layout should say.
        */}
        {done.upload?.wanted && (
          <DocumentUpload
            token={done.upload.token}
            retentionDays={done.upload.retentionDays}
            accent={accentColor}
            onAccent={onAccent}
            text={textColor}
            businessName={site.business.name}
            label={done.upload.label ?? null}
          />
        )}
      </Shell>
    );
  }

  return (
    <Shell site={site} embedded={embedded}>
      <div ref={flowRef} className="mb-4 flex items-center gap-3">
        {stepIndex > 0 && (
          <button
            onClick={() => setStepIndex((s) => Math.max(0, s - 1))}
            className="flex items-center gap-1 text-sm opacity-70 hover:opacity-100"
            style={{ color: textColor }}
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
        )}
        <span className="text-xs uppercase tracking-wide opacity-60" style={{ color: textColor }}>
          Step {steps.indexOf(step) + 1} of {steps.length}
        </span>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/*
        ⚠️ "HELD", NEVER "BOOKED" — the same word the text message used. Nothing is booked until
        they press Confirm, and a page that said otherwise is how somebody turns up to an
        appointment that does not exist.
      */}
      {held && !done && (
        <div className="mb-4 rounded-lg border px-4 py-3 text-sm" style={{ borderColor: hairline(textColor), color: textColor }}>
          {holdLive ? (
            <>
              <span className="font-semibold">
                {prettyDate(held.date)} at {clockIn(held.startsAt, site.settings.timezone)} is held for you
              </span>{' '}
              until {clockIn(held.expiresAt, site.settings.timezone)}. Finish below to book it.
            </>
          ) : (
            <>
              We&rsquo;re no longer holding {prettyDate(held.date)} at{' '}
              {clockIn(held.startsAt, site.settings.timezone)}, but you can still book it if it&rsquo;s free.
            </>
          )}
        </div>
      )}
      {holdLapsed && !held && !done && (
        <div className="mb-4 rounded-lg border px-4 py-3 text-sm" style={{ borderColor: hairline(textColor), color: textColor }}>
          {/*
            Neutral on purpose: the server does not say whether the hold lapsed or was already
            used to book, and a customer who booked yesterday and reopens the text should not be
            told something "ran out".
          */}
          This link isn&rsquo;t holding a time any more. Pick a time below and we&rsquo;ll book it if
          it&rsquo;s free.
        </div>
      )}

      {step === 'services' && (
        <StepServices
          site={site}
          rebookToken={rebookToken}
          asksFirstVisit={asksFirstVisit}
          /*
           * ⚠️ A MATCH SETTLES `firstVisit` TOO, and that is the point of recognising her
           * here rather than a screen later: the first-visit-only questions are filtered
           * before they render, instead of a list getting shorter underneath her. It also
           * gets the deposit right from the start, since that only applies to a first visit.
           */
          onRecognised={(t) => {
            setRebookToken(t);
            setFirstVisit(false);
          }}
          firstVisit={firstVisit}
          /*
           * ⚠️ SAME RULE AS STEP TWO'S COPY OF THIS: saying "first time" after a match must
           * DROP the match, or the token rides along on a request whose form is now a
           * stranger's and the server fills her blanks with the previous customer's details.
           */
          onFirstVisit={(v) => {
            reportFunnel('flow_start');
            setFirstVisit(v);
            if (v) {
              setRebookToken(null);
              setAddressChanged(false);
            }
          }}
          lastPackage={lastPackage}
          /*
           * ⚠️ APPLIED AS ONE SELECTION, NOT BY REPLAYING TAPS. `chooseService` resets the tier
           * and the add-ons by design — that is what stops a size chosen on one card being
           * carried onto another — so calling it and then setting the rest would wipe exactly
           * what it was about to be given. Everything the offer named goes on together, or a
           * customer agrees to a package and gets a bare service.
           */
          onSamePackage={(chosen, t, ids) => {
            setService(chosen);
            setTier(t);
            setAddonIds(ids);
            setRecurrence('once');
            setSlot(null);
            setDate(null);
          }}
          /*
           * ⚠️ `firstVisit` GOES BACK TO null, NOT false. It was only false because we
           * inferred it from the match; leaving it false would silently keep her past the
           * first-visit questions and the deposit as somebody we no longer believe she is.
           */
          petName={petName}
          recalledTier={recalledTier}
          petLookupSettled={petLookupSettled}
          hasPetNameQuestion={petNameQuestionId !== null}
          /*
           * ⚠️ DEBOUNCED. She is typing a name, so every keystroke would otherwise be a
           * request. A miss is reported on BLUR rather than here — "Bel" is not found either,
           * and telling somebody we have no pet by that name while they are still spelling it
           * is worse than saying nothing.
           */
          onPet={(name) => {
            setPetName(name);
            setRecalledTier(null);
            setLastPackage(null);
            setPetLookupSettled(false);
            /*
             * ⚠️ IT ALSO ANSWERS HER OWN QUESTION. Step two asks "What is your dog's name?" —
             * that is the very question this field is keyed to — and making a customer type
             * the same word twice, two screens apart, would be a worse experience than the one
             * we started with.
             */
            if (petNameQuestionId) {
              setAnswers((a) => ({ ...a, [petNameQuestionId]: name }));
            }
            if (petLookup.current) clearTimeout(petLookup.current);
            if (!rebookToken || name.trim().length < 2) return;
            petLookup.current = setTimeout(() => {
              void fetch(withTenant('/api/public/rebook/pet'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: rebookToken, name: name.trim() }),
              })
                .then((r) => (r.ok ? r.json() : null))
                .then((b: PetLookup | null) => {
                  if (b?.tierLabel) setRecalledTier(b.tierLabel);
                  setLastPackage(b?.lastPackage ?? null);
                  /*
                   * ⚠️ SETTLED EVEN ON A NULL, because "we asked and found nothing" is the
                   * thing the message below needs to know. Left unset, a miss is
                   * indistinguishable from a request still in flight.
                   */
                  setPetLookupSettled(true);
                })
                .catch(() => setPetLookupSettled(false));
            }, 700);
          }}
          services={visibleServices}
          selected={service}
          tier={tier}
          addonIds={addonIds}
          quote={quote}
          recurrence={recurrence}
          onSelect={chooseService}
          onTier={(t) => {
            setTier(t);
            // A tier can change the length of the appointment, so the chosen slot may no
            // longer fit. Clearing it sends her back through the grid rather than letting
            // the server refuse her at the last step.
            setSlot(null);
            setDate(null);
          }}
          onToggleAddon={(id) => {
            setAddonIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
            setSlot(null);
            setDate(null);
          }}
          onRecurrence={setRecurrence}
          /*
           * ⚠️ ADVANCE BY INDEX, NEVER BY NAME. This said `goTo('when')`, which was correct
           * when services was followed by the calendar — and silently WRONG the moment the
           * profile moved in front of it (#134): the step existed, the list contained it, and
           * every customer sailed straight past the questions about her dog. Nothing failed;
           * the answers were simply never asked for.
           */
          onNext={() => setStepIndex((s) => s + 1)}
          onAccent={onAccent}
        />
      )}

      {step === 'zip' && (
        <StepZip
          site={site}
          zip={zip}
          area={area}
          onZip={(v) => {
            setZip(v);
            // A changed postcode invalidates the answer AND anything chosen under it.
            setArea(null);
            setSlot(null);
            setDate(null);
          }}
          onArea={setArea}
          onNext={() => setStepIndex((s) => s + 1)}
          onAccent={onAccent}
        />
      )}

      {step === 'where' && (
        <StepWhere
          site={site}
          locations={locations}
          locationId={locationId}
          onPick={(id) => {
            // A different location is a different set of days: the time chosen under the old
            // one may not exist under this one.
            if (id !== locationId) {
              setSlot(null);
              setDate(null);
            }
            setLocationId(id);
            setStepIndex((s) => s + 1);
          }}
        />
      )}

      {step === 'when' && service && (
        <StepWhen
          site={site}
          service={service}
          tier={tier}
          addonIds={addonIds}
          /*
           * ⚠️ EVERY DOG, so the grid reserves the SUM (#134). Two Full Grooms is four hours
           * in the driveway, not two — a grid computed at one dog's length offers times that
           * cannot hold the visit, and she would find out at the last step.
           */
          pets={allPets}
          /* Filters the grid to the days she covers this postcode (#135). */
          zip={area?.filtered ? zip : ''}
          locationId={locationId}
          durationMinutes={visitQuote.durationMinutes || service.durationMinutes}
          date={date}
          slot={slot}
          zone={zone}
          held={held}
          onDate={setDate}
          onSlot={setSlot}
          onNext={() => setStepIndex((s) => s + 1)}
          onAccent={onAccent}
        />
      )}

      {step === 'details' && service && (
        <StepDetails
          site={site}
          form={form}
          onForm={setForm}
          smsConsent={smsConsent}
          onSmsConsent={setSmsConsent}
          dropOff={dropOff}
          onNext={() => setStepIndex((s) => s + 1)}
          onAccent={onAccent}
        />
      )}

      {step === 'profile' && service && (
        <StepProfile
          site={site}
          questions={visibleQuestions}
          answers={answers}
          firstVisit={firstVisit}
          asksFirstVisit={asksFirstVisit}
          onFirstVisit={(v) => {
            setFirstVisit(v);
            /*
             * ⚠️ SAYING "FIRST TIME" AFTER A MATCH MUST DROP THE MATCH. Otherwise the token
             * rides along on a request whose form is now a stranger's, and the server would
             * quietly fill her blanks with the previous customer's details.
             */
            if (v) {
              setRebookToken(null);
              setAddressChanged(false);
            }
          }}
          rebookToken={rebookToken}
          onRebookToken={setRebookToken}
          addressChanged={addressChanged}
          address={form.address}
          onAddress={(v) => setForm((f) => ({ ...f, address: v }))}
          onAddressChanged={setAddressChanged}
          onAnswers={setAnswers}
          onNext={() => setStepIndex((s) => s + 1)}
          onAccent={onAccent}
          /*
           * ⚠️ THE LOOP CLOSES HERE (#134). Everything about a dog is settled by the end of
           * this step, and the calendar cannot be offered until every dog is known, because
           * it reserves the SUM of their durations.
           */
          pets={pets}
          onAddAnother={() => {
            if (!service) return;
            setPets((p) => [...p, { service, tier, addonIds, answers }]);
            /*
             * ⚠️ THE WORKING STATE IS CLEARED, NOT CARRIED. The next dog picks its own
             * service, its own size and its own add-ons — copying the first one's forward
             * would put a Giant groom on a puppy because nobody noticed the form was
             * pre-filled. `firstVisit` is deliberately NOT reset: it is a fact about the
             * CUSTOMER, and she is the same person.
             */
            setService(null);
            setTier(null);
            setAddonIds([]);
            setAnswers({});
            /*
             * ⚠️ THE PET AND ITS RECALLED SIZE GO WITH THEM (0056), and forgetting these two
             * was exactly the failure the note above describes. The name field kept saying
             * "Bella" for the second dog, `recalledTier` kept Bella's size and preselected it
             * the moment a service was picked — while `answers` HAD been cleared, so the
             * second dog would have been booked at Bella's size under no name at all.
             *
             * `rebookToken` is NOT cleared, for the same reason `firstVisit` is not: it is a
             * fact about the CUSTOMER, and she is the same person.
             */
            setPetName('');
            setRecalledTier(null);
            setLastPackage(null);
            setPetLookupSettled(false);
            setSlot(null);
            setDate(null);
            goTo('services');
          }}
          onRemovePet={(i) => setPets((p) => p.filter((_, x) => x !== i))}
        />
      )}

      {step === 'confirm' && service && slot && quote && (
        <StepConfirm
          rebookToken={rebookToken}
          site={site}
          service={service}
          tier={tier}
          addons={addons}
          quote={quote}
          /*
           * ⚠️ THE VISIT'S FIGURE, NOT THE LAST DOG'S (#134). `quote` is the dog on screen;
           * for a two-dog visit it is roughly half the bill. This is the last screen before
           * she commits, and a total that covers one animal while reading as though it
           * covered the visit is the exact failure the pricing model exists to prevent.
           */
          pets={allPets}
          petLines={petLines}
          visitQuote={visitQuote}
          discountPercent={discountPercent}
          date={date!}
          slot={slot}
          zone={zone}
          recurrence={recurrence}
          form={form}
          where={location?.name ?? null}
          questions={visibleQuestions}
          answers={answers}
          firstVisit={firstVisit}
          smsConsent={smsConsent}
          onSmsConsent={setSmsConsent}
          policyText={policyText}
          policyAccepted={policyAccepted}
          onPolicyAccepted={setPolicyAccepted}
          submitting={submitting}
          onSubmit={submit}
          onAccent={onAccent}
        />
      )}
      {/* Last, so its spacer lands under the form and the floating button never covers the last control. Not on the done screens. */}
      {site.settings.brainEnabled && <AssistantChat site={site} />}
    </Shell>
  );
}

// ── Shell ──────────────────────────────────────────────────────────────────

function Shell({
  site,
  children,
  embedded = false,
}: {
  site: Site;
  children: ReactNode;
  embedded?: boolean;
}) {
  const { backgroundColor, textColor, logoUrl, logoVersion } = site.branding;

  // Embedded: just the column. The landing page supplies the background, the logo and the
  // footer, and drawing them twice would look like the page had been pasted onto itself.
  if (embedded) {
    return (
      <div className="mx-auto max-w-2xl px-5" style={{ color: textColor }}>
        {children}
      </div>
    );
  }

  /*
    Our own page gets our wordmark and a way back to the site the visitor came from; a
    customer's page keeps the header it has always had.
  */
  const ours = site.business.isInternal ? ourBrand() : null;

  return (
    <div className="min-h-screen" style={{ backgroundColor, color: textColor }}>
      <div className="mx-auto max-w-2xl px-5 py-8 sm:py-12">
        {ours && (
          <nav className="mb-8 flex flex-wrap items-center justify-between gap-4">
            <a href={ours.home}>
              <img src={ours.logo} alt={ours.name} className="h-9 w-auto sm:h-10" />
            </a>
            <a href={ours.home} className="text-sm font-semibold underline underline-offset-4 opacity-75 hover:opacity-100">
              &larr; Back to {ours.home.replace('https://', '')}
            </a>
          </nav>
        )}
        {/* Our wordmark is already in the nav above, so the name is not repeated here. */}
        <header className="mb-6">
          {ours ? null : logoUrl ? (
            <img
              src={`${API_URL}${logoUrl}?v=${logoVersion}`}
              alt={site.business.name}
              className="mb-3 max-h-14 max-w-[220px] object-contain"
            />
          ) : (
            <div className="mb-1 text-xl font-bold">{site.business.name}</div>
          )}
          {site.settings.intro && <p className="opacity-75">{site.settings.intro}</p>}
        </header>
        {children}
        {/*
          Our name appears once, small, at the bottom — and nowhere else. The customer is
          buying from this business, not from us (#48).
        */}
        <footer className="mt-10 text-xs opacity-40">
          <a href="/privacy" className="underline">
            Privacy
          </a>
          {' \u00b7 '}
          <a href="/terms" className="underline">
            Terms
          </a>
          {' \u00b7 '}
          {/*
            ⚠️ LINKED, NOT JUST PUBLISHED. /text is the page a campaign registration points a
            reviewer at for the call to action, and "published on the site" has to be true: a
            URL reachable only by typing it is not published. See legal-pages.ts.
          */}
          <a href="/text" className="underline">
            Text messages
          </a>
          {' \u00b7 '}
          Booking powered by StartMeUpPro
        </footer>
      </div>
    </div>
  );
}

function Card({ children, bg, text }: { children: ReactNode; bg: string; text: string }) {
  // A panel that sits on the tenant's background without introducing a third colour: a
  // translucent white lift works on light backgrounds, a translucent black on dark ones.
  const dark = isDark(bg);
  return (
    <div
      className="rounded-xl p-5"
      style={{
        backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.035)',
        color: text,
      }}
    >
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="opacity-60">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  );
}

function PrimaryButton({
  children,
  disabled,
  onClick,
  accent,
  onAccent,
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  accent: string;
  onAccent: string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="mt-5 w-full rounded-xl px-5 py-3 text-base font-semibold transition disabled:opacity-40"
      style={{ backgroundColor: accent, color: onAccent }}
    >
      {children}
    </button>
  );
}

// ── Step 1 — what do you need ──────────────────────────────────────────────

/*
 * ── The three things a chosen service brings with it ────────────────────────
 *
 * ⚠️ EXTRACTED SO THE TWO LAYOUTS CANNOT DRIFT. The reveal layout and the `?reveal=0`
 * fallback render exactly these, from here. Copying the markup into either would
 * mean her prices, her wording and the per-minute unit note all have two homes, and the
 * second one goes stale the first time she changes something.
 */
function TierList({
  service: s,
  tier,
  onTier,
  accentColor,
  onAccent,
  textColor,
}: {
  service: Service;
  tier: Tier | null;
  onTier: (t: Tier) => void;
  accentColor: string;
  onAccent: string;
  textColor: string;
}) {
  return (
    <div className="mt-2 space-y-1.5 px-1">
        {s.tiers.map((t) => {
          const on = tier?.id === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onTier(t)}
              className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition"
              style={
                on
                  ? { backgroundColor: accentColor, color: onAccent }
                  : { border: `1px solid ${hairline(textColor)}`, color: textColor }
              }
            >
              <span className="min-w-0">
                {/* Her wording, verbatim. The weight bracket is guidance for the
                    customer, never a rule the platform enforces. */}
                <span className="font-medium">{t.label}</span>
                {t.durationMinutes != null && t.durationMinutes !== s.durationMinutes && (
                  <span className="ml-2 opacity-70">{durationLabel(t.durationMinutes)}</span>
                )}
              </span>
              <span className="shrink-0 font-semibold">
                {t.priceMaxCents != null && t.priceMaxCents !== t.priceCents
                  ? `${money(t.priceCents)}–${money(t.priceMaxCents)}`
                  : s.priceIsEstimate
                    ? `${money(t.priceCents)}+`
                    : money(t.priceCents)}
              </span>
            </button>
          );
        })}
      </div>
  );
}

function AddonList({
  service: s,
  addonIds,
  onToggleAddon,
  accentColor,
  onAccent,
  textColor,
}: {
  service: Service;
  addonIds: string[];
  onToggleAddon: (id: string) => void;
  accentColor: string;
  onAccent: string;
  textColor: string;
}) {
  return (
    <div className="mt-3 px-1">
        <div className="mb-1.5 flex items-center gap-1.5 text-sm font-medium opacity-80">
          <Plus className="h-3.5 w-3.5" /> Add anything else?
        </div>
        <div className="space-y-1.5">
          {s.addons.map((a) => {
            const on = addonIds.includes(a.id);
            return (
              <button
                key={a.id}
                onClick={() => onToggleAddon(a.id)}
                aria-pressed={on}
                className="flex w-full items-start justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition"
                style={
                  on
                    ? { backgroundColor: accentColor, color: onAccent }
                    : { border: `1px solid ${hairline(textColor)}`, color: textColor }
                }
              >
                <span className="min-w-0">
                  <span className="font-medium">{a.name}</span>
                  {a.description && (
                    <span className="mt-0.5 block text-xs opacity-75">{a.description}</span>
                  )}
                </span>
                <span className="shrink-0 text-right">
                  <span className="font-semibold">
                    {a.priceMaxCents != null && a.priceMaxCents !== a.priceCents
                      ? `${money(a.priceCents)}–${money(a.priceMaxCents)}`
                      : money(a.priceCents)}
                  </span>
                  {/*
                    ⚠️ THE UNIT, ALWAYS. "$1–$2" and "$1–$2 per minute" are wildly
                    different promises, and the second one is the one she charges.
                  */}
                  {a.priceUnit === 'minute' && (
                    <span className="block text-xs opacity-75">per minute</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
  );
}

function RecurrenceChips({
  site,
  recurrence,
  onRecurrence,
  accentColor,
  onAccent,
  textColor,
}: {
  site: Site;
  recurrence: Recurrence;
  onRecurrence: (r: Recurrence) => void;
  accentColor: string;
  onAccent: string;
  textColor: string;
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-2 px-1">
        {/*
          Driven entirely by what the operator configured. "Once" is always here
          and never carries a discount; the rest are hers, in her order, with her
          percentages. The page never invents a chip and never invents a saving.
        */}
        {([{ recurrence: 'once' as Recurrence, percentOff: 0 }] as const)
          .concat(site.settings.recurringOptions as never)
          .map(({ recurrence: value, percentOff }) => (
            <button
              key={value}
              onClick={() => onRecurrence(value)}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-medium"
              style={
                recurrence === value
                  ? { backgroundColor: accentColor, color: onAccent }
                  : { border: `1px solid ${hairline(textColor)}`, color: textColor }
              }
            >
              {value !== 'once' && <Repeat className="h-3 w-3" />}
              {RECURRENCE_LABELS[value]}
              {percentOff > 0 && (
                <span className="opacity-80">· {percentOff}% off</span>
              )}
            </button>
          ))}
      </div>
  );
}

/**
 * A section that opens when the answer above it arrives.
 *
 * ⚠️ IT MOUNTS CLOSED AND OPENS ON A LATER FRAME, AND THAT IS THE ENTIRE TRICK. A node that
 * is created already-open has exactly one computed style and a transition needs two, so it
 * appears instantly — with no error, and with markup and CSS that both read as correct. Two
 * attempts at this looked right and did nothing before the cause was found.
 *
 * ⚠️ AND IT IS DRIVEN BY `open`, NEVER BY THE CLICK THAT CAUSED IT. A size recalled from the
 * pet lookup lands a moment after the tap that chose the service — if opening were wired to
 * the tap, that late fact would open nothing, which is exactly the bug that shipped on
 * 2026-09-07: the page said it remembered the dog and then refused to continue.
 */
/**
 * `Fold` in the reveal layout, and nothing at all under `?reveal=0`.
 *
 * ⚠️ THIS EXISTS BECAUSE THE FIRST VERSION ANIMATED THE SHIPPED PAGE. A `Fold` that is open
 * on its very first render still mounts closed and opens a frame later — which is exactly
 * right when a customer has just answered something, and exactly wrong on load, where it made
 * the service list slide in every time the booking page opened. Wrapping instead of branching
 * is what caused it: the classic layout must not merely LOOK unchanged, it must render the
 * same DOM.
 */
function Reveal({ open, children }: { open: boolean; children: ReactNode }) {
  if (!REVEAL_MODE) return open ? <>{children}</> : null;
  return <Fold open={open}>{children}</Fold>;
}

function Fold({ open, children }: { open: boolean; children: ReactNode }) {
  const [shown, setShown] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  /** When this section began opening, so a gesture around the tap counts against it. */
  const openedAt = useRef(0);

  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    openedAt.current = Date.now();
    const id = window.requestAnimationFrame(() => setShown(true));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  /*
   * ── Bringing the new section into view ──────────────────────────────────
   *
   * ⚠️ THE DANGEROUS PART OF THIS WHOLE FEATURE, AND THE ONLY ONE THAT CAN MAKE THE PAGE WORSE
   * THAN IT WAS. Revealing a section below the fold and leaving it there is the reveal doing
   * nothing on a phone; moving the page under a thumb that is already moving is the thing
   * people close a tab over. Three rules, and all three matter:
   *
   *   1. AFTER the opening finishes, not during. The target's own height is still animating,
   *      so a scroll started early aims at a position that no longer exists by the time it
   *      lands, and overshoots.
   *   2. Only when it is not already on screen. A fold that fits below the thing just tapped
   *      needs nothing, and scrolling anyway moves the page for no reason at all.
   *   3. Never against the customer. Any wheel, touch or key from just before the tap through
   *      to the scroll means she is already going somewhere, and this yields without argument.
   *      See `lastUserScrollAt`, which lives outside the component precisely so it is already
   *      listening when the tap arrives.
   */
  useEffect(() => {
    if (!shown) return;
    const el = box.current;
    if (!el) return;

    let done = false;

    const bring = () => {
      if (done) return;
      /*
       * ⚠️ THE WINDOW REACHES BACK BEFORE THE TAP. A flick and a tap land within a few
       * milliseconds of each other, and which one the browser reports first is not something
       * to build on. 250ms of grace either side of the opening means "she was already moving"
       * is answered the same way however the two events happened to interleave.
       */
      if (lastUserScrollAt > openedAt.current - 250) return;
      done = true;
      if (!userHasActed) return; // opened by the page loading, not by her — see userHasActed
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Already readable where it is.
      if (r.top >= 0 && r.bottom <= vh) return;
      /*
       * ⚠️ REDUCED MOTION GETS THE SCROLL, NOT THE ANIMATION. Landing on the new section is
       * what the tap was for; the gliding is the decoration, and only the decoration goes.
       */
      const smooth = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      /*
       * ⚠️ `start`, NOT `nearest`. Six sizes are taller than a phone screen, and for anything
       * bigger than the viewport `nearest` picks whichever edge is closer to align — which for
       * a section opening below you is the BOTTOM, leaving its first rows scrolled off above.
       * The whole point is to arrive at the beginning of the new thing. `scroll-margin-top` on
       * `.smup-fold` keeps the card she just tapped in view above it, so landing here still
       * says what it is answering.
       */
      el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
    };

    const onEnd = (e: TransitionEvent) => {
      // The lift and the rows both finish here; the height is the one worth waiting for.
      if (e.propertyName === 'grid-template-rows') bring();
    };
    el.addEventListener('transitionend', onEnd);
    /*
     * The backstop. `transitionend` does not fire when the transition is interrupted, when the
     * height happens not to change, or under reduced motion where it is over in a millisecond —
     * and a reveal that silently never scrolls is the failure nobody reports.
     */
    const timer = window.setTimeout(bring, 700);

    return () => {
      done = true;
      window.clearTimeout(timer);
      el.removeEventListener('transitionend', onEnd);
    };
  }, [shown]);

  /*
   * Nothing is rendered while closed, rather than rendered and hidden. Content clipped by
   * `overflow: hidden` is still in the tab order and still read aloud, so a keyboard user
   * would tab into sizes that are not on the screen yet.
   */
  if (!open) return null;
  return (
    <div ref={box} className={`smup-fold${shown ? ' smup-fold-open' : ''}`}>
      <div>
        <div className="smup-lift">{children}</div>
      </div>
    </div>
  );
}

/**
 * "Would you like the same as last time?" (2026-09-08.)
 *
 * ⚠️ IT NAMES EVERY LINE, WHICH IS THE ONLY THING THAT MAKES "YES" SAFE TO PRESS. "The same as
 * last time" with nothing itemised asks somebody to agree to a figure from memory — and the
 * add-ons are exactly what she will not remember, which is how a customer arrives expecting a
 * $110 groom and is handed a bill for $145. Read as a list, "yes" is informed consent; read as
 * a phrase, it is a guess.
 *
 * ⚠️ AND IT IS NEVER THE ONLY WAY FORWARD. "Show me the list" is a peer of the yes, not a link
 * hiding underneath it: a dog whose coat has changed, a customer who wants a bath this time
 * and a groom next, or simply somebody who wants to see what else there is.
 */
function SamePackageOffer({
  pet,
  service,
  tier,
  addons,
  onYes,
  onShowList,
  accentColor,
  onAccent,
  textColor,
}: {
  pet: string;
  service: Service | null;
  tier: Tier | null;
  addons: Addon[];
  onYes: () => void;
  onShowList: () => void;
  accentColor: string;
  onAccent: string;
  textColor: string;
}) {
  if (!service) return null;
  return (
    <div className="rounded-xl p-4" style={{ border: `1px solid ${hairline(textColor)}` }}>
      <p className="font-semibold">
        {/* Her dog by name: this is the one screen where we have earned the right to use it. */}
        Same as last time for {pet || 'them'}?
      </p>
      <ul className="mt-2 space-y-1 text-sm" style={{ color: textColor }}>
        <li className="flex items-baseline justify-between gap-3">
          <span className="font-medium">{service.name}</span>
          {!isFree(service) && <span className="opacity-70">{cardPriceLabel(service)}</span>}
        </li>
        {tier && (
          <li className="flex items-baseline justify-between gap-3 opacity-80">
            <span>{tier.label}</span>
            <span className="opacity-70">
              {tier.priceMaxCents != null && tier.priceMaxCents !== tier.priceCents
                ? `${money(tier.priceCents)}–${money(tier.priceMaxCents)}`
                : service.priceIsEstimate
                  ? `${money(tier.priceCents)}+`
                  : money(tier.priceCents)}
            </span>
          </li>
        )}
        {addons.map((a) => (
          <li key={a.id} className="flex items-baseline justify-between gap-3 opacity-80">
            <span>+ {a.name}</span>
            <span className="opacity-70">
              {a.priceMaxCents != null && a.priceMaxCents !== a.priceCents
                ? `${money(a.priceCents)}–${money(a.priceMaxCents)}`
                : money(a.priceCents)}
              {/* The unit, always — "$1–$2" and "$1–$2 per minute" are different promises. */}
              {a.priceUnit === 'minute' && <span className="opacity-75"> /min</span>}
            </span>
          </li>
        ))}
      </ul>
      <button
        onClick={onYes}
        className="mt-4 w-full rounded-lg px-4 py-3 font-semibold transition"
        style={{ backgroundColor: accentColor, color: onAccent }}
      >
        Yes, book the same again
      </button>
      <button
        onClick={onShowList}
        className="mt-2 w-full rounded-lg px-4 py-2.5 text-sm font-medium transition"
        style={{ border: `1px solid ${hairline(textColor)}`, color: textColor }}
      >
        Show me the list
      </button>
    </div>
  );
}

function StepServices({
  site,
  services,
  selected,
  tier,
  addonIds,
  quote,
  recurrence,
  onSelect,
  onTier,
  onToggleAddon,
  onRecurrence,
  onNext,
  onAccent,
  rebookToken,
  onRecognised,
  onPet,
  petName,
  recalledTier,
  petLookupSettled,
  asksFirstVisit,
  hasPetNameQuestion,
  firstVisit,
  onFirstVisit,
  lastPackage,
  onSamePackage,
}: {
  site: Site;
  /** What she typed, and the size we got back for it. */
  onPet: (name: string) => void;
  petName: string;
  /** The size we recognised for the typed name, or null. See the note where it renders. */
  recalledTier: string | null;
  /** True once a lookup for the CURRENT name has come back — so a miss can be distinguished
   *  from "we have not asked yet", which is what every keystroke looks like otherwise. */
  petLookupSettled: boolean;
  /** False when the operator has not marked which question names a pet — see 0056. */
  hasPetNameQuestion: boolean;
  /** Set once a returning customer has been recognised here (0056). */
  rebookToken: string | null;
  /** Match found: the token, and the fact that she is not a first visit. */
  onRecognised: (token: string) => void;
  /** Hidden entirely when the tenant asks nothing that depends on it — see `Flow`. */
  asksFirstVisit: boolean;
  /**
   * Reveal layout: new or returning is settled HERE, once, and step two never asks again.
   * Null until she says. Unused by the classic layout, which leaves the question to step two.
   */
  firstVisit: boolean | null;
  onFirstVisit: (v: boolean) => void;
  /** What this pet was last booked in for, or null. Reveal layout only. */
  lastPackage: LastPackage | null;
  onSamePackage: (service: Service, tier: Tier | null, addonIds: string[]) => void;
  /**
   * What to LIST — normally every service, or just one when a `?service=` link named it.
   *
   * ⚠️ SEPARATE FROM `site.services` ON PURPOSE. Everything else on this screen still reads
   * the full set from `site`, because pricing, tiers and add-ons must not change shape based
   * on how she arrived. Only the list she chooses from narrows.
   */
  services: Service[];
  selected: Service | null;
  tier: Tier | null;
  addonIds: string[];
  quote: Estimate | null;
  recurrence: Recurrence;
  onSelect: (s: Service) => void;
  onTier: (t: Tier) => void;
  onToggleAddon: (id: string) => void;
  onRecurrence: (r: Recurrence) => void;
  onNext: () => void;
  onAccent: string;
}) {
  const { accentColor, textColor } = site.branding;
  /*
   * A tiered card cannot be booked until a size is picked. The engine returns "no figure"
   * rather than zero for that state and the API refuses it outright, so blocking here is
   * what turns a 400 into a control she has not used yet.
   */
  /*
   * ⚠️ RESET ON EVERY EDIT, so correcting a typo clears the message immediately rather than
   * leaving "we don't have a pet called Bel" under a field that now reads Bella.
   */
  const [blurred, setBlurred] = useState(false);
  useEffect(() => setBlurred(false), [petName]);

  const needsTier = selected?.tiered === true && tier === null;
  /*
   * Reveal layout: the customer asked to see the other services again. It does NOT clear
   * the selection — the list comes back with the current one still chosen, so there is never
   * a moment where the step holds no service and the button changes its mind about why it is
   * disabled. Picking anything (the same one included) collapses it again.
   */
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [selected?.id]);
  /*
   * The list waits on new-or-returning, because the answer changes what the list
   * is for — a regular is about to have her dog's size recalled onto it.
   *
   * ⚠️ ONLY WHERE THE QUESTION EXISTS. A tenant who asks nothing and takes no deposit has no
   * `asksFirstVisit`, so there is nothing to wait for and the services must not be gated
   * behind a question that is never rendered.
   */
  /*
   * ⚠️ THE LIST WAITS FOR THE WHOLE OF THE REGULAR'S PATH, NOT JUST THE END OF IT. This first
   * shipped keyed on the token, so it only began holding once the number had matched — leaving
   * the entire catalogue on screen between "yes, I'm a regular" and being found, which is
   * exactly the stretch where she is about to be offered her last package instead (Mike,
   * 2026-09-08, on the deployed page).
   *
   * Two waits, one after the other. Before a match: she is typing her number. After it: which
   * dog, because a household with two has two different last packages, and the catalogue
   * appearing and then vanishing under her is the thing being avoided.
   *
   * ⚠️ A MISS ENDS THE WAIT. A number we cannot find, a rate limit, a dropped connection — all
   * of them mean no offer is coming, and holding the prices back for something that will never
   * arrive is worse than never having held them.
   *
   * ⚠️ AND THERE IS ALWAYS A WAY PAST IT. A new puppy, a spelling we will not match, or simply
   * not wanting to type: without the skip this is a field standing between a returning customer
   * and every price on the page.
   */
  const [petSkipped, setPetSkipped] = useState(false);
  const [lookupSettled, setLookupSettled] = useState(false);
  useEffect(() => setPetSkipped(false), [rebookToken]);
  const waitingOnRegular =
    REVEAL_MODE &&
    firstVisit === false &&
    !petSkipped &&
    (rebookToken === null
      ? !lookupSettled
      : hasPetNameQuestion && !petLookupSettled);

  const gateOnFirstVisit = REVEAL_MODE && asksFirstVisit;
  const servicesReady = (!gateOnFirstVisit || firstVisit !== null) && !waitingOnRegular;
  /** The heading is carrying the question, so the buttons below must not repeat it. */
  const askingFirstVisit = gateOnFirstVisit && firstVisit === null;

  /*
   * ── "The same as last time" ─────────────────────────────────────────────
   *
   * The snapshot from the lookup, matched onto what she sells TODAY. Everything is matched by
   * the text that was sold, because that is what a booking stores — see the note on
   * `bookings.addons`. Anything renamed or retired since simply does not match and leaves the
   * offer, rather than the offer naming something that is no longer bookable.
   */
  const samePackage = useMemo(() => {
    if (!REVEAL_MODE || !lastPackage) return null;
    const svc = services.find((x) => x.id === lastPackage.serviceId);
    if (!svc) return null;
    const t =
      lastPackage.tierLabel
        ? (svc.tiers.find(
            (x) => x.label.toLowerCase() === lastPackage.tierLabel!.toLowerCase(),
          ) ?? null)
        : null;
    /*
     * ⚠️ A TIER THAT NO LONGER EXISTS MUST NOT SILENTLY BECOME "NO SIZE". The offer would then
     * read as a complete package and hand the customer a service still needing one, with the
     * Continue button refusing and nothing on screen saying why. If she was sold a size and we
     * cannot find it again, there is no honest "same as last time" to offer.
     */
    if (svc.tiered && !t) return null;
    const addons = svc.addons.filter((a) =>
      lastPackage.addonNames.some((n) => n.toLowerCase() === a.name.toLowerCase()),
    );
    return { service: svc, tier: t, addons };
  }, [lastPackage, services]);

  /** Pressed "show me the list": the offer stands down for the rest of this visit. */
  const [listAsked, setListAsked] = useState(false);
  /** Pressed "change" on a size that came from an accepted package. */
  const [sizeReopened, setSizeReopened] = useState(false);
  useEffect(() => setSizeReopened(false), [selected?.id]);
  useEffect(() => setListAsked(false), [lastPackage]);
  const offeringPackage = samePackage !== null && !selected && !listAsked;

  /*
   * ⚠️ SHE HAS ALREADY AGREED TO THE SIZE, SO STOP ASKING FOR IT (Mike, 2026-09-10). "Same as
   * last time" names the size on its own line and she pressed yes to that. Opening the full
   * ladder underneath is the page re-asking a question it just got an answer to — and worse,
   * a list of six with one highlighted reads as "pick one", not as "this is settled".
   *
   * ⚠️ NOT A VALUE MATCH ALONE — `!listAsked` IS LOAD-BEARING. A customer who declined the
   * offer and went to the catalogue may well have gone there to change the size; the recalled
   * tier preselects for her there, and hiding the ladder because it happens to equal last
   * time's would take away the very thing she asked to see.
   *
   * Extras are deliberately NOT settled the same way: they are the part of a repeat groom
   * somebody actually varies — no nail grind this time, add a de-shed — so they stay open.
   */
  const sizeSettledByPackage =
    samePackage !== null &&
    !listAsked &&
    !sizeReopened &&
    selected?.id === samePackage.service.id &&
    tier != null &&
    tier.id === samePackage.tier?.id;

  if (services.length === 0) {
    return (
      <Card bg={site.branding.backgroundColor} text={textColor}>
        <p className="opacity-75">Nothing is bookable online just yet.</p>
      </Card>
    );
  }
  return (
    <div>
      {/*
        ⚠️ THE HEADING NAMES THE QUESTION ON THE SCREEN. The first control on the screen is
        not about services, and "What do you need?" over two buttons reading "No, first time"
        and "Yes, I'm a regular" is a heading answering a question nobody asked. It changes back
        the moment the services arrive, because by then it is true again.
      */}
      <h1 className="mb-1 text-2xl font-bold">
        {askingFirstVisit
          ? `Have you booked with ${site.business.name} before?`
          : 'What do you need?'}
      </h1>
      {/*
        ⚠️ THE INSTRUCTION TRAVELS WITH THE THING IT INSTRUCTS. The first
        control on the screen asks whether she has booked before, and "Pick a service to get
        started" sitting above it tells her to do something that is not there yet. It moves
        inside the reveal, so it arrives with the list.
      */}
      {!gateOnFirstVisit && <p className="mb-5 opacity-70">Pick a service to get started.</p>}
      {/*
        ⚠️ A "BOOK THIS" TAP IS ACKNOWLEDGED (2026-10-12). From a landing-page card the service
        is already chosen, but the first-visit question was all she saw, so the tap looked like
        it had been ignored.
      */}
      {askingFirstVisit && selected && (
        <p className="mb-4 opacity-70">You picked {selected.name}. Answer this, then choose the details.</p>
      )}

      {/*
        ⚠️ AN OFFER, NOT A GATE, AND THAT IS THE WHOLE DESIGN (0056). Asking "new or
        returning?" as its own screen would put admin in front of the only page that sells —
        a stranger from an ad needs to see what this costs, not answer a question — and would
        add a step for the first-timers who are the majority and gain nothing from it.
        Here a regular self-selects in one tap and everybody else's eye slides past it to the
        prices.

        ⚠️ AND IT SITS ABOVE THE LIST FOR A SECOND REASON. Recognising her HERE means the
        questions she has already answered are filtered before they are ever rendered; doing
        it on the questions step, as this first shipped, briefly showed her a list that then
        got shorter underneath her.
      */}
      {asksFirstVisit && (
        <div className="mb-5 space-y-3">
          {REVEAL_MODE ? (
            /*
              ⚠️ THE QUESTION COMES FIRST, AND STEP TWO NEVER REPEATS IT (Mike,
              2026-09-08). Both screens carried it before: an offer here, a required question
              there — so anyone who let their eye slide past the offer, which is exactly what
              it was designed to allow, answered the same thing twice.

              ⚠️ THIS KNOWINGLY REVERSES 0056'S "AN OFFER, NOT A GATE", whose reasoning is
              directly below and still governs the shipped layout. One tap now stands between
              a stranger from an ad and the prices. That cost is the thing being tried out,
              and it is why this lives behind a URL switch rather than in front of customers.
            */
            <>
              {/*
                ⚠️ THE LABEL APPEARS ONLY ONCE THE HEADING HAS STOPPED ASKING. Both at once is
                the same sentence twice, one above the other; neither is a heading with two
                unexplained buttons under it. The buttons themselves do not move between the
                two states, so answering does not shift what is under the thumb.
              */}
              {askingFirstVisit ? (
                <div className="flex gap-2" role="group" aria-label="Have you booked with us before?">
                  {FIRST_VISIT_CHOICES.map(([label, isFirst]) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => onFirstVisit(isFirst)}
                      className="flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition"
                      style={
                        firstVisit === isFirst
                          ? { backgroundColor: accentColor, color: onAccent }
                          : { border: `1px solid ${hairline(textColor)}`, color: textColor }
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ) : (
                <Field
                  id="q-first-visit"
                  label={`Have you booked with ${site.business.name} before?`}
                  text={textColor}
                >
                  <div className="flex gap-2">
                    {FIRST_VISIT_CHOICES.map(([label, isFirst]) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => onFirstVisit(isFirst)}
                        className="flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition"
                        style={
                          firstVisit === isFirst
                            ? { backgroundColor: accentColor, color: onAccent }
                            : { border: `1px solid ${hairline(textColor)}`, color: textColor }
                        }
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </Field>
              )}

              {/* Not compact: she has just said she is a regular, so the field belongs open. */}
              <Fold open={firstVisit === false}>
                <RebookLookup
                  token={rebookToken}
                  onToken={(t) => t && onRecognised(t)}
                  /* A number we cannot find means no offer is coming — stop holding the list. */
                  onSettled={() => setLookupSettled(true)}
                  textColor={textColor}
                  accentColor={accentColor}
                  onAccent={onAccent}
                />
                {/*
                  ⚠️ THE SAME ESCAPE, ONE STEP EARLIER. While the number is what the page is
                  waiting on, this is the way past it — a regular who would rather just look at
                  the list than be looked up should not have to type a phone number to see one.
                */}
                {waitingOnRegular && rebookToken === null && (
                  <button
                    onClick={() => setPetSkipped(true)}
                    className="mt-2 text-sm underline"
                    style={{ color: textColor, opacity: 0.7 }}
                  >
                    Skip — just show me the services
                  </button>
                )}
              </Fold>

              <Fold open={rebookToken !== null && hasPetNameQuestion}>
                <Field
                  label="Who is this visit for?"
                  /* Once we have confirmed the size, saying why we asked is a line of clutter. */
                  hint={recalledTier ? undefined : 'Their name, so we can remember their size.'}
                  text={textColor}
                >
                  <Input
                    value={petName}
                    onChange={onPet}
                    /*
                     * ⚠️ THE MISS IS REPORTED ON BLUR, NOT ON CHANGE. "Bel" is not found either,
                     * and telling somebody we have no pet by that name while they are still
                     * spelling it is worse than saying nothing at all. A match still confirms
                     * immediately — being told you were recognised early is only ever welcome.
                     */
                    onBlur={() => setBlurred(true)}
                    placeholder="Bella"
                  />
                  {/*
                    ⚠️ CONFIRMATION ON A MATCH, AND SILENCE ON A MISS — the asymmetry is the whole
                    point (Mike, 2026-09-08). A customer who typed "Bela" under a banner reading
                    "Welcome back" had nothing at all to tell her something was off, picked a size
                    by hand, and left a dog the groomer has never heard of in the record. Naming
                    what we found means its ABSENCE is the signal, without the page ever telling
                    somebody that is not their dog — which it cannot know and would be a strange
                    thing to say about a puppy they got last week.

                    ⚠️ IT NAMES THE SIZE, which is no new disclosure: she is about to see it
                    preselected on the very next screen. Saying it here is what makes a dog that
                    has grown obvious while she can still do something about it.
                  */}
                  {/*
                    ⚠️ NOT WHILE THE OFFER IS UP. The card below names the same size on its own
                    line, and confirming it twice, two lines apart, reads as two different
                    facts about the dog.
                  */}
                  {recalledTier && !offeringPackage && (
                    <p className="mt-1.5 text-xs font-medium" style={{ color: accentColor }}>
                      {/* Not parenthesised: her tier labels already carry their own — "usual
                          size (Medium (21–50 lbs))" is what that produced. */}
                      Got it — we’ll use {petName.trim()}’s usual size: {recalledTier}.
                    </p>
                  )}
                  {/*
                    ⚠️ IT OFFERS BOTH READINGS, because the page cannot tell them apart: a
                    misspelling and a puppy they got last week look identical from here. Naming
                    only the first would tell somebody their new dog does not exist.
                  */}
                  {blurred && petLookupSettled && !recalledTier && petName.trim().length >= 2 && (
                    <p className="mt-1.5 text-xs" style={{ color: textColor, opacity: 0.75 }}>
                      We don’t have a pet called <span className="font-medium">{petName.trim()}</span>{' '}
                      on file. Check the spelling — or if this one is new to us, just carry on and
                      pick their service below.
                    </p>
                  )}
                </Field>
                {/*
                  ⚠️ THE ESCAPE FROM THE FIELD ABOVE, and it exists because that field now
                  holds the prices back. A new puppy, a spelling we will not match, or somebody
                  who simply does not want to type — all of them must be able to get to the
                  list, and none of them should have to guess how.
                */}
                {waitingOnRegular && (
                  <button
                    onClick={() => setPetSkipped(true)}
                    className="mt-2 text-sm underline"
                    style={{ color: textColor, opacity: 0.7 }}
                  >
                    Skip — just show me the services
                  </button>
                )}
              </Fold>
            </>
          ) : (
            <>
              <RebookLookup
                token={rebookToken}
                /* Only ever called with a token: there is no longer a control that clears one. */
                onToken={(t) => t && onRecognised(t)}
                textColor={textColor}
                accentColor={accentColor}
                onAccent={onAccent}
                compact
              />

              {rebookToken && hasPetNameQuestion && (
                <Field
                  label="Who is this visit for?"
                  /* Once we have confirmed the size, saying why we asked is a line of clutter. */
                  hint={recalledTier ? undefined : 'Their name, so we can remember their size.'}
                  text={textColor}
                >
                  <Input
                    value={petName}
                    onChange={onPet}
                    /*
                     * ⚠️ THE MISS IS REPORTED ON BLUR, NOT ON CHANGE. "Bel" is not found either,
                     * and telling somebody we have no pet by that name while they are still
                     * spelling it is worse than saying nothing at all. A match still confirms
                     * immediately — being told you were recognised early is only ever welcome.
                     */
                    onBlur={() => setBlurred(true)}
                    placeholder="Bella"
                  />
                  {/*
                    ⚠️ CONFIRMATION ON A MATCH, AND SILENCE ON A MISS — the asymmetry is the whole
                    point (Mike, 2026-09-08). A customer who typed "Bela" under a banner reading
                    "Welcome back" had nothing at all to tell her something was off, picked a size
                    by hand, and left a dog the groomer has never heard of in the record. Naming
                    what we found means its ABSENCE is the signal, without the page ever telling
                    somebody that is not their dog — which it cannot know and would be a strange
                    thing to say about a puppy they got last week.

                    ⚠️ IT NAMES THE SIZE, which is no new disclosure: she is about to see it
                    preselected on the very next screen. Saying it here is what makes a dog that
                    has grown obvious while she can still do something about it.
                  */}
                  {recalledTier && (
                    <p className="mt-1.5 text-xs font-medium" style={{ color: accentColor }}>
                      {/* Not parenthesised: her tier labels already carry their own — "usual
                          size (Medium (21–50 lbs))" is what that produced. */}
                      Got it — we’ll use {petName.trim()}’s usual size: {recalledTier}.
                    </p>
                  )}
                  {/*
                    ⚠️ IT OFFERS BOTH READINGS, because the page cannot tell them apart: a
                    misspelling and a puppy they got last week look identical from here. Naming
                    only the first would tell somebody their new dog does not exist.
                  */}
                  {blurred && petLookupSettled && !recalledTier && petName.trim().length >= 2 && (
                    <p className="mt-1.5 text-xs" style={{ color: textColor, opacity: 0.75 }}>
                      We don’t have a pet called <span className="font-medium">{petName.trim()}</span>{' '}
                      on file. Check the spelling — or if this one is new to us, just carry on and
                      pick their service below.
                    </p>
                  )}
                </Field>
              )}
            </>
          )}
        </div>
      )}

      {/*
        ⚠️ TWO LAYOUTS, ONE SET OF PARTS. The reveal layout renders the chosen service promoted with
        everything else folded away and revealed in turn; without it this is exactly the list
        that has been taking her bookings since August. Both call the same TierList, AddonList
        and RecurrenceChips, so her catalogue has one rendering and the two layouts cannot
        quietly disagree with production about what a service costs.
      */}
      {/*
        ⚠️ THE OFFER REPLACES THE LIST, IT DOES NOT SIT ABOVE IT. A returning customer who is
        about to book the same thing again does not need her groomer's whole catalogue read out
        first — and an offer with the list already open underneath is just a longer list. She
        says yes, or she asks for the list and gets it.
      */}
      <Reveal open={servicesReady && offeringPackage}>
        <SamePackageOffer
          pet={petName.trim()}
          service={samePackage?.service ?? null}
          tier={samePackage?.tier ?? null}
          addons={samePackage?.addons ?? []}
          onYes={() => {
            if (samePackage) {
              onSamePackage(
                samePackage.service,
                samePackage.tier,
                samePackage.addons.map((a) => a.id),
              );
            }
          }}
          onShowList={() => setListAsked(true)}
          accentColor={accentColor}
          onAccent={onAccent}
          textColor={textColor}
        />
      </Reveal>

      <Reveal open={servicesReady && !offeringPackage}>
      {gateOnFirstVisit && !selected && (
        <p className="mb-4 opacity-70">Pick a service to get started.</p>
      )}
      {REVEAL_MODE && selected && !showAll ? (
        <div className="space-y-3">
          {/*
            The chosen service, promoted and always in the selected style — there is nothing
            else on screen for it to be distinguished FROM. Pressing it reopens the list, which
            is the same gesture as the "Or:" line below and the reason both exist.
          */}
          <button
            onClick={() => setShowAll(true)}
            aria-label={`${selected.name} — choose a different service`}
            className="w-full rounded-xl p-4 text-left transition"
            style={{ backgroundColor: accentColor, color: onAccent }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold">{selected.name}</div>
                {selected.description && (
                  <div className="mt-0.5 text-sm opacity-75">{selected.description}</div>
                )}
                <div className="mt-1 text-sm opacity-75">
                  {durationLabel(selected.durationMinutes)}
                </div>
              </div>
              {!isFree(selected) && (
                <div className="shrink-0 text-right">
                  <div className="text-lg font-bold">{cardPriceLabel(selected)}</div>
                  {selected.pricingMode === 'quote' ? (
                    <div className="text-xs opacity-75">after we see it</div>
                  ) : (
                    /*
                      ⚠️ "starting at" IS NOT DECORATION. It is the difference between a price
                      and a bait price, and it goes on the card itself rather than only in the
                      fine print — the card is what she screenshots and sends to her husband.
                    */
                    selected.priceIsEstimate && (
                      <div className="text-xs opacity-75">starting at</div>
                    )
                  )}
                </div>
              )}
            </div>
          </button>

          {/*
            ⚠️ COLLAPSED, NOT HIDDEN, AND THE NAMES STAY VISIBLE. "Change service" alone makes
            somebody open the list again purely to remember what else there was. This also
            deliberately does not clear the selection — it re-shows the list with the current
            one still chosen, so there is no moment where the flow holds no service.
          */}
          {services.length > 1 && (
            <button
              onClick={() => setShowAll(true)}
              className="block w-full text-left text-sm underline"
              style={{ color: textColor, opacity: 0.7 }}
            >
              Or: {services.filter((x) => x.id !== selected.id).map((x) => x.name).join(', ')}
            </button>
          )}

          {/*
            ⚠️ KEYED ON THE SERVICE so switching from a full groom to a bath replays the
            reveal with the new sizes, rather than swapping the rows underneath a section that
            is already open and looks settled.
          */}
          {sizeSettledByPackage && tier ? (
            /*
              One line, and it still says the size — "size: change" alone would make somebody
              open the ladder purely to remember what they agreed to. Same shape as the "Or:"
              line above, because it is the same idea: settled, visible, one tap from open.
            */
            <button
              onClick={() => setSizeReopened(true)}
              className="block w-full text-left text-sm"
              style={{ color: textColor, opacity: 0.7 }}
            >
              Size: <span className="font-medium">{tier.label}</span> ·{' '}
              <span className="underline">change</span>
            </button>
          ) : (
            <Fold key={`tiers-${selected.id}`} open={selected.tiered}>
              <TierList
                service={selected}
                tier={tier}
                onTier={onTier}
                accentColor={accentColor}
                onAccent={onAccent}
                textColor={textColor}
              />
            </Fold>
          )}

          {/*
            ⚠️ `!needsTier`, NOT "a tier is chosen". A service with no sizes at all never had
            a size to wait for, and gating on `tier !== null` would leave a nail trim showing
            nothing but its own name for ever.
          */}
          <Fold key={`rest-${selected.id}`} open={!needsTier}>
            {/* No space-y here: it zeroed the mt-2 / mt-3 the note and the repeat chips set for
                themselves, and the chips sat flush against the last add-on card. */}
            <div>
              {selected.addons.length > 0 && (
                <AddonList
                  service={selected}
                  addonIds={addonIds}
                  onToggleAddon={onToggleAddon}
                  accentColor={accentColor}
                  onAccent={onAccent}
                  textColor={textColor}
                />
              )}
              {selected.priceIsEstimate && selected.priceNote && (
                <p className="mt-2 px-1 text-sm opacity-70">{selected.priceNote}</p>
              )}
              {selected.recurringCapable && (
                <RecurrenceChips
                  site={site}
                  recurrence={recurrence}
                  onRecurrence={onRecurrence}
                  accentColor={accentColor}
                  onAccent={onAccent}
                  textColor={textColor}
                />
              )}
            </div>
          </Fold>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((s) => {
            const isSelected = selected?.id === s.id;
            return (
              <div key={s.id}>
                <button
                  onClick={() => onSelect(s)}
                  className="w-full rounded-xl p-4 text-left transition"
                  style={{
                    backgroundColor: isSelected
                      ? accentColor
                      : isDark(site.branding.backgroundColor)
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.035)',
                    color: isSelected ? onAccent : textColor,
                    outline: isSelected ? `2px solid ${accentColor}` : 'none',
                  }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold">{s.name}</div>
                      {s.description && <div className="mt-0.5 text-sm opacity-75">{s.description}</div>}
                      <div className="mt-1 text-sm opacity-75">{durationLabel(s.durationMinutes)}</div>
                    </div>
                    {/*
                      Price on every card that HAS one. Nobody books a $45 lawn cut without
                      seeing $45 — and nobody needs to be told a free call costs $0.
                    */}
                    {!isFree(s) && (
                    <div className="shrink-0 text-right">
                      <div className="text-lg font-bold">{cardPriceLabel(s)}</div>
                      {s.pricingMode === 'quote' ? (
                        <div className="text-xs opacity-75">after we see it</div>
                      ) : (
                        /*
                          ⚠️ "starting at" IS NOT DECORATION. It is the difference between a
                          price and a bait price, and it goes on the card itself rather than
                          only in the fine print — the card is what she screenshots and sends
                          to her husband.
                        */
                        s.priceIsEstimate && <div className="text-xs opacity-75">starting at</div>
                      )}
                    </div>
                    )}
                  </div>
                </button>

                {/*
                  Sizes, on the SELECTED card only. Shown inline rather than as a step of their
                  own for the same reason the recurring chips are: it is one decision about one
                  card, and a whole screen to answer "how big is your dog" reads as a form.
                */}
                {isSelected && s.tiered && (
                  <TierList
                    service={s}
                    tier={tier}
                    onTier={onTier}
                    accentColor={accentColor}
                    onAccent={onAccent}
                    textColor={textColor}
                  />
                )}

                {/* Extras. Multi-select, because they genuinely combine. */}
                {isSelected && s.addons.length > 0 && (
                  <AddonList
                    service={s}
                    addonIds={addonIds}
                    onToggleAddon={onToggleAddon}
                    accentColor={accentColor}
                    onAccent={onAccent}
                    textColor={textColor}
                  />
                )}

                {/* Her sentence about why the figure can move, under the card it explains. */}
                {isSelected && s.priceIsEstimate && s.priceNote && (
                  <p className="mt-2 px-1 text-sm opacity-70">{s.priceNote}</p>
                )}

                {/*
                  Recurring is an inline chip row on the SELECTED card, not a separate step
                  (#48). It is the wedge — Calendly handles it badly and Jobber charges for
                  it — so it belongs in the primary flow rather than buried in settings.
                */}
                {isSelected && s.recurringCapable && (
                  <RecurrenceChips
                    site={site}
                    recurrence={recurrence}
                    onRecurrence={onRecurrence}
                    accentColor={accentColor}
                    onAccent={onAccent}
                    textColor={textColor}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
      </Reveal>

      {/*
        Page-level fine print — "Travel is included in all prices. No hidden fees." Under the
        whole list rather than on any one card, because it is true of all of them.
      */}
      {/*
        ⚠️ NOT WHILE THERE ARE NO PRICES ON THE SCREEN. The first thing shown
        is a question about bookings, and "Travel is included in all prices" underneath it is a
        promise about a list she has not been shown, answering nothing she was asked.
      */}
      {site.settings.pricingNote && servicesReady && (
        <p className="mt-4 text-sm opacity-60">{site.settings.pricingNote}</p>
      )}

      {/*
        The running total. It appears only once there is something to total — a figure that
        sits at $0 while she reads the list would be the exact "price the operator is on the
        hook for" that the schema goes out of its way to prevent.
      */}
      {/*
        ⚠️ AND ZERO IS NOTHING TO TOTAL EITHER. The condition used to be `minCents != null`,
        which the comment above already argued against without excluding the one value that
        makes the point — a free service produced a "Total $0" row sitting under a card that
        deliberately shows no price. A range that merely STARTS at zero is still a total and
        still shown.
      */}
      {selected && quote && quote.minCents != null &&
        !(quote.minCents === 0 && (quote.maxCents == null || quote.maxCents === 0)) && (
        <div
          className="mt-5 rounded-xl px-4 py-3"
          style={{ border: `1px solid ${hairline(textColor)}` }}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm opacity-70">
              {quote.isEstimate ? 'Estimated total' : 'Total'}
            </span>
            <span className="text-xl font-bold">{formatEstimate(quote)}</span>
          </div>
          {quote.discountCents > 0 && (
            <div className="mt-1 text-sm opacity-70">
              Includes {money(quote.discountCents)} off for booking{' '}
              {RECURRENCE_LABELS[recurrence].toLowerCase()}
            </div>
          )}
          {quote.durationMinutes !== selected.durationMinutes && (
            <div className="mt-1 text-sm opacity-70">
              About {durationLabel(quote.durationMinutes)} in total
            </div>
          )}
          <UnpricedAddonNote addons={quote.unpricedAddons} />
        </div>
      )}

      <PrimaryButton
        accent={accentColor}
        onAccent={onAccent}
        disabled={!selected || needsTier || askingFirstVisit}
        onClick={onNext}
      >
        {/* The label names what is actually missing: it said "Pick a size" under a question with no sizes on screen. */}
        {askingFirstVisit ? 'Answer above to continue' : needsTier ? 'Pick a size to continue' : 'Continue'}
      </PrimaryButton>
    </div>
  );
}

/**
 * The line that says a per-minute add-on has no total yet.
 *
 * ⚠️ IT IS NOT OPTIONAL POLISH. Dematting at $1–2/min is excluded from every figure on this
 * page — deliberately, because nobody knows the minutes until the dog is on the table — and
 * a customer who ticked it and then read "Estimated total $150" with no further comment has
 * been quietly misled about what she will be asked for.
 */
function UnpricedAddonNote({
  addons,
}: {
  addons: { name: string; priceCents: number; priceMaxCents: number | null }[];
}) {
  if (addons.length === 0) return null;
  return (
    <div className="mt-2 text-sm opacity-70">
      {addons.map((a) => (
        <div key={a.name}>
          {a.name} is charged by the minute (
          {a.priceMaxCents != null && a.priceMaxCents !== a.priceCents
            ? `${money(a.priceCents)}–${money(a.priceMaxCents)}`
            : money(a.priceCents)}
          /min) and is not included above.
        </div>
      ))}
    </div>
  );
}

/**
 * "Where are you bringing your dog?" — the drop-off business's version of the ZIP step (0091).
 *
 * ⚠️ BEFORE THE CALENDAR, for the same reason the ZIP is: she is in Pendleton some days and
 * Starr on others, so the days on offer depend on the answer. The days are shown on each card
 * so nobody picks a location and then wonders why Tuesday is missing.
 *
 * ⚠️ NO ADDRESS HERE. The site payload doesn't carry one (a home salon is her home); the
 * customer gets it on the confirmation screen and in the email once they've booked.
 */
function StepWhere({
  site,
  locations,
  locationId,
  onPick,
}: {
  site: Site;
  locations: SiteLocation[];
  locationId: string | null;
  onPick: (id: string) => void;
}) {
  const { accentColor, textColor, backgroundColor } = site.branding;
  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold">Pick a location</h1>
      <p className="mb-4 opacity-70">
        {site.business.name} works in different places on different days. This decides which
        times you’re offered.
      </p>
      <div className="space-y-3">
        {locations.map((l) => {
          const picked = l.id === locationId;
          return (
            <button
              key={l.id}
              type="button"
              onClick={() => onPick(l.id)}
              className="flex w-full items-start gap-3 rounded-2xl border p-4 text-left transition"
              style={{
                backgroundColor,
                color: textColor,
                borderColor: picked ? accentColor : hairline(textColor),
                boxShadow: picked ? `0 0 0 1px ${accentColor}` : undefined,
              }}
            >
              <MapPin className="mt-0.5 h-5 w-5 shrink-0" style={{ color: accentColor }} />
              <span className="min-w-0">
                <span className="block font-semibold">{l.name}</span>
                {l.description && <span className="mt-0.5 block text-sm opacity-75">{l.description}</span>}
                <span className="mt-1 block text-xs font-medium opacity-60">{daysLabel(l.days)}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** "Mondays, Tuesdays & Thursdays" — Monday-first, the way a week is read aloud. */
function daysLabel(days: number[]): string {
  const NAMES = ['Sundays', 'Mondays', 'Tuesdays', 'Wednesdays', 'Thursdays', 'Fridays', 'Saturdays'];
  const ordered = [1, 2, 3, 4, 5, 6, 0].filter((d) => days.includes(d)).map((d) => NAMES[d]!);
  if (ordered.length === 0) return 'No days set yet';
  if (ordered.length === 7) return 'Every day';
  if (ordered.length === 1) return ordered[0]!;
  return `${ordered.slice(0, -1).join(', ')} & ${ordered[ordered.length - 1]}`;
}

/**
 * Where is the job? (#135)
 *
 * ⚠️ IT ASKS BEFORE THE CALENDAR because the answer changes which days exist. A groomer
 * drives: she is in Easley on Tuesdays and Anderson on Wednesdays, and offering a Wednesday
 * to somebody in Easley is how a day falls apart.
 *
 * ⚠️ AN UNSERVED POSTCODE IS NOT A DEAD END. That is the whole reason this screen exists
 * rather than a silent empty calendar — she captures the enquiry and the customer is told
 * plainly that nothing is booked. Telling somebody "we don't come to you" and nothing else is
 * a customer lost for no reason.
 */
function StepZip({
  site,
  zip,
  area,
  onZip,
  onArea,
  onNext,
  onAccent,
}: {
  site: Site;
  zip: string;
  area: AreaAnswer | null;
  onZip: (v: string) => void;
  onArea: (a: AreaAnswer) => void;
  onNext: () => void;
  onAccent: string;
}) {
  const { accentColor, textColor } = site.branding;
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = /^\d{5}$/.test(zip.trim());

  async function check() {
    if (!valid || checking) return;
    setChecking(true);
    setError(null);
    try {
      const r = await fetch(withTenant(`/api/public/area?zip=${zip.trim()}`));
      if (!r.ok) throw new Error();
      const a = (await r.json()) as AreaAnswer;
      onArea(a);
      // Served — straight on to the calendar. Nothing to read, nothing to press twice.
      if (a.served) onNext();
    } catch {
      setError('We could not check that postcode. Try again.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold">Where are you?</h1>
      <p className="mb-4 opacity-70">
        {site.business.name} covers different areas on different days — this decides which
        times you are offered.
      </p>

      <Card bg={site.branding.backgroundColor} text={textColor}>
        <label className="block text-sm font-medium" htmlFor="zip">
          Your ZIP code
        </label>
        <input
          id="zip"
          value={zip}
          onChange={(e) => onZip(e.target.value.replace(/[^\d]/g, '').slice(0, 5))}
          onKeyDown={(e) => e.key === 'Enter' && void check()}
          inputMode="numeric"
          autoComplete="postal-code"
          placeholder="29621"
          className="mt-1 w-40 rounded-xl border px-3 py-2 text-base outline-none"
          style={{ borderColor: hairline(textColor) }}
        />
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      </Card>

      {/*
        ⚠️ ONLY AFTER SHE HAS ASKED. Rendering "we don't serve you" while she is still typing
        the third digit tells a customer she is not wanted before she has finished answering.
      */}
      {area && !area.served && (
        <div className="mt-4">
          <OutOfArea site={site} zip={zip} accent={accentColor} onAccent={onAccent} />
        </div>
      )}

      {(!area || area.served) && (
        <div className="mt-5">
          <PrimaryButton
            accent={accentColor}
            onAccent={onAccent}
            disabled={!valid || checking}
            onClick={() => void check()}
          >
            {checking ? 'Checking…' : 'Continue'}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
}

/**
 * "We don't come out that far — but tell me anyway." (#135, #144)
 *
 * ⚠️ THIS REACHES THE OPERATOR, and the wording has to be honest about what it is. Nothing is
 * booked and no time is held; saying anything warmer than "she will get back to you" is the
 * promise #89 removed the old lead form for.
 */
function OutOfArea({
  site,
  zip,
  accent,
  onAccent,
}: {
  site: Site;
  zip: string;
  accent: string;
  onAccent: string;
}) {
  const { textColor } = site.branding;
  const [form, setForm] = useState({ name: '', phone: '', email: '', whenSuits: '' });
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Same rule the server enforces: a name with no way to reply is not an enquiry.
  const ready = form.name.trim().length > 0 && (form.phone.trim() || form.email.trim());

  async function send() {
    if (!ready || state === 'sending') return;
    setState('sending');
    setError(null);
    try {
      const r = await fetch(withTenant('/api/public/enquiries'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: 'outside_area',
          name: form.name.trim(),
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          zip,
          whenSuits: form.whenSuits.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error();
      setState('sent');
    } catch {
      setState('failed');
      setError('We could not send that. Try again.');
    }
  }

  if (state === 'sent') {
    return (
      <Card bg={site.branding.backgroundColor} text={textColor}>
        <h2 className="text-lg font-semibold">Thanks — she has your details.</h2>
        {/*
          ⚠️ SAYS PLAINLY THAT NOTHING IS BOOKED. A warm message with no such sentence is how
          somebody ends up expecting a groomer who is not coming.
        */}
        <p className="mt-1 text-sm opacity-75">
          Nothing is booked and no time is held — {site.business.name} will get in touch if she
          can cover your area.
        </p>
      </Card>
    );
  }

  return (
    <Card bg={site.branding.backgroundColor} text={textColor}>
      <h2 className="text-lg font-semibold">
        {site.business.name} doesn’t regularly cover {zip}
      </h2>
      <p className="mt-1 text-sm opacity-75">
        Leave your details and she’ll reach out if she can get to you. Nothing is booked.
      </p>
      <div className="mt-3 space-y-2">
        <input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Your name"
          className="w-full rounded-xl border px-3 py-2 text-base outline-none"
          style={{ borderColor: hairline(textColor) }}
        />
        <input
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="Mobile number"
          inputMode="tel"
          className="w-full rounded-xl border px-3 py-2 text-base outline-none"
          style={{ borderColor: hairline(textColor) }}
        />
        <input
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email (optional if you gave a number)"
          inputMode="email"
          className="w-full rounded-xl border px-3 py-2 text-base outline-none"
          style={{ borderColor: hairline(textColor) }}
        />
        <input
          value={form.whenSuits}
          onChange={(e) => setForm({ ...form, whenSuits: e.target.value })}
          placeholder="When suits you? e.g. any Tuesday"
          className="w-full rounded-xl border px-3 py-2 text-base outline-none"
          style={{ borderColor: hairline(textColor) }}
        />
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      <div className="mt-4">
        <PrimaryButton accent={accent} onAccent={onAccent} disabled={!ready || state === 'sending'} onClick={() => void send()}>
          {state === 'sending' ? 'Sending…' : 'Ask her to get in touch'}
        </PrimaryButton>
      </div>
    </Card>
  );
}

// ── Step 2 — pick a day ────────────────────────────────────────────────────

/**
 * How many Mon–Sun rows the day picker shows at once.
 *
 * ⚠️ CHANGING THIS ALSO CHANGES THE PAGE SIZE, deliberately — `Later` advances by exactly
 * this many weeks, which is what keeps every page starting on a Monday.
 */
const WEEKS_SHOWN = 3;

function StepWhen({
  site,
  service,
  tier,
  addonIds,
  pets,
  zip,
  locationId,
  durationMinutes,
  date,
  slot,
  zone,
  held,
  onDate,
  onSlot,
  onNext,
  onAccent,
}: {
  site: Site;
  service: Service;
  tier: Tier | null;
  addonIds: string[];
  /** Every dog on the visit. The grid is asked for the total of their durations (#134). */
  pets: PetSelection[];
  /** The customer's postcode, when it actually filters anything (#135). */
  zip: string;
  /** Her drop-off location (0091). Filters the grid to the days she is there. */
  locationId: string | null;
  /** The length actually ordered — tier plus flat add-ons. Display only; see below. */
  durationMinutes: number;
  date: string | null;
  slot: Slot | null;
  zone: string;
  /** The time the booking assistant held for them, if they came from its link. */
  held?: HeldSlot | null;
  onDate: (d: string) => void;
  onSlot: (s: Slot) => void;
  onNext: () => void;
  onAccent: string;
}) {
  const { accentColor, textColor, backgroundColor } = site.branding;
  const [days, setDays] = useState<DayAvailability[] | null>(null);
  /*
   * How many three-week pages forward. A held day can be weeks out, so the grid opens on the
   * page that contains it rather than making them page forward to find their own appointment.
   */
  const [offset, setOffset] = useState(() => {
    if (!held) return 0;
    const start = mondayOnOrBefore(todayISO());
    const [y1 = 0, m1 = 1, d1 = 1] = start.split('-').map(Number);
    const [y2 = 0, m2 = 1, d2 = 1] = held.date.split('-').map(Number);
    const daysAway = Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86_400_000);
    return Math.max(0, Math.floor(daysAway / (WEEKS_SHOWN * 7)));
  });

  const addonKey = addonIds.join(',');
  const petKey = pets
    .map((p) => `${p.service.id}:${p.tier?.id ?? ''}:${p.addonIds.join('+')}`)
    .join('|');

  useEffect(() => {
    setDays(null);
    /*
     * ⚠️ THE RANGE STARTS ON A MONDAY, NOT ON TODAY (Mike, 2026-08-14). Fourteen consecutive
     * days from today put whatever weekday it happens to be in the first column — on a Friday
     * the grid read F S S M T W T, which is a fortnight, not a calendar. Anchoring to the
     * Monday of the current week makes the seven columns mean Mon…Sun always.
     *
     * Days already past are still requested: the engine marks them `past` and they render
     * greyed, which is what keeps the columns aligned. Dropping them would shift everything
     * left again and undo the whole point.
     */
    /*
     * ⚠️ THREE WEEKS, AND THE NUMBER IS LOAD-BEARING RATHER THAN ARBITRARY. Anchoring to
     * Monday means the current week's past days are still drawn, greyed, to keep the columns
     * aligned — so on a Friday a fortnight spent half its cells on days nobody can book. Three
     * weeks restores roughly the forward visibility a rolling fortnight used to give,
     * whichever day she happens to arrive on.
     *
     * Paging moves by the same 21, so every page still starts on a Monday.
     */
    const from = isoAddDays(mondayOnOrBefore(todayISO()), offset * WEEKS_SHOWN * 7);
    const to = isoAddDays(from, WEEKS_SHOWN * 7 - 1);
    /*
     * ⚠️ THE SELECTION TRAVELS WITH THE QUERY, as ids. A Giant groom with a de-shed is a
     * longer appointment than the bare service, and a grid computed at the short length
     * offers times that cannot hold the booking — she would pick 4pm, fill in three screens,
     * and be told at the last step that it is gone.
     *
     * Ids rather than the duration itself: the server derives the length from its own rows,
     * exactly as it does when accepting the booking, so the grid and the acceptance cannot
     * disagree. `durationMinutes` above is for the label only.
     */
    const params = new URLSearchParams({ serviceId: service.id, from, to });
    if (tier) params.set('tierId', tier.id);
    if (addonKey) params.set('addons', addonKey);
    /*
     * ⚠️ EVERY DOG, AS IDS (#134). The server resolves each duration from its own rows and
     * sums them, exactly as it does when accepting the booking — so the grid and the
     * acceptance cannot disagree. The single-service params above stay for a one-dog visit
     * and for any page still running the pre-#134 bundle.
     */
    if (zip) params.set('zip', zip);
    if (locationId) params.set('location', locationId);
    if (pets.length > 1) {
      params.set(
        'pets',
        JSON.stringify(
          pets.map((p) => ({ serviceId: p.service.id, tierId: p.tier?.id, addonIds: p.addonIds })),
        ),
      );
    }
    fetch(withTenant(`/api/public/availability?${params}`), {
      // Their own hold, so the time kept for them shows as free to them. See the route.
      headers: HOLD_TOKEN ? { 'X-Hold-Token': HOLD_TOKEN } : undefined,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((d) => setDays(d.days ?? []))
      .catch(() => setDays([]));
    // `petKey` rather than `pets`: a new array identity every render would refetch forever.
  }, [service.id, tier?.id, addonKey, petKey, zip, locationId, offset]);

  const selectedDay = days?.find((d) => d.date === date);

  /*
   * Select the held time once the grid has it.
   *
   * ⚠️ INTO AN EMPTY SELECTION ONLY — no time, and either no day or the held day. A day they
   * picked themselves is a newer decision than the hold. An empty one is not: entering a ZIP or
   * changing the size clears the day on purpose, and the held time should come straight back
   * if it still fits. If it no longer does — a size that makes the visit longer, an area she
   * does not cover that day — the grid simply does not have it and nothing is selected.
   */
  useEffect(() => {
    if (!held || slot || (date && date !== held.date)) return;
    const heldDay = days?.find((d) => d.date === held.date);
    const match = heldDay?.slots.find((s) => s.startsAt === held.startsAt);
    if (!match) return;
    if (!date) onDate(held.date);
    onSlot(match);
  }, [held, slot, date, days]);

  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold">Pick a day</h1>
      {/*
        ⚠️ WITH MORE THAN ONE, THIS MUST NOT NAME JUST ONE OF THEM. It read
        "Full Groom · Large · 3 hr 30 min" for a two-item visit — which says a Large full groom
        takes three and a half hours, and she would reasonably conclude the page had
        mispriced her time. The duration is the visit's; so is the description.

        ⚠️ "services", NOT "dogs" (#150, found via #143). This is shared chrome — a detailing
        customer booking two cars was told "2 dogs · 5 hr". The count is of things booked into
        one visit, which is what every trade has; the animal was a groomer's word for it.
      */}
      <p className="mb-4 opacity-70">
        {pets.length > 1
          ? `${pets.length} services · ${durationLabel(durationMinutes)}`
          : `${service.name}${tier ? ` · ${tier.label}` : ''} · ${durationLabel(durationMinutes)}`}
      </p>

      {days === null ? (
        <div className="flex justify-center py-10 opacity-50">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <>
          {/*
            ⚠️ WHICH MONTH THESE NUMBERS BELONG TO. The grid shows bare day numbers, so on any
            page that crosses a month — which a three-week window usually does — "the 3rd"
            meant nothing without scrolling back to a confirmation email to work it out.
          */}
          {days.length > 0 && (
            <p className="mb-2 text-sm font-semibold">
              {monthSpanLabel(days[0]!.date, days[days.length - 1]!.date)}
            </p>
          )}
          <div className="grid grid-cols-7 gap-1.5">
            {days.map((d) => {
              const open = d.slots.length > 0;
              const isSel = d.date === date;
              return (
                <button
                  key={d.date}
                  disabled={!open}
                  onClick={() => onDate(d.date)}
                  className="flex flex-col items-center rounded-lg py-2 text-xs transition disabled:opacity-25"
                  style={
                    isSel
                      ? { backgroundColor: accentColor, color: onAccent }
                      : {
                          backgroundColor: isDark(backgroundColor)
                            ? 'rgba(255,255,255,0.08)'
                            : 'rgba(0,0,0,0.035)',
                          color: textColor,
                        }
                  }
                  aria-label={`${prettyDate(d.date)}${open ? '' : ', unavailable'}`}
                >
                  <span className="opacity-60">{dayLetter(d.date)}</span>
                  <span className="text-base font-semibold">{Number(d.date.slice(8, 10))}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between text-sm">
            <button
              onClick={() => setOffset((o) => Math.max(0, o - 1))}
              disabled={offset === 0}
              className="opacity-70 hover:opacity-100 disabled:opacity-25"
            >
              ← Earlier
            </button>
            <button onClick={() => setOffset((o) => o + 1)} className="opacity-70 hover:opacity-100">
              Later →
            </button>
          </div>

          {date && selectedDay && (
            <div className="mt-6">
              <div className="mb-2 flex items-center gap-2 font-semibold">
                <Calendar className="h-4 w-4" />
                {prettyDate(date)}
              </div>
              {selectedDay.slots.length === 0 ? (
                <p className="opacity-70">No times left on this day.</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {selectedDay.slots.map((s) => {
                      const isSel = slot?.startsAt === s.startsAt;
                      return (
                        <button
                          key={s.startsAt}
                          onClick={() => onSlot(s)}
                          className="rounded-lg py-2.5 text-sm font-medium transition"
                          style={
                            isSel
                              ? { backgroundColor: accentColor, color: onAccent }
                              : {
                                  border: `1px solid ${hairline(textColor)}`,
                                  color: textColor,
                                }
                          }
                        >
                          {prettyTime(s.label)}
                        </button>
                      );
                    })}
                  </div>
                  {/*
                    The zone is STATED, never selectable (#48). A customer two states away
                    still needs to know whose clock this is.
                  */}
                  {zone && <p className="mt-2 text-xs opacity-50">All times {zone}.</p>}
                </>
              )}
            </div>
          )}

          {days.every((d) => d.slots.length === 0) && (
            <p className="mt-6 opacity-70">
              Nothing available in these two weeks. Try looking further ahead.
            </p>
          )}
        </>
      )}

      <PrimaryButton accent={accentColor} onAccent={onAccent} disabled={!slot} onClick={onNext}>
        Continue
      </PrimaryButton>
    </div>
  );
}

// ── Step 3 — who and where ─────────────────────────────────────────────────

function StepDetails({
  site,
  form,
  onForm,
  smsConsent,
  onSmsConsent,
  dropOff,
  onNext,
  onAccent,
}: {
  site: Site;
  form: { name: string; phone: string; email: string; address: string; notes: string };
  onForm: (f: typeof form) => void;
  smsConsent: boolean;
  onSmsConsent: (v: boolean) => void;
  /** Customers come to her (0091), so there is no job address to ask for. */
  dropOff: boolean;
  onNext: () => void;
  onAccent: string;
}) {
  const { accentColor, textColor } = site.branding;
  const set = (k: keyof typeof form) => (v: string) => onForm({ ...form, [k]: v });
  // Questions moved to their own step (0024) — this one is back to being about the person
  // and the address, which is all it was ever meant to hold.
  const ready = form.name.trim().length > 0;
  const phoneOnly = site.business.isInternal === true;

  return (
    <div>
      {/*
        ⚠️ OUR OWN TENANT BOOKS PHONE CALLS, NOT VISITS. Asking a prospect "Where is the job?"
        so we "know where to turn up" for a 15-minute call made no sense (2026-09-18).
      */}
      <h1 className="mb-1 text-2xl font-bold">{phoneOnly || dropOff ? 'Your details' : 'Who and where'}</h1>
      <p className="mb-5 opacity-70">
        {phoneOnly ? 'So we know who to call.' : dropOff ? 'So we know who’s coming.' : 'So we know where to turn up.'}
      </p>

      <div className="space-y-4">
        {/*
          The service address replaces the video-conferencing link a meeting scheduler would
          put here (#48). It is the one thing the operator cannot do the job without.
        */}
        {/* ⚠️ NOT ASKED AT A DROP-OFF BUSINESS (0091): the customer is coming to her. */}
        {!phoneOnly && !dropOff && (
          <Field label="Where is the job?" hint="Street address" text={textColor}>
            <div className="relative">
              <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-40" />
              <Input value={form.address} onChange={set('address')} className="pl-9" placeholder="12 Elm St" />
            </div>
          </Field>
        )}

        <Field label="Your name" text={textColor}>
          <Input value={form.name} onChange={set('name')} placeholder="Jane Cooper" />
        </Field>

        {/* ⚠️ "so we can reach you" — NOT "for arrival texts". There are no arrival texts. */}
        {/* Wrapped so the notice sits under the field's hint, not between the input and it. */}
        <div>
          <Field
            label="Mobile number"
            hint={phoneOnly ? 'We’ll call you on this number.' : 'So we can reach you about this visit.'}
            text={textColor}
          >
            {/*
              Formatted as it is typed, by the same helper the CRM uses, so numbers reach her in
              one shape rather than in whatever each customer happened to type.

              ⚠️ IT LEAVES ALONE WHAT IT DOES NOT RECOGNISE rather than mangling it — more than
              11 digits, or 11 not starting with 1, come back untouched. Someone entering an
              international number is not left fighting the field.

              The placeholder matches what the field now produces; it used to read
              `(864) 555-0100`, a shape this input can no longer make.
            */}
            <Input
              value={form.phone}
              onChange={(v) => set('phone')(formatPhoneInput(v))}
              type="tel"
              placeholder="864-555-0100"
            />
          </Field>
          <SmsConsent site={site} checked={smsConsent} onChange={onSmsConsent} />
        </div>

        <Field label="Email" hint="Optional." text={textColor}>
          <Input value={form.email} onChange={set('email')} type="email" placeholder="jane@example.com" />
        </Field>

        {/*
          Operators will tell you this is the most useful field on the page — gate codes,
          dogs, where to park. Free text on purpose; a set of checkboxes cannot hold it.
        */}
        <Field
          label={phoneOnly ? 'Anything you’d like to cover on the call?' : 'Anything we should know?'}
          hint={phoneOnly ? 'Optional.' : dropOff ? 'Optional.' : 'Gate code, parking, access.'}
          text={textColor}
        >
          <textarea
            value={form.notes}
            onChange={(e) => set('notes')(e.target.value)}
            rows={3}
            className="w-full rounded-lg border bg-white/90 px-3 py-2 text-base text-slate-900 outline-none"
            style={{ borderColor: hairline(textColor) }}
          />
        </Field>
      </div>

      <PrimaryButton accent={accentColor} onAccent={onAccent} disabled={!ready} onClick={onNext}>
        Continue
      </PrimaryButton>
    </div>
  );
}

/**
 * "Yes, I'm a regular" → her phone number → we recognise her, or we do not (0056).
 *
 * ⚠️ THE ONLY THING THIS COMPONENT EVER LEARNS IS A BOOLEAN. No name, no address, no pet
 * names. That is not an oversight to be improved on later: the stored address for a mobile
 * groomer is the customer's home, and a page that echoed it back would hand it to anyone who
 * typed a mobile number they happened to know.
 *
 * ⚠️ NOT FOUND IS NOT AN ERROR. Plenty of regulars booked by phone, or under a different
 * number, or before the business was on this platform at all. It falls through to the normal
 * form with a sentence that does not accuse her of getting it wrong.
 */
function RebookLookup({
  token,
  onToken,
  onSettled,
  textColor,
  accentColor,
  onAccent,
  compact = false,
}: {
  token: string | null;
  onToken: (t: string | null) => void;
  /**
   * Called once a lookup has finished WITHOUT a match — not found, rate-limited, or offline.
   *
   * ⚠️ THE CALLER NEEDS THE MISS, NOT JUST THE HIT. Step one holds the whole catalogue back
   * while a regular is being recognised; with only `onToken` to go on, a number we cannot find
   * leaves that page waiting for something that is never coming, and she is stuck looking at a
   * phone field with no prices anywhere.
   */
  onSettled?: () => void;
  textColor: string;
  accentColor: string;
  onAccent: string;
  /**
   * Step one's variant: a single line that opens the field, rather than a field sitting
   * open. On the screen whose job is to show prices, an empty input asking for a phone
   * number reads as a form to fill in before you are allowed to look.
   */
  compact?: boolean;
}) {
  const [phone, setPhone] = useState('');
  const [open, setOpen] = useState(!compact);
  const [state, setState] = useState<'idle' | 'looking' | 'missed' | 'failed'>('idle');

  async function look() {
    setState('looking');
    try {
      /*
       * ⚠️ `withTenant`, NOT A BARE PATH. Every other call on this page goes through it, and
       * on a custom domain the Host header alone is enough — but local development and the
       * `?tenant=` preview both identify the business by query parameter. A bare path resolved
       * no tenant, so the route 404'd and this reported "we could not find that number" for a
       * customer who was right there in the table. Caught by driving the page, not by any test.
       */
      const res = await fetch(withTenant('/api/public/rebook'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone }),
      });
      // ⚠️ 429 is its own message. "We could not find you" after five tries would be a lie,
      // and would read as "keep guessing" to exactly the person who should not.
      if (res.status === 429) {
        setState('failed');
        onSettled?.();
        return;
      }
      const body = (await res.json().catch(() => ({}))) as { found?: boolean; token?: string };
      if (body.found && body.token) {
        onToken(body.token);
        setState('idle');
      } else {
        setState('missed');
        onSettled?.();
      }
    } catch {
      setState('failed');
      onSettled?.();
    }
  }

  if (token) {
    return (
      /*
       * ⚠️ THIS CARRIED A "Not you?" LINK AND IT WAS REMOVED (Mike, 2026-09-08). It asked the
       * customer to judge whether we had matched the right person — while the whole design
       * deliberately never tells her WHO we matched. A control nobody can answer is worse
       * than none: it implies a check has been offered when it has not.
       *
       * The residual risk is a shared household number booking under a relative's name. What
       * actually matters for a mobile business — turning up at the wrong house — is caught by
       * "has your address changed?" on the next step.
       */
      <div
        className="rounded-lg px-3 py-2.5 text-sm font-medium"
        style={{ backgroundColor: `${accentColor}1a`, color: textColor }}
      >
        Welcome back — we have your details, so you can skip that part.
      </div>
    );
  }

  if (!open) {
    /*
     * ⚠️ A PANEL, NOT A BARE LINK. It shipped as an underlined line and was easy to miss
     * (Mike, 2026-09-08) — which makes it worthless to the returning customers it exists for,
     * while still costing the pricing screen a row. A tinted band in her own accent reads as
     * an offer at a glance and is still plainly not a step: no field, nothing required, and
     * the services are the next thing under it.
     */
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 text-left text-sm transition-opacity hover:opacity-90"
        style={{ backgroundColor: `${accentColor}14`, color: textColor }}
      >
        <span>
          <span className="font-semibold">Booked with us before?</span>{' '}
          <span className="opacity-75">Enter your number and skip the form.</span>
        </span>
        <span aria-hidden className="shrink-0 font-semibold" style={{ color: accentColor }}>
          →
        </span>
      </button>
    );
  }

  return (
    <Field
      label="Your mobile number"
      hint="So we can find your details and save you retyping them."
      text={textColor}
    >
      <div className="flex gap-2">
        <Input
          value={phone}
          onChange={(v) => setPhone(formatPhoneInput(v))}
          type="tel"
          placeholder="864-555-0100"
        />
        <button
          type="button"
          onClick={() => void look()}
          disabled={state === 'looking' || phone.replace(/\D/g, '').length < 10}
          className="shrink-0 rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-40"
          style={{ backgroundColor: accentColor, color: onAccent }}
        >
          {state === 'looking' ? 'Checking…' : 'Find me'}
        </button>
      </div>
      {state === 'missed' && (
        <p className="mt-2 text-sm" style={{ color: textColor, opacity: 0.7 }}>
          {/*
            ⚠️ IT OFFERS THE RETRY FIRST. The likeliest reason a regular is not found is that
            she typed the wrong number — a work phone, a digit out — and the old copy went
            straight to "just fill in the form", which sends somebody who IS on file back to
            retyping everything she came here to skip.
          */}
          We couldn’t find that number — try re-entering it, or just fill in the form below.
        </p>
      )}
      {state === 'failed' && (
        <p className="mt-2 text-sm" style={{ color: textColor, opacity: 0.7 }}>
          We could not check just now. Fill in the form below and it will all still work.
        </p>
      )}
    </Field>
  );
}

// ── Step 4 — the tenant's own questions ────────────────────────────────────
/*
 * The operator's own questions, in her own words, with a control that fits each one.
 *
 * ⚠️ THIS STEP DOES NOT EXIST FOR MOST TENANTS. A landscaper asks nothing and never sees it
 * — the step list in `Flow` simply omits it — which is why "Step 3 of 4" stays true there
 * and "Step 4 of 5" here.
 */
function StepProfile({
  site,
  questions,
  answers,
  firstVisit,
  asksFirstVisit,
  onFirstVisit,
  rebookToken,
  onRebookToken,
  addressChanged,
  address,
  onAddress,
  onAddressChanged,
  onAnswers,
  onNext,
  onAccent,
  pets,
  onAddAnother,
  onRemovePet,
}: {
  site: Site;
  questions: IntakeQuestion[];
  answers: Record<string, string>;
  firstVisit: boolean | null;
  asksFirstVisit: boolean;
  onFirstVisit: (v: boolean) => void;
  rebookToken: string | null;
  onRebookToken: (t: string | null) => void;
  addressChanged: boolean;
  /** The new address, when she has said it changed. Collected here, not two steps later. */
  address: string;
  onAddress: (v: string) => void;
  onAddressChanged: (v: boolean) => void;
  onAnswers: (a: Record<string, string>) => void;
  onNext: () => void;
  onAccent: string;
  /** Dogs already set aside on this visit (#134). Empty for the overwhelming majority. */
  pets: PetSelection[];
  onAddAnother: () => void;
  onRemovePet: (index: number) => void;
}) {
  const { accentColor, textColor } = site.branding;
  const set = (id: string, v: string) => onAnswers({ ...answers, [id]: v });

  /*
   * ⚠️ ONE TAP, ONE ANSWER, THEN MOVE HER ON (Mike, 2026-09-19, on our own intake: picking a
   * trade from fifteen options left the next question below the fold and the page sitting
   * still, so it read as nothing having happened).
   *
   * Three guards, and each one is a way this gets annoying instead of helpful:
   *   · SINGLE-ANSWER QUESTIONS ONLY. A `multi` question is mid-thought after one tap, and a
   *     text field would yank the keyboard away between keystrokes.
   *   · NOT WHEN CLEARING. Tapping the selected option to un-pick it is a correction, not
   *     progress.
   *   · NOT IF THE NEXT QUESTION IS ALREADY NEAR THE TOP. Scrolling a form that already sits
   *     under her thumb is motion for its own sake.
   *
   * ⚠️ "NEAR THE TOP", NOT "VISIBLE AT ALL" (Mike, 2026-09-19, on a tall desktop window). The
   * first rule here was "skip if it is on screen", which on a short window scrolled and on a
   * tall one did nothing — the next question was technically visible, clinging to the bottom
   * edge, so the page sat still exactly where the long list of trades made it look broken.
   * Anything below the first quarter of the viewport gets brought up.
   */
  const advanceFrom = (index: number) => {
    const next = questions[index + 1];
    const el = next ? document.getElementById(`q-${next.id}`) : null;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    if (top >= 0 && top < window.innerHeight * 0.25) return;
    el.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  };
  /*
   * ⚠️ ERRORS APPEAR ONLY AFTER SHE PRESSES CONTINUE, never while she is still typing.
   * Marking a form red before anybody has attempted anything tells a customer she has done
   * something wrong on arrival, which is both untrue and the fastest way to lose her.
   */
  const [attempted, setAttempted] = useState(false);

  /*
   * The same rule the SERVER applies. A page that let her past a required question would
   * bounce her back from the confirm step with an error she cannot see the cause of; a page
   * stricter than the server would block a booking the API would have accepted.
   */
  /*
   * ⚠️ THE LIST, NOT A BOOLEAN, because the customer has to be told WHICH. A dead Continue
   * button under eleven questions is unusable: she cannot see what is holding it, and the
   * only marker on the form is "(optional)" on the two that are not required — so the
   * blocking field looks exactly like every field she has already answered. Reported as
   * "the button won't activate unless optional fields are completed", which is what being
   * unable to tell them apart feels like.
   */
  const answered = (q: IntakeQuestion) =>
    q.type === 'yesno_explain'
      ? isYesExplainComplete(answers[q.id])
      : (answers[q.id] ?? '').trim().length > 0;
  /*
   * ⚠️ AN UNEXPLAINED "YES" COUNTS AS UNANSWERED, matching the server exactly. Anything
   * looser here would let her past and have the API bounce her back from the confirm step
   * with an error about a question she believes she answered.
   */
  const missingQuestions = questions.filter((q) => q.required && !answered(q));
  // Unanswered is not "no". Nothing may proceed on a guess about whether she is new.
  const undeclared = asksFirstVisit && firstVisit === null;
  /*
   * ⚠️ AN EMPTY NEW ADDRESS IS WORSE THAN NOT ASKING. The server's rule is
   * `supplied ?? the one on the matched booking`, so a blank here does not fail — it silently
   * keeps the OLD address, and a mobile business drives to the house she just moved out of.
   */
  // A drop-off business never asks where (0091), so there is no address to have changed.
  const dropOff = (site.settings.locations ?? []).length > 0;
  const missingAddress = !dropOff && addressChanged && address.trim().length === 0;
  const blocked = missingQuestions.length > 0 || undeclared || missingAddress;

  /*
   * The step exists whenever there is anything to ask — which includes the case where the
   * ONLY thing to ask is whether she has booked before, because a deposit is configured.
   * A questions heading over a single yes/no about bookings would describe a form that is
   * not there.
   */
  const onlyTheClaim = questions.length === 0;

  /*
   * ⚠️ NO TRADE'S VOCABULARY IN THIS HEADING (#150, and #143 is how it was found). This step
   * is shared chrome — every tenant's customers reach it — and it read "About your dog" for
   * all of them. #150 fixed the OPERATOR side of exactly this (BookingBuilder.tsx) and this,
   * the CUSTOMER side, was missed: a car detailing demo opened step 2 headed "About your dog"
   * directly above the tenant's own sentence about where to park the car.
   *
   * The dog words belong in the questions the tenant writes, which are hers. Everything under
   * this heading is already her words — `intakeIntro` sits immediately below it — so the
   * heading only has to name the step, and any trade-specific framing arrives from her.
   */
  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold">
        {onlyTheClaim ? 'One last thing' : 'A few questions'}
      </h1>

      {/*
        The summary, in the tenant's own error colour-free style — plain red on white reads as
        a system error; this is a form that is not finished yet, which is a different thing.
        It names the count so she knows how much is left, and each field below is marked.
      */}
      {attempted && blocked && (
        <div
          role="alert"
          className="mb-4 mt-3 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800"
        >
          {undeclared
            ? 'Please let us know whether you’ve booked with us before.'
            : missingQuestions.length === 1
              ? `Please answer “${missingQuestions[0]!.label}” — it’s marked below.`
              : `${missingQuestions.length} questions still need an answer — they’re marked below.`}
        </div>
      )}
      <p className="mb-5 opacity-70">
        {onlyTheClaim
          ? `So ${site.business.name} knows whether you’ve been before.`
          : (site.settings.intakeIntro ?? `So ${site.business.name} knows who they’re meeting.`)}
      </p>

      <div className="space-y-4">
        {/*
          ⚠️ FIRST, because it decides what the rest of the form even is. Asking it at the
          bottom would mean the questions above her rearrange themselves as she answers.

          It is a claim, not a lookup — see the note in 0024. Checking her against past
          bookings would tell anyone with the page open whether a given email has booked
          this business.
        */}
        {/*
          ⚠️ HIDDEN ONCE SHE HAS BEEN RECOGNISED (0056). Asking "have you booked before?" under
          a banner reading "Welcome back" is asking a question we just answered.

          ⚠️ AND THERE IS DELIBERATELY NO UNDO FOR A MATCH (Mike, 2026-09-08). The "Not you?"
          link that used to sit on that banner asked the customer to judge whether we had the
          right person, while the design never tells her WHO we matched — a control nobody can
          answer. Reloading the page is the only reset, which is the accepted cost. What
          actually matters for a mobile business, arriving at the wrong house, is caught by
          "has your address changed?" below.
        */}
        {/*
          ⚠️ NOT ASKED TWICE. Step one settles this before the prices, so
          repeating it here is the redundancy that made the flow feel like two forms. The
          classic layout still asks it here, because there it is the only place it is asked.
        */}
        {asksFirstVisit && !rebookToken && !(REVEAL_MODE && firstVisit !== null) && (
          <Field
            id="q-first-visit"
            label={`Have you booked with ${site.business.name} before?`}
            text={textColor}
            invalid={attempted && undeclared}
          >
            <div className="flex gap-2">
              {[
                ['No, first time', true],
                ['Yes, I’m a regular', false],
              ].map(([label, isFirst]) => (
                <button
                  key={String(label)}
                  type="button"
                  onClick={() => onFirstVisit(isFirst as boolean)}
                  className="flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition"
                  style={
                    firstVisit === isFirst
                      ? { backgroundColor: accentColor, color: onAccent }
                      : { border: `1px solid ${hairline(textColor)}`, color: textColor }
                  }
                >
                  {label as string}
                </button>
              ))}
            </div>
          </Field>
        )}

        {/*
          Recognise a regular from her number, so she does not retype a name, address, email
          and phone we already hold (0056).

          ⚠️ NOTHING ABOUT HER COMES BACK TO THIS PAGE — the server answers only whether a
          match existed. Read the note on the route before adding a friendly "Welcome back,
          Jane": for a mobile business the stored address is her home, and a name returned
          from a phone number is the first step to returning the rest.
        */}
        {/* Step one owns the lookup, including the retry after a miss. `?reveal=0` asks here. */}
        {asksFirstVisit && firstVisit === false && !REVEAL_MODE && (
          <RebookLookup
            token={rebookToken}
            onToken={onRebookToken}
            textColor={textColor}
            accentColor={accentColor}
            onAccent={onAccent}
          />
        )}

        {/*
          Asked instead of showing her what we hold. "Has it changed?" needs no disclosure to
          answer, and a mover types the new one — which the server then prefers over the old.
        */}
        <Reveal open={rebookToken !== null && !dropOff}>
          <Field label="Has your address changed?" text={textColor}>
            <div className="flex gap-2">
              {[
                ['No, same as last time', false],
                ['Yes, it has changed', true],
              ].map(([label, changed]) => (
                <button
                  key={String(label)}
                  type="button"
                  onClick={() => onAddressChanged(changed as boolean)}
                  className="flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition"
                  style={
                    addressChanged === changed
                      ? { backgroundColor: accentColor, color: onAccent }
                      : { border: `1px solid ${hairline(textColor)}`, color: textColor }
                  }
                >
                  {label as string}
                </button>
              ))}
            </div>
          </Field>
          {/*
            ⚠️ THE FIELD IS THE ANSWER TO THE QUESTION ABOVE IT. Saying "yes, it has changed"
            and being shown nothing is the whole bug this replaced — a claim the page appeared
            to ignore, with the actual field two steps away.

            ⚠️ AND IT IS EMPTY, NEVER PREFILLED. Reading her old address back to her is exactly
            the disclosure this flow is built to avoid: a phone number is not proof of identity,
            and for a mobile business the address on file is her home.
          */}
          <Reveal open={addressChanged}>
            <div className="mt-3">
              <Field
                id="q-new-address"
                label="What's the new address?"
                text={textColor}
                invalid={attempted && missingAddress}
              >
                <Input
                  value={address}
                  onChange={onAddress}
                  placeholder="118 Meadow Rd, Anderson SC"
                />
              </Field>
            </div>
          </Reveal>
        </Reveal>

        {/*
          Nothing is shown until she has said, or the answers would rearrange under her.

          ⚠️ THE STAGGER IS ON THE QUESTIONS, and this is where it earns its keep: nine fields
          arriving at once is the wall that makes a form look long. `smup-stagger` is inert
          without a `.smup-fold-open` ancestor, so the classic layout below is unaffected.
        */}
        <Reveal open={!undeclared}>
          <div className="smup-stagger space-y-4">
          {questions.map((q, qi) => (
            <Field
              key={q.id}
              id={`q-${q.id}`}
              label={q.label + (q.required ? '' : ' (optional)')}
              sub={q.multi ? 'Select all that apply' : undefined}
              hint={q.help ?? undefined}
              text={textColor}
              invalid={attempted && missingQuestions.some((m) => m.id === q.id)}
            >
              <QuestionInput
                question={q}
                value={answers[q.id] ?? ''}
                onChange={(v) => {
                  set(q.id, v);
                  const oneTap = q.type === 'yesno' || (q.type === 'choice' && !q.multi);
                  if (oneTap && v) requestAnimationFrame(() => advanceFrom(qi));
                }}
                accent={accentColor}
                onAccent={onAccent}
                text={textColor}
              />
            </Field>
          ))}
          </div>
        </Reveal>
      </div>

      {/*
        ⚠️ THE DOGS ALREADY SET ASIDE (#134), listed so she can see what the visit holds and
        take one back out. Rendered only when there ARE any — the overwhelming majority book
        one dog and must never meet this.
      */}
      {pets.length > 0 && (
        <div className="mt-6 rounded-xl border p-3" style={{ borderColor: hairline(textColor) }}>
          <p className="text-sm font-semibold">Also on this visit</p>
          {pets.map((p, i) => (
            <div key={i} className="mt-1.5 flex items-center justify-between gap-3 text-sm">
              <span className="opacity-75">
                {/*
            ⚠️ FALLS BACK TO THE SERVICE, NOT TO "Dog N" (#150). When the tenant asks a name
            question this still reads "Bella"; when she does not — every non-grooming trade —
            it read "Dog 1 · Maintenance wash" on a car detailing booking. The service name is
            both neutral and more useful than a number.
          */}
          {p.answers[nameQuestionId(site, p)] || p.service.name}
                {p.tier ? ` · ${p.tier.label}` : ''}
              </span>
              <button
                type="button"
                onClick={() => onRemovePet(i)}
                className="shrink-0 text-xs underline opacity-60 hover:opacity-100"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {/*
        ⚠️ NOT DISABLED. A dead button is the worst of both worlds: it refuses to act and
        refuses to say why, so the customer is left comparing eleven questions against each
        other to find the one that looks different. Pressing it now always does something —
        it either advances or it says exactly what is missing and takes her to it.
      */}
      <PrimaryButton
        accent={accentColor}
        onAccent={onAccent}
        onClick={() => {
          if (!blocked) {
            onNext();
            return;
          }
          setAttempted(true);
          /*
           * Take her TO the problem. On a phone the offending question is very often off
           * screen — that is the whole reason it was missed — so naming it without moving
           * there just relocates the hunt.
           */
          const target = undeclared ? 'first-visit' : missingQuestions[0]!.id;
          document
            .getElementById(`q-${target}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      >
        Continue
      </PrimaryButton>

      {/*
        ⚠️ SECONDARY, AND BELOW CONTINUE. Almost everyone books one; making this a second
        primary button would put a decision in front of every customer that only a minority
        need. It is offered once the one on screen is actually complete — inviting her to add
        a second before the first is finished would set aside a half-filled form.

        ⚠️ "Add another", NOT "Add another dog" (#150). This button is ungated: it appeared on
        every tenant's booking page, so a mobile detailing customer was invited to add another
        dog to their car wash. Verified on the detailing demo before and after.
      */}
      {/*
        ⚠️ NOT ON OUR OWN PAGE (Mike, 2026-09-19). "Add another" means a second pet, car or
        yard on the same visit; on the intro call it invited a prospect to book two sales
        calls back to back. Gated on the tenant, not on the service, because every service we
        ever add to our own booking page is a call with us.
      */}
      {!blocked && !site.business.isInternal && (
        <button
          type="button"
          onClick={onAddAnother}
          className="mt-3 w-full rounded-xl border px-4 py-3 text-sm font-semibold"
          style={{ borderColor: hairline(textColor) }}
        >
          + Add another
        </button>
      )}
    </div>
  );
}

/**
 * Which question is the dog's NAME, so a set-aside pet can be listed as "Bella" rather than
 * "Dog 2". Matched on wording because there is no dedicated field — the operator writes her
 * own questions (0024), and this is presentation only: nothing depends on getting it right,
 * and the fallback is a number.
 */
function nameQuestionId(site: Site, p: PetSelection): string {
  const q = [...site.settings.intakeQuestions, ...(p.service.intakeQuestions ?? [])].find(
    (x) => x.type === 'short' && /name/i.test(x.label),
  );
  return q?.id ?? '';
}

/**
 * One question, drawn as whatever it is.
 *
 * ⚠️ THE ANSWER IS ALWAYS A STRING, whatever the control — "Yes", "42", the option's own
 * text. That is what the API accepts and what the operator reads; see the note on
 * `bookings.intakeAnswers`. A choice stores its LABEL rather than an index, so an answer
 * still means something after she reorders her options.
 */
function QuestionInput({
  question,
  value,
  onChange,
  accent,
  onAccent,
  text,
}: {
  question: IntakeQuestion;
  value: string;
  onChange: (v: string) => void;
  accent: string;
  onAccent: string;
  text: string;
}) {
  if (question.type === 'long') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full rounded-lg border bg-white/90 px-3 py-2 text-base text-slate-900 outline-none"
        style={{ borderColor: hairline(text) }}
      />
    );
  }

  if (question.type === 'number') {
    return (
      <Input
        value={value}
        onChange={onChange}
        // `inputMode` rather than type="number": a number spinner on a phone is worse than a
        // numeric keypad, and type="number" silently discards anything it dislikes.
        type="text"
        placeholder="e.g. 24"
      />
    );
  }

  /*
   * Yes / no with a follow-up. The explanation appears only on yes, so somebody with a
   * healthy dog answers in one tap instead of typing "None" to satisfy a required text box.
   */
  if (question.type === 'yesno_explain') {
    const { yes, explanation } = parseYesExplain(value);
    return (
      <div>
        <div className="flex gap-2">
          {[true, false].map((v) => (
            <button
              key={String(v)}
              type="button"
              onClick={() => onChange(composeYesExplain(v, v ? explanation : ''))}
              aria-pressed={yes === v}
              className="flex-1 rounded-lg px-3 py-2.5 text-center text-sm font-medium transition"
              style={
                yes === v
                  ? { backgroundColor: accent, color: onAccent }
                  : { border: `1px solid ${hairline(text)}`, color: text }
              }
            >
              {v ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
        {yes === true && (
          <textarea
            value={explanation}
            onChange={(e) => onChange(composeYesExplain(true, e.target.value))}
            rows={2}
            placeholder="Please explain"
            autoFocus
            className="mt-2 w-full rounded-lg border bg-white/90 px-3 py-2 text-base text-slate-900 outline-none"
            style={{ borderColor: hairline(text) }}
          />
        )}
      </div>
    );
  }

  // Yes/no and choice are the same control with a different list — a row of buttons rather
  // than a <select>, because a native picker on a phone hides the options behind a tap.
  const options =
    question.type === 'yesno' ? ['Yes', 'No'] : question.type === 'choice' ? question.options ?? [] : null;

  if (options) {
    /*
      ⚠️ A MULTI QUESTION IS THE SAME CONTROL, holding a joined answer rather than one option
      (schema.ts `IntakeQuestion.multi`). Picked options are re-joined IN THE ORDER THE
      OPERATOR WROTE THEM, not the order they were tapped, so two customers who pick the same
      pair produce the same string and the CRM's list stays sortable.
    */
    const picked = question.multi ? value.split(', ').filter(Boolean) : [];
    const isOn = (opt: string) => (question.multi ? picked.includes(opt) : value === opt);
    const toggle = (opt: string) => {
      if (!question.multi) return onChange(value === opt ? '' : opt);
      const next = picked.includes(opt) ? picked.filter((p) => p !== opt) : [...picked, opt];
      onChange(options!.filter((o) => next.includes(o)).join(', '));
    };
    return (
      <div className={question.type === 'yesno' ? 'flex gap-2' : 'space-y-1.5'}>
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            aria-pressed={isOn(opt)}
            className={`rounded-lg px-3 py-2.5 text-left text-sm font-medium transition ${
              question.type === 'yesno' ? 'flex-1 text-center' : 'block w-full'
            }`}
            style={
              isOn(opt)
                ? { backgroundColor: accent, color: onAccent }
                : { border: `1px solid ${hairline(text)}`, color: text }
            }
          >
            {opt}
          </button>
        ))}
      </div>
    );
  }

  return <Input value={value} onChange={onChange} />;
}

function Field({
  id,
  label,
  sub,
  hint,
  text,
  invalid = false,
  children,
}: {
  /** Scroll target, so Continue can take the customer to the question it is complaining about. */
  id?: string;
  label: string;
  /** A line under the label, ABOVE the control — for instructions she needs before answering. */
  sub?: string;
  hint?: string;
  text: string;
  invalid?: boolean;
  children: ReactNode;
}) {
  return (
    <label id={id} className="block scroll-mt-6" style={{ color: text }}>
      <span className="mb-1 block text-sm font-medium">
        {label}
        {/*
          ⚠️ MARKED WITH WORDS, NOT ONLY WITH COLOUR. The page wears the tenant's palette, so
          a red that reads as an error on one background is decorative on another — and a
          customer who cannot distinguish red from the body text would be left with a form
          that refuses to continue and looks unchanged.
        */}
        {invalid && <span className="ml-2 text-xs font-semibold text-red-600">Needs an answer</span>}
      </span>
      {/*
        ⚠️ ABOVE THE OPTIONS, NOT IN `hint` BELOW THEM. "Select all that apply" is only useful
        before she taps; under a list of six options it arrives after she has already decided
        she is picking one.
      */}
      {sub && <span className="mb-1.5 block text-xs opacity-60">{sub}</span>}
      {children}
      {hint && <span className="mt-1 block text-xs opacity-60">{hint}</span>}
    </label>
  );
}

function Input({
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  className = '',
}: {
  value: string;
  onChange: (v: string) => void;
  /** Used where "you have finished typing" is a different moment from "you typed". */
  onBlur?: () => void;
  type?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      // 16px so iOS does not zoom the page on focus.
      className={`w-full rounded-lg border bg-white/90 px-3 py-2.5 text-base text-slate-900 outline-none ${className}`}
      style={{ borderColor: 'rgba(0,0,0,0.15)' }}
    />
  );
}

// ── Step 4 — confirm ───────────────────────────────────────────────────────

function StepConfirm({
  site,
  service,
  tier,
  addons,
  quote,
  pets,
  petLines,
  visitQuote,
  discountPercent,
  date,
  slot,
  zone,
  recurrence,
  form,
  where,
  questions,
  answers,
  firstVisit,
  /** Set when a returning customer was recognised — this page then holds no name (0056). */
  rebookToken,
  smsConsent,
  onSmsConsent,
  policyText,
  policyAccepted,
  onPolicyAccepted,
  submitting,
  onSubmit,
  onAccent,
}: {
  site: Site;
  service: Service;
  tier: Tier | null;
  addons: Addon[];
  quote: Estimate;
  /** Every dog on the visit, and each one's own figure (#134). */
  pets: PetSelection[];
  petLines: Estimate[];
  /** ⚠️ THE TOTAL SHE IS AGREEING TO — both discounts applied, across all dogs. */
  visitQuote: VisitEstimate;
  discountPercent: number;
  date: string;
  slot: Slot;
  zone: string;
  recurrence: Recurrence;
  form: { name: string; phone: string; email: string; address: string; notes: string };
  /** At a drop-off business, the location's name (0091). Its address arrives with the booking. */
  where?: string | null;
  questions: IntakeQuestion[];
  answers: Record<string, string>;
  /** What she declared, or null when the operator never asks. Drives the deposit. */
  firstVisit: boolean | null;
  rebookToken: string | null;
  smsConsent: boolean;
  onSmsConsent: (v: boolean) => void;
  /** Her cancellation policy, or null for none (0083). */
  policyText: string | null;
  policyAccepted: boolean;
  onPolicyAccepted: (v: boolean) => void;
  submitting: boolean;
  onSubmit: () => void;
  onAccent: string;
}) {
  const { accentColor, textColor, backgroundColor } = site.branding;
  const isQuote = service.pricingMode === 'quote';
  /*
   * ⚠️ THE SAME TWO CONDITIONS THE SERVER APPLIES, and it re-derives this independently when
   * it writes the booking — this is a preview of its answer, never an input to it. `=== true`
   * because undefined means she was never asked, and a quote-mode booking is a REQUEST the
   * operator has not accepted, so there is nothing yet to take a deposit against.
   */
  const deposit =
    firstVisit === true && !isQuote ? site.settings.firstVisitDepositCents : null;
  // Only what she actually filled in, in the operator's own question order.
  const answered = questions
    .map((q) => ({ q, a: (answers[q.id] ?? '').trim() }))
    .filter(({ a }) => a.length > 0);
  return (
    <div>
      <h1 className="mb-1 text-2xl font-bold">Check it over</h1>
      <p className="mb-5 opacity-70">
        {/*
          ⚠️ THE TIME IS NOT PENDING ANY MORE (#132). This said she "will confirm the time
          and send a price", which promised a confirmation step that no longer exists —
          quote bookings hold the slot the moment they land. The price half is still true and
          is the half that matters, so it is all that survives.
        */}
        {isQuote
          ? `${site.business.name} has your time. The price follows when she sees the job.`
          : quote.isEstimate
            ? `${site.business.name} has your time. The price is a starting point.`
            : 'Then you’re booked.'}
      </p>

      <Card bg={backgroundColor} text={textColor}>
        <dl className="space-y-1.5 text-sm">
          <Row label="Service" value={service.name} />
          {tier && <Row label="Size" value={tier.label} />}
          <Row label="How long" value={durationLabel(quote.durationMinutes)} />
          <Row label="When" value={`${prettyDate(date)}, ${prettyTime(slot.label)}${zone ? ` ${zone}` : ''}`} />
          {recurrence !== 'once' && (
            <Row
              label="Repeats"
              value={
                RECURRENCE_LABELS[recurrence] +
                (discountPercent > 0 ? ` · ${discountPercent}% off` : '')
              }
            />
          )}
          {where ? <Row label="Where" value={where} /> : form.address && <Row label="Where" value={form.address} />}
          {/*
            ⚠️ A RECOGNISED RETURNING CUSTOMER HAS NO NAME OR PHONE ON THIS PAGE (0056), and
            these rows rendered blank next to their labels — which reads as the form having
            lost something rather than as it never having asked. Say where the details are
            coming from instead. Caught on the confirm screen, not by any test.
          */}
          {rebookToken ? (
            <Row label="You" value="Using the details we have on file" />
          ) : (
            <>
              <Row label="Name" value={form.name} />
              {form.phone && <Row label="Phone" value={form.phone} />}
            </>
          )}

          {/*
            Every add-on itemised. A single rolled-up total is how a customer arrives
            believing she ordered a bath and is charged for a de-shed she does not remember
            ticking — and the operator is the one standing in the driveway explaining it.
          */}
          {addons.length > 0 && (
            <div className="mt-2 border-t pt-2" style={{ borderColor: hairline(textColor) }}>
              {addons.map((a) => (
                <Row
                  key={a.id}
                  label={a.name}
                  value={
                    (a.priceMaxCents != null && a.priceMaxCents !== a.priceCents
                      ? `${money(a.priceCents)}–${money(a.priceMaxCents)}`
                      : money(a.priceCents)) + (a.priceUnit === 'minute' ? ' per min' : '')
                  }
                />
              ))}
            </div>
          )}

          {/*
            ⚠️ EVERY DOG, ITEMISED, WHEN THERE IS MORE THAN ONE (#134). A single total for a
            two-dog visit tells her what to pay and nothing about what she is paying for —
            and the rows above describe only the dog she configured last. One line per animal,
            then the visit's arithmetic underneath it.
          */}
          {pets.length > 1 && (
            <div
              className="mt-2 space-y-1 border-t pt-2"
              style={{ borderColor: hairline(textColor) }}
            >
              {pets.map((p, i) => (
                <Row
                  key={i}
                  label={`${p.answers[nameQuestionId(site, p)] ? `${p.answers[nameQuestionId(site, p)]} · ` : ''}${p.service.name}${p.tier ? ` · ${p.tier.label}` : ''}`}
                  value={formatEstimate(petLines[i]!)}
                />
              ))}
            </div>
          )}

          <div className="mt-2 flex justify-between gap-4 border-t pt-2" style={{ borderColor: hairline(textColor) }}>
            <dt className="font-medium">{visitQuote.isEstimate ? 'Estimated' : 'Price'}</dt>
            {/*
              ⚠️ THE VISIT TOTAL. This rendered `quote` — the dog on screen — which on a
              two-dog visit is roughly half the bill, on the last screen before she commits.
            */}
            <dd className="text-right text-lg font-bold">
              {formatEstimate({
                minCents: visitQuote.totalCents,
                maxCents: visitQuote.totalMaxCents,
                isEstimate: visitQuote.isEstimate,
              })}
            </dd>
          </div>
          {/* Named separately so she can see WHICH concession she is getting, and that the
              two are independent — a repeat customer booking one keeps only the repeat.

        ⚠️ WORDED TO MATCH THE OPERATOR'S OWN SETTING (#150). The builder already calls this
        "Discount for more than one at a visit"; only the customer's side still said
        "multi-dog discount", so the two halves of one feature disagreed by trade. */}
          {visitQuote.multiPetPercent > 0 && (
            <Row label={`${visitQuote.multiPetPercent}% off for more than one`} value="applied" />
          )}
          {visitQuote.recurringPercent > 0 && (
            <Row label={`${visitQuote.recurringPercent}% repeat discount`} value="applied" />
          )}
          {/*
            ⚠️ NAMED, ON THE LAST SCREEN BEFORE SHE COMMITS. Her approved wording promises
            "Further out adds 10%, and you'll see it before you book" — a percentage a
            customer only discovers at the van is precisely the hidden fee the old copy
            disclaimed, and precisely what this line exists to prevent.
          */}
          {visitQuote.travelUpchargePercent > 0 && (
            <Row
              label={`${visitQuote.travelUpchargePercent}% travel — you're outside the regular area`}
              value="included above"
            />
          )}
          {/*
            ⚠️ "ON YOUR FIRST VISIT", NOT "TODAY". The old wording was `Deposit today` under a
            button that takes no payment, which reads as a charge about to happen. Nothing is
            charged here and the operator collects it herself, so the row names WHEN it
            applies and the line under the button says who takes it.
          */}
          {deposit != null && (
            <Row label="Deposit on your first visit" value={money(deposit)} />
          )}
        </dl>

        {/* The two things that keep a starting-at price honest, next to the figure. */}
        {quote.isEstimate && service.priceNote && (
          <p className="mt-3 text-sm opacity-70">{service.priceNote}</p>
        )}
        <UnpricedAddonNote addons={quote.unpricedAddons} />
      </Card>

      {/*
        Her answers, read back. A screen headed "Check it over" that hides twelve answers is
        not asking her to check anything — and this is the last point at which a typo in a
        dog's weight or a wrong "no" about vaccinations can be fixed by the person who knows.
      */}
      {answered.length > 0 && (
        <div className="mt-3">
          <dl className="space-y-1.5 text-sm">
            {answered.map(({ q, a }) => (
              <div key={q.id}>
                <dt className="opacity-60">{q.label}</dt>
                <dd className="font-medium">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {form.notes && (
        <p className="mt-3 text-sm opacity-70">
          <span className="font-medium">Your note:</span> {form.notes}
        </p>
      )}

      {/*
        ⚠️ ONLY FOR A RECOGNISED REGULAR (0056). She skips the details step and never sees the
        number field, so this is the one place she can tick the box. Anybody else ticked (or
        didn't) beside the field a step ago — a second box here would ask twice, and a customer
        who unticks one and not the other has told us nothing we can rely on.

        ⚠️ ABOVE THE BUTTON, NOT BELOW IT. The notice this replaced sat under the button because
        "by booking, you agree" reads naturally after it. A checkbox is a choice, and a choice
        placed after the thing that commits her is one she is likely never to see.
      */}
      {rebookToken && (
        <div className="mb-4">
          <SmsConsent site={site} checked={smsConsent} onChange={onSmsConsent} />
        </div>
      )}
      {/*
        ⚠️ ABOVE THE BUTTON, and the button waits for it, for the reason the SMS box sits here:
        an agreement placed after the thing that commits her is one she never reads. Unlike
        that box, this one IS required — the server refuses a booking without it.
      */}
      {policyText && (
        <CancellationPolicy
          text={policyText}
          checked={policyAccepted}
          onChange={onPolicyAccepted}
          border={hairline(textColor)}
        />
      )}
      {/*
        Nothing is charged here. Deposits are recorded as intent only — MVP invoicing is
        status tracking and Connect is a separate flow — so the button must not look like a
        payment or say one is being taken.
      */}
      <PrimaryButton
        accent={accentColor}
        onAccent={onAccent}
        disabled={submitting || (!!policyText && !policyAccepted)}
        onClick={onSubmit}
      >
        {submitting ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Sending…
          </span>
        ) : (
          /*
            ⚠️ "Confirm booking" FOR QUOTE MODE TOO (#132). It used to read "Request this
            time", which was accurate when quote bookings arrived unaccepted and is a
            misdescription now — pressing this books her. The price caveat is carried by the
            line below and by the heading above, where there is room to say it properly.
          */
          'Confirm booking'
        )}
      </PrimaryButton>
      <p className="mt-2 text-center text-xs opacity-50">
        No payment is taken now.
        {deposit != null &&
          ` ${site.business.name} will arrange the ${money(deposit)} deposit with you directly.`}
        {/*
          ⚠️ SAID PLAINLY, ON THE BUTTON'S OWN LINE, on the last screen before she commits.
          "Confirm booking" under a figure she has been told is only a starting point is the
          one place this page could still be read as a fixed quote — and this sentence is
          what makes the difference between a starting price and a bait price.
        */}
        {!isQuote && quote.isEstimate && ' Your time is confirmed; the final price is settled on the day.'}
        {/*
          The same promise with no floor. A quote booking commits her to turning up and to
          nothing else, and this is the last line before the button — the one place silence
          would read as a price agreed.
        */}
        {isQuote && ' Your time is confirmed; the price is agreed after she sees the job.'}
      </p>
    </div>
  );
}

/**
 * The customer's opt-in to texts about her booking (Mike, 2026-09-17; 0065).
 *
 * ⚠️ IT WAS A NOTICE THAT MORNING AND BECAME A CHECKBOX THAT AFTERNOON. The notice — "by giving
 * your mobile number, you agree to get texts" — was a deliberate choice: these are texts about a
 * booking she is making, not marketing. Twilio's campaign pre-check rejected it anyway, asking
 * for "the SMS checkbox and consent wording", and its guidance says the box "must be actively
 * selected by the user, not pre-checked". A human reviewer applies the same rule, so arguing
 * the notice was enough would have cost a rejection and another $15 per campaign.
 *
 * ⚠️ UNTICKED BY DEFAULT, AND IT MUST STAY THAT WAY. The state starts `false` and the build
 * fails if it does not (check-legal-disclosures.mjs). A pre-ticked box is the specific thing
 * reviewers look for and reject.
 *
 * ⚠️ A CARRIER REVIEWER READS THIS. The campaign describes this box as the opt-in, so the
 * wording has to agree with services/api/src/lib/legal-pages.ts (the tenant's terms and privacy
 * policy, server-rendered) and with the campaign's sample messages. Change one,
 * check the other two — and bump `SMS_CONSENT_VERSION`, which is stored with every tick.
 *
 * ⚠️ NOT MARKETING. These are texts about this booking. Promotional texts need their own
 * separate opt-in (#256) and must never be folded into this box.
 *
 * The links open in a new tab so reading the terms does not lose a half-finished booking.
 */
function SmsConsent({
  site,
  checked,
  onChange,
  className = '',
}: {
  site: Site;
  checked: boolean;
  onChange: (v: boolean) => void;
  className?: string;
}) {
  const name = site.business.name;
  return (
    <label className={`mt-3 flex cursor-pointer items-start gap-2.5 text-left text-xs ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
      />
      <span className="opacity-75">
        {/*
          ⚠️ OUR OWN CAMPAIGN QUOTES "Text me about this booking… Msg & data rates may apply.
          Reply STOP to opt out." Keep both ends of the internal sentence exactly that.
        */}
        {site.business.isInternal ? (
          <>Text me about this booking: a confirmation and any change to the time, from {name}.</>
        ) : site.business.smsReminders ? (
          <>
            Text me about this booking: a confirmation, a reminder the day before, my invoice, and a
            request for a review from {name}.
          </>
        ) : (
          <>
            Text me about this booking: a confirmation, my invoice, and a request for a review
            from {name}.
          </>
        )}{' '}
        Msg &amp; data rates may apply. Reply STOP to opt out. Not required to book.{' '}
        <a href="/terms" target="_blank" rel="noopener" className="underline">
          Terms
        </a>
        {' · '}
        <a href="/privacy" target="_blank" rel="noopener" className="underline">
          Privacy
        </a>
      </span>
    </label>
  );
}

/**
 * Her cancellation policy and the box agreeing to it (Fluffy Paw Spa, 2026-10-16; 0083).
 *
 * ⚠️ HER WORDS, PRINTED AS SHE WROTE THEM — line breaks kept, nothing templated or reworded.
 * It's her commitment to her customers; we only show it and record the tick. The sentence on
 * the box is hers too, and generic enough for any business that writes a policy.
 *
 * ⚠️ UNTICKED UNTIL SHE TICKS IT. A pre-ticked agreement is not an agreement.
 */
function CancellationPolicy({
  text,
  checked,
  onChange,
  border,
}: {
  text: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  border: string;
}) {
  return (
    <div className="mb-4 mt-4 rounded-lg border px-4 py-3 text-left text-sm" style={{ borderColor: border }}>
      <p className="font-semibold">Cancellation policy</p>
      <p className="mt-1 whitespace-pre-line opacity-80">{text}</p>
      <label className="mt-3 flex cursor-pointer items-start gap-2.5">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer"
        />
        <span className="font-medium">By checking this box, you acknowledge and agree to these terms.</span>
      </label>
    </div>
  );
}

// ── Proof of vaccination ───────────────────────────────────────────────────

/**
 * Offered AFTER the booking is made, and only when the operator asked for it.
 *
 * ⚠️ IT CANNOT BLOCK ANYTHING, by construction — the appointment above is already saved and
 * nothing on this panel can undo it. That is the product decision (0025), and the copy has to
 * carry it: a customer who cannot find the paperwork right now must be able to close the tab
 * without believing she has lost her slot.
 *
 * ⚠️ AND IT IS THIS TAB ONLY. The token is not emailed and cannot be reissued, so the panel
 * says so plainly and names the fallback. Implying she can come back to it later would be a
 * promise the server will not keep two hours from now.
 */
/**
 * Attaching the document the operator asked for, after the booking is made.
 *
 * ⚠️ THE DOCUMENT IS NAMED BY THE OPERATOR, NOT BY US (#150, 0040). This panel read
 * "{business} would like a copy of your dog's vaccination record" for every tenant that
 * switched the setting on — and the setting is offered to all of them, with a description in
 * the builder that is already trade-neutral ("they can attach a photo or PDF"). Only the
 * words were grooming-specific. A landscaper collecting an HOA permit and a groomer
 * collecting a vaccination record are the same feature.
 *
 * `label` null falls back to a sentence that names no document at all, rather than to a
 * default noun — a default is how the next trade inherits the wrong one.
 */
function DocumentUpload({
  token,
  retentionDays,
  accent,
  onAccent,
  text,
  businessName,
  label,
}: {
  token: string;
  /** The operator's own retention window, stated before she hands the document over. */
  retentionDays: number;
  accent: string;
  onAccent: string;
  text: string;
  businessName: string;
  /** What SHE calls it. Null when she never said, which the copy handles rather than guesses. */
  label: string | null;
}) {
  const [state, setState] = useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setState('sending');
    setError(null);
    const body = new FormData();
    /*
     * ⚠️ THE TOKEN GOES IN THE BODY, NOT THE URL. A query string lands in access logs, in
     * the Referer of anything the page loads next, and in every proxy in between — and this
     * one grants write access to a booking. It is appended BEFORE the file so the server,
     * which streams the multipart body, has read it by the time the file part arrives.
     */
    body.append('token', token);
    body.append('file', file);
    try {
      const res = await fetch(withTenant('/api/public/bookings/attachment'), {
        method: 'POST',
        body,
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        setError(
          b.issues?.[0]?.message ??
            (res.status === 429
              ? 'Too many tries. Give it a minute.'
              : 'That did not go through. You can send it to us instead.'),
        );
        setState('idle');
        return;
      }
      setState('done');
    } catch {
      setError('We could not reach the server. You can send it to us instead.');
      setState('idle');
    }
  }

  if (state === 'done') {
    return (
      <div className="mt-4 rounded-xl px-4 py-3 text-sm" style={{ border: `1px solid ${hairline(text)}` }}>
        <div className="flex items-center gap-2 font-medium">
          <Check className="h-4 w-4" /> {label ?? 'Document'} received
        </div>
        <p className="mt-1 opacity-70">
          {businessName} will see it with your booking, and it is deleted{' '}
          {retentionDays} days after your appointment.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl px-4 py-4" style={{ border: `1px solid ${hairline(text)}` }}>
      <div className="flex items-center gap-2 font-medium">
        <FileUp className="h-4 w-4" /> {label ?? 'One more thing'}
      </div>
      <p className="mt-1 text-sm opacity-70">
        {label
          ? `${businessName} would like a copy of your ${label.toLowerCase()}. A photo is fine.`
          : `${businessName} would like a copy of a document before your visit. A photo is fine.`}
      </p>
      {/*
        ⚠️ SAID BEFORE SHE UPLOADS, not after. She is about to hand her name, address and her
        vet's details to a business she may never have used, and "how long do you keep this?"
        is the first fair question — answering it afterwards is answering it too late. The
        number is the operator's (0026) and the API's sweep is what makes it true.
      */}
      <p className="mt-1 text-xs opacity-60">
        It is deleted {retentionDays} days after your appointment, and only {businessName} can
        see it.
      </p>

      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

      <label
        className="mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold"
        style={{ backgroundColor: accent, color: onAccent, opacity: state === 'sending' ? 0.6 : 1 }}
      >
        {state === 'sending' ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" /> Sending…
          </>
        ) : (
          <>
            <FileUp className="h-5 w-5" /> Choose a photo or PDF
          </>
        )}
        <input
          type="file"
          // `capture` is deliberately absent: most people already have the photo, and forcing
          // the camera would make them retake a card that is in their gallery.
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          disabled={state === 'sending'}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            // Cleared so picking the same file twice after a failure still fires onChange.
            e.target.value = '';
          }}
        />
      </label>

      {/*
        ⚠️ SAYS WHAT IS ACTUALLY TRUE ABOUT THE TOKEN. It lives in this tab and nowhere else,
        so "you can do it later" would be a lie the server stops honouring in two hours. Your
        booking is safe either way, and the fallback is the one that always works.
      */}
      <p className="mt-2 text-xs opacity-60">
        Your booking is confirmed either way. This upload only works on this screen — if you
        close it, just reply to your confirmation email instead.
      </p>
    </div>
  );
}

// ── Colour helpers ─────────────────────────────────────────────────────────
/*
 * The server already derives the page's text colour and refuses unreadable combinations,
 * so these only cover things it does not send: what to print on the ACCENT, and how to lift
 * a panel off the background without introducing a third colour.
 */

function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const v = [0, 2, 4].map((i) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * v[0] + 0.7152 * v[1] + 0.0722 * v[2];
}
const isDark = (hex: string) => luminance(hex) < 0.5;
/** Ink or white on the accent, whichever reads — matches the server's own rule. */
export const pickReadable = (bg: string) => (luminance(bg) > 0.35 ? '#16201D' : '#FFFFFF');
/** A hairline that works on either a light or a dark tenant background. */
export const hairline = (text: string) =>
  text === '#FFFFFF' ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)';

// ── Small date helpers (calendar dates, never instants) ────────────────────

function todayISO(): string {
  const n = new Date();
  const p = (x: number) => String(x).padStart(2, '0');
  return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}`;
}
function isoAddDays(iso: string, n: number): string {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  t.setUTCDate(t.getUTCDate() + n);
  const p = (x: number) => String(x).padStart(2, '0');
  return `${t.getUTCFullYear()}-${p(t.getUTCMonth() + 1)}-${p(t.getUTCDate())}`;
}
/**
 * The Monday on or before this date.
 *
 * ⚠️ THE WEEK STARTS ON MONDAY HERE, and `getUTCDay()` starts on Sunday — that off-by-one is
 * the whole reason this is a named function rather than arithmetic at the call site. Sunday
 * (0) belongs to the week that BEGAN six days earlier, not the one starting tomorrow.
 */
function mondayOnOrBefore(iso: string): string {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return isoAddDays(iso, -((dow + 6) % 7));
}

function dayLetter(iso: string): string {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][new Date(Date.UTC(y, m - 1, d)).getUTCDay()]!;
}

/**
 * Hand the calendar file to the browser as a download.
 *
 * A Blob rather than a data: URL — iOS Safari refuses to open text/calendar from a data:
 * URL, and this audience is overwhelmingly on phones. The object URL is revoked on the next
 * tick so the tab does not hold the file for its lifetime.
 */
function downloadIcs(ics: string, serviceName: string) {
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${serviceName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
